import {IMapOpts} from '../../ui/map';
import {OBJ, Obj, SIGNAL, SLOT} from '../../obj';
import {bind, capitalize, numberFormat, pixelString} from '../../util';
import {IFilterListOpt, svc} from '../../request';
import {getLogger} from '../../logging';
import {TextInput, TextInputIconPosition} from '../../ui/textinput';
import {renderDealBlock} from '../../ui/dealblock';
import {beginDrag, defaultGhost, DropTargetRegistry} from '../../ui/dnd';
import {ElObj} from '../../elobj';
import {InteractiveMapControlMode} from '../../constants';
import {ProjectDetailGeoMap} from './map';
import {Drawer} from './drawer';
import {AutoComplete} from './autocomplete';
import {ButtonId} from '../../ui/map/draw/ui';
import {FilterList, FilterManager} from './filters';
import {assembleZoneIRGroup, zoneMatchForGroup, splitIRToGroups, combineGroupsToIR} from './filter_ir_zone';
import {ParcelAction, ParcelInfo} from './parcels';
import {GeoCoordinate, GeoRectangle, list, Point} from '../../tools';
import {ProjectDetailStats} from './stats';
import {DownloadPay, DownloadPayButton} from './downloadpay';
import {PaymentDialogView, paymentErrorMessage, showPaymentError} from '../paymentdialog';
import {UndoStack} from '../../ui/undostack';
import {UndoView} from './undoview';
import {Dialog} from '../../ui/dialog';
import {FancyPushButton} from '../../ui/pushbutton';
import {setProjectSurfaceData, writeProjectFlyBack} from '../projecttransition';

const logger = getLogger('projectdetailview');

interface IParcelFilterSyncOpt {
	parcelFilterId?: number;
}

type ProjectWorkspaceTab = 'print' | 'owners' | 'parcel';
type UnifiedPanelMode = 'search' | 'edit';
type UnifiedQueryShape = 'address' | 'freeform';
const FILTER_GROUP_EXPRESSION_LHS = '__filter_group__';
const FILTER_GROUP_EXPRESSION_OPERATOR = 'exact';
const FILTER_GROUP_LOGIC_AND: FilterEditorGroupLogic = 'and';
const FILTER_GROUP_LOGIC_OR: FilterEditorGroupLogic = 'or';

// Lane E PR 1e-i — map the engine's logical token (Expression.lhs) to the IR's
// friendly FIELD_REGISTRY field name. Mirrors core/filter_ir.FIELD_REGISTRY; a
// token absent here cannot be previewed as an IR condition (e.g. owner-search
// selections, which are opaque), so the live count falls back to the persisted
// per-filter count.
const TOKEN_TO_IR_FIELD: {[token: string]: string} = {
	'parcel__zoning__name': 'zoning',
	'parcel__total_value': 'total_value',
	'parcel__acres': 'acres',
	'parcel__address__road': 'street',
	'parcel__address__city': 'city',
	'parcel__address__state': 'state',
	'parcel__owner_01': 'owner_name',
	'parcel__owner_uid': 'owner_id',
	'parcel__owner_address_uid': 'owner_address_id',
};
const FILTER_IR_FIELD_LABELS: {[token: string]: string} = {
	'parcel__zoning__name': 'Zoning',
	'parcel__total_value': 'Total value',
	'parcel__acres': 'Lot size',
	'parcel__address__road': 'Street',
	'parcel__address__city': 'City',
	'parcel__address__state': 'State',
	'parcel__owner_01': 'Owner name',
	'parcel__owner_uid': 'Owner ID',
	'parcel__owner_address_uid': 'Owner mailing address',
};
const FILTER_IR_OPERATOR_LABELS: {[op: string]: string} = {
	'exact': 'is',
	'ne': 'is not',
	'gt': 'is over',
	'gte': 'is at least',
	'lt': 'is under',
	'lte': 'is at most',
	'contains': 'contains',
	// Lane E PR 1e-ii — natural-language verbs for the IR sugar operators.
	'between': 'is between',
	'in': 'is one of',
};
// Lane E PR 1e-ii — reverse of TOKEN_TO_IR_FIELD: the IR's logical FIELD_REGISTRY
// name back to the engine token the editor's condition drafts speak. Used when
// loading the project tree IR (which carries `field`) into token-based drafts.
const IR_FIELD_TO_TOKEN: {[field: string]: string} = Object.keys(TOKEN_TO_IR_FIELD)
	.reduce((acc, token) => {
		acc[TOKEN_TO_IR_FIELD[token]] = token;
		return acc;
	}, {} as {[field: string]: string});
// Lane E PR 1e-ii — the depth-1 field picker groups (Location / Property / Owner).
// Maps an engine token to its grouping; tokens absent here fall under "Other".
const FILTER_FIELD_GROUPS: Array<{label: string; tokens: Array<string>;}> = [
	{label: 'Location', tokens: ['parcel__address__road', 'parcel__address__city', 'parcel__address__state']},
	{label: 'Property', tokens: ['parcel__zoning__name', 'parcel__total_value', 'parcel__acres']},
	{label: 'Owner', tokens: ['parcel__owner_01']},
];
// Lane E PR 1e-ii — tokens for which "is one of" (IR `in`) is offered. Zoning gets
// the engine's single `__in` expansion; the string fields lower to N OR'd `exact`
// rows.
const IN_CAPABLE_TOKENS = new Set<string>([
	'parcel__zoning__name',
	'parcel__address__road',
	'parcel__address__city',
	'parcel__address__state',
]);
// Lane E PR 1e-ii — selection-backed tokens that must NOT be raw editor conditions.
// Owner/address filtering resolves through the owner/address SEARCH flow as an
// opaque `selection` (the Lane B contract). A raw `parcel__owner_uid` condition
// instead lowers to a `parcelowner__owner__uid` join that matches nothing — silently
// zeroing the whole list. The editor won't offer these as addable conditions (a
// pre-existing one still renders, flagged, and is deletable); owner exclusion goes
// through search.
const SELECTION_BACKED_TOKENS = new Set<string>([
	'parcel__owner_uid',
	'parcel__owner_address_uid',
]);
// Numeric tokens whose values get thousands-separators (as-typed and displayed).
// The stored/wire value stays clean digits — the engine compares decimals and
// normalize_rhs strips separators, so we format only at the UI layer.
const NUMERIC_IR_TOKENS = new Set<string>(['parcel__total_value', 'parcel__acres']);

function formatThousands(raw: string): string {
	const text = (raw || '').trim();
	if (!/^\d[\d,]*(\.\d+)?$/.test(text)) {
		return raw;
	}
	return numberFormat(text.replace(/,/g, ''));
}

function stripThousands(raw: string): string {
	return (raw || '').replace(/,/g, '');
}

interface IOwnerSearchSample {
	parcelId?: string;
	parcelUid?: string;
	pin?: string;
}

interface IOwnerSearchRow {
	addressUid?: string;
	city?: string;
	owner?: string;
	ownerUid?: string;
	parcelCount?: number;
	parcelId?: string;
	parcelUid?: string;
	pin?: string;
	samples?: Array<IOwnerSearchSample>;
	state?: string;
	street?: string;
	zip?: string;
}

interface IUnifiedAddressRow {
	city?: string;
	parcelCount?: number;
	samples?: Array<IOwnerSearchSample>;
	state?: string;
	street?: string;
	zip?: string;
	// Phases 3+4: surfaces which matcher path produced this row.
	//   'direct'        — exact `house_number + road__icontains` hit
	//   'direct_fuzzy'  — Phase 4 trigram-similarity fallback (typo'd road)
	//   'geocoded'      — Phase 2 geocoder + spatial-intersect fallback
	// The UI renders a subtle badge for the non-'direct' variants so
	// users see the confidence-level distinction.
	matchKind?: 'direct' | 'direct_fuzzy' | 'geocoded';
	// Phase A: 'project' (default) when the row came from the project's
	// filtered parcel set; 'global' when the project has no filters yet
	// and the geocoder ran against the full active-parcel universe.
	// Frontend can show a "Searching all counties" hint when global
	// rows are present.
	matchScope?: 'project' | 'global';
	geocoderProvider?: string;
	// User's raw typed query, echoed back from the server for geocoded
	// rows so the title can render something meaningful when the
	// parcel's address columns are blank. Used as a last-resort title
	// fallback below the Mapbox-resolved address.
	queryEcho?: string;
	// Mapbox-resolved address fields (only populated for matchKind
	// === 'geocoded' when Mapbox returned a feature with structured
	// context). Preferred over `queryEcho` for the row title and used
	// to fill the locale line when the parcel's own city/state/zip
	// are blank (Walton-style empty-column counties).
	geocodedStreet?: string;
	geocodedCity?: string;
	geocodedState?: string;
	geocodedZip?: string;
}

interface IUnifiedSearchResults {
	addresses: Array<IUnifiedAddressRow>;
	owners: Array<IOwnerSearchRow>;
	places: Array<IPlace>;
	queryShape: UnifiedQueryShape;
}

interface IUnifiedSearchResponse {
	query: string;
	addresses: Array<IUnifiedAddressRow>;
	owners: Array<IOwnerSearchRow>;
	places: Array<IPlace>;
	meta?: {
		queryShape?: UnifiedQueryShape;
	};
}

interface IUnifiedRecentSearch {
	icon: string;
	meta: string;
	title: string;
}

interface IPinnedParcelWorkspaceItem {
	expanded: boolean;
	kind: 'parcel';
	parcel: IParcel;
	workspaceItemId?: WorkspaceItemPk;
	// Phase 5B: scenarios attached to this parcel for the current user.
	// Surfaced on the pin card so users can see / open existing
	// scenarios + create more, without leaving Project Detail.
	attachedDeals: ReadonlyArray<IAttachedDealRef>;
	// Local UI state — collapsed scenario list expands inline.
	scenariosExpanded: boolean;
}

interface IPinnedOwnerWorkspaceItem {
	expanded: boolean;
	key: string;
	kind: 'owner';
	row: IOwnerSearchRow;
	workspaceItemId?: WorkspaceItemPk;
}

type IPinnedWorkspaceItem = IPinnedOwnerWorkspaceItem | IPinnedParcelWorkspaceItem;

@OBJ
export class ProjectDetailView extends Obj {
	private autoComplete: AutoComplete | null;
	private cancelSrc: {syncCounts: ICancelTokenSource | null; filterPreview: ICancelTokenSource | null};
	private disabled: boolean;
	private downloadPay: DownloadPay;
	private absoluteListUri: string;
	private activeDownloadDialog: Dialog | null;
	private activeFilterSummaryEl: HTMLElement | null;
	private downloadWithOwnerNameEnabled: boolean;
	drawer: Drawer;
	filterList: FilterList;
	private filterCreatePendingTimeout: number | null;
	private filterListDivider: ElObj | null;
	private filterMgr: FilterManager;
	lastMousePos: Point;
	map: ProjectDetailGeoMap;
	private mapCfgPk: GeoMapConfigurationPk | null;
	private parcelInfo: ParcelInfo | null;
	private paymentDialog: PaymentDialogView | null;
	private parcelWorkspaceParcels: Array<IParcel>;
	private pinnedWorkspaceItems: Array<IPinnedWorkspaceItem>;
	private places: list<IPlace>;
	private primaryInput: TextInput | null;
	private primaryInputDomEl: HTMLInputElement | null;
	slug: string;
	private stats: ProjectDetailStats;
	undoStack: UndoStack;
	private undoView: UndoView | null;
	private sideWorkspacePanelEl: HTMLElement | null;
	private sideWorkspaceTab: ProjectWorkspaceTab;
	// Deal-forward PR D: resolved per-project boot mode, read from the
	// root's `data-project-mode` (server resolves 'auto'). 'deals' boots
	// the Pinned workspace; 'lists' is today's filters-first default.
	private projectMode: DashboardMode;
	private workspaceLauncherEl: HTMLElement | null;
	private unifiedEditingFilter: IFilter | null;
	private unifiedEditorDraft: IFilterEditorDraft | null;
	// Lane E PR 1e-ii — the PROJECT-scoped two-zone draft. Loaded from the
	// filter-tree IR endpoint when the editor opens; saved back via saveFilterTree.
	private projectFilterDraft: IProjectFilterEditorDraft | null;
	private projectFilterDraftLoading: boolean;
	private projectFilterDraftError: string;
	// Lane E Phase 2 (Stage 2) — when the editor is opened FROM a boundary on the
	// map (the per-polygon popup), focus the group that scopes that boundary so the
	// user lands on the right group to add criteria to. `pendingFocusLeafId` is the
	// clicked filter id, resolved to `focusGroupIndex` once the draft loads.
	private projectFilterPendingFocusLeafId: number | null;
	private projectFilterFocusGroupIndex: number | null;
	private projectFilterFocusScrolled: boolean;
	// Apply is gated on having seen a successful live count (preview == result).
	private projectFilterPreviewOk: boolean;
	// The two-zone editor can open without a seed chip (the "+ Add filter" entry on
	// an empty project), so routing keys on this flag, not just unifiedEditingFilter.
	private projectFilterEditorActive: boolean;
	// False until the user actually edits the draft. While false the footer shows the
	// project's already-known list count instead of firing the expensive dry-run.
	private projectFilterDirty: boolean;
	// Last list address count from syncCounts — shown instantly on a pristine open.
	private lastKnownAddressCount: number | null;
	// Lane E PR 1e-iii — this project's filter engine version (1 = legacy/locked,
	// 2 = include/exclude). Drives the opt-in banner + the editor's before/after and
	// switch-back. Tracked locally; Apply opts in to v2, the banner can switch back.
	private projectFilterSchemaVersion: number;
	private projectOptInBannerDismissed: boolean;
	private unifiedEditorRenameActive: boolean;
	private unifiedEditorRenameDraft: string;
	private unifiedPanelMode: UnifiedPanelMode;
	private unifiedPanelInputEl: HTMLInputElement | null;
	private unifiedPanelQuery: string;
	private unifiedResults: IUnifiedSearchResults;
	private unifiedSearchedQuery: string;
	private unifiedSearchError: string;
	private unifiedSearchLoading: boolean;
	private unifiedSearchSeq: number;
	private unifiedSearchTimer: number | null;
	private unifiedSelectedOwnerKeys: Set<string>;
	private unifiedSelectedPlaceKeys: Set<string>;
	// Phase 3: address rows are selectable. Keyed by `samples[0].parcelUid`
	// since the address row's selection contract is "the parcels in this
	// row's samples become children of an address-search filter."
	private unifiedSelectedAddressKeys: Set<string>;
	private workspacePanelEl: HTMLElement | null;
	private workspacePanelOpen: boolean;

	constructor(slug?: string) {
		super();
		this.absoluteListUri = '';
		this.activeDownloadDialog = null;
		this.activeFilterSummaryEl = null;
		this.cancelSrc = {syncCounts: null, filterPreview: null};
		this.downloadWithOwnerNameEnabled = false;
		this.undoStack = new UndoStack();
		this.autoComplete = null;
		this.disabled = false;
		this.filterListDivider = null;
		this.filterCreatePendingTimeout = null;
		this.lastMousePos = new Point();
		this.parcelInfo = null;
		this.parcelWorkspaceParcels = [];
		this.paymentDialog = null;
		this.pinnedWorkspaceItems = [];
		this.places = new list<IPlace>();
		this.primaryInput = null;
		this.primaryInputDomEl = null;
		this.slug = slug || '';
		this.projectMode = 'lists';
		// FIXME: Don't mess ordering below. But fix this BS dependency ASAP.
		this.drawer = new Drawer();
		this.downloadPay = new DownloadPay();
		this.map = new ProjectDetailGeoMap(this, false, this.slug);
		this.mapCfgPk = null;
		this.filterList = new FilterList();
		this.filterList.addClass('lb-project-workspace-filter-list');
		this.filterMgr = new FilterManager(this);
		this.stats = new ProjectDetailStats({classNames: ['lb-project-workspace-kpi']});
		this.undoView = null;
		this.sideWorkspacePanelEl = null;
		this.sideWorkspaceTab = 'print';
		this.workspaceLauncherEl = null;
		this.unifiedEditingFilter = null;
		this.unifiedEditorDraft = null;
		this.projectFilterDraft = null;
		this.projectFilterDraftLoading = false;
		this.projectFilterDraftError = '';
		this.projectFilterPendingFocusLeafId = null;
		this.projectFilterFocusGroupIndex = null;
		this.projectFilterFocusScrolled = false;
		this.projectFilterPreviewOk = false;
		this.projectFilterEditorActive = false;
		this.projectFilterDirty = false;
		this.lastKnownAddressCount = null;
		this.projectFilterSchemaVersion = 1;
		this.projectOptInBannerDismissed = false;
		this.unifiedEditorRenameActive = false;
		this.unifiedEditorRenameDraft = '';
		this.unifiedPanelMode = 'search';
		this.unifiedPanelInputEl = null;
		this.unifiedPanelQuery = '';
		this.unifiedResults = {addresses: [], owners: [], places: [], queryShape: 'freeform'};
		this.unifiedSearchedQuery = '';
		this.unifiedSearchError = '';
		this.unifiedSearchLoading = false;
		this.unifiedSearchSeq = 0;
		this.unifiedSearchTimer = null;
		this.unifiedSelectedOwnerKeys = new Set<string>();
		this.unifiedSelectedPlaceKeys = new Set<string>();
		this.unifiedSelectedAddressKeys = new Set<string>();
		this.workspacePanelEl = null;
		this.workspacePanelOpen = false;
		this.init();
	}

	@SLOT
	private autoCompleteActivated(index: number): void {
		this.setAutoCompleteEnabled(false);
		this.setPrimaryInputText('');
		if ((index >= 0) && (index < this.places.size())) {
			this.beginPendingFilterCreateFeedback();
			this.placeAutoCompleteActivated(this.places.at(index));
		}
	}

	private beginPendingFilterCreateFeedback(): void {
		this.clearPendingFilterCreateFeedback();
		if (!this.cancelSrc.syncCounts) {
			this.stats.beginSync();
		}
		this.filterCreatePendingTimeout = window.setTimeout(() => {
			this.filterCreatePendingTimeout = null;
			if (!this.cancelSrc.syncCounts) {
				this.stats.endSync();
			}
		}, 20000);
	}

	private clearPendingFilterCreateFeedback(): void {
		if (this.filterCreatePendingTimeout !== null) {
			window.clearTimeout(this.filterCreatePendingTimeout);
			this.filterCreatePendingTimeout = null;
		}
	}

	@SLOT
	private async beginPayment(): Promise<void> {
		if (this.paymentDialog) {
			return;
		}
		this.downloadPay.showProgress();
		try {
			const invoice = await this.fetchInvoice(true);
			const title = `${this.slug} - ${numberFormat(invoice.totalQuantity)} mailing addresses`;
			const paymentData = await this.fetchPaymentIntent();
			this.paymentDialog = new PaymentDialogView({
				amount: invoice.total,
				paymentIntent: paymentData,
				title,
			});
			Obj.connect(
				this.paymentDialog, 'finished',
				this, 'endPayment');
			this.paymentDialog.beginPayment();
			this.downloadPay.showPayButton();
			this.downloadPay.setButtonEnabled(false);
		} catch (err) {
			logger.error('beginPayment: failed to start payment flow', err);
			this.downloadPay.showPayButton();
			this.downloadPay.setButtonEnabled(true);
			showPaymentError(paymentErrorMessage(err));
		}
	}

	closeDrawer(): void {
		// Repurposed for the rail-collapse pattern: instead of MDC-closing
		// the drawer (which slides it off-screen entirely), collapse to
		// rail mode so the brand badge stays as a click target for
		// reopening. See setDrawerMode below + the 2026-05-21 UI thread
		// (Method A rail-collapse decision).
		this.setDrawerMode('rail');
	}

	private drawerModeKey(): string {
		return `lb-project-drawer-mode:${this.slug || 'unknown'}`;
	}

	private restoreDrawerMode(): void {
		try {
			const saved = window.localStorage.getItem(this.drawerModeKey());
			if (saved === 'rail') {
				this.setDrawerMode('rail', {persist: false});
			}
			// Default = expanded; no class needed.
		} catch (err) {
			logger.warning('restoreDrawerMode: localStorage unavailable', err);
		}
	}

	setDrawerMode(mode: 'rail' | 'expanded', opts: {persist?: boolean} = {}): void {
		const workspace = document.getElementById('id_lb-project-detail');
		if (!workspace) {
			return;
		}
		const RAIL_CLASS = 'lb-project-workspace--drawer-rail';
		if (mode === 'rail') {
			workspace.classList.add(RAIL_CLASS);
		} else {
			workspace.classList.remove(RAIL_CLASS);
		}
		if (opts.persist !== false) {
			try {
				window.localStorage.setItem(this.drawerModeKey(), mode);
			} catch (err) {
				logger.warning('setDrawerMode: localStorage write failed', err);
			}
		}
	}

	toggleDrawerMode(): void {
		const workspace = document.getElementById('id_lb-project-detail');
		const isRail = !!workspace?.classList.contains('lb-project-workspace--drawer-rail');
		this.setDrawerMode(isRail ? 'expanded' : 'rail');
	}

	private wireDrawerModeControls(): void {
		const collapse = document.getElementById('id_lb-project-drawer-collapse');
		if (collapse) {
			collapse.addEventListener('click', evt => {
				evt.preventDefault();
				this.setDrawerMode('rail');
			});
		}
		const railBadge = document.getElementById('id_lb-project-drawer-rail-badge');
		if (railBadge) {
			railBadge.addEventListener('click', evt => {
				evt.preventDefault();
				this.setDrawerMode('expanded');
			});
		}
		const railExpand = document.getElementById('id_lb-project-drawer-rail-expand');
		if (railExpand) {
			railExpand.addEventListener('click', evt => {
				evt.preventDefault();
				this.setDrawerMode('expanded');
			});
		}
	}

	// ───────── Drawer resize (R1) ─────────
	//
	// Drag the drawer's right edge to set a custom expanded width;
	// double-click the handle to reset. The width is written onto the
	// `--lb-project-drawer-width-expanded` custom property (NOT
	// `--lb-project-drawer-width`, which rail mode owns), so resizing
	// never collides with rail collapse. Persisted per-project to
	// localStorage. Range clamped to [DRAWER_WIDTH_MIN, _MAX].

	private static readonly DRAWER_WIDTH_MIN = 200;
	private static readonly DRAWER_WIDTH_MAX = 420;
	private static readonly DRAWER_WIDTH_DEFAULT = 280;

	private drawerWidthKey(): string {
		return `lb-project-drawer-width:${this.slug || 'unknown'}`;
	}

	private workspaceRootEl(): HTMLElement | null {
		return document.getElementById('id_lb-project-detail');
	}

	private applyDrawerWidth(width: number): void {
		const root = this.workspaceRootEl();
		if (!root) {
			return;
		}
		root.style.setProperty('--lb-project-drawer-width-expanded', `${width}px`);
	}

	private restoreDrawerWidth(): void {
		try {
			const saved = window.localStorage.getItem(this.drawerWidthKey());
			if (saved === null) {
				return;
			}
			const width = parseInt(saved, 10);
			if (!Number.isFinite(width)) {
				return;
			}
			const clamped = Math.min(
				Math.max(width, ProjectDetailView.DRAWER_WIDTH_MIN),
				ProjectDetailView.DRAWER_WIDTH_MAX);
			this.applyDrawerWidth(clamped);
		} catch (err) {
			logger.warning('restoreDrawerWidth: localStorage unavailable', err);
		}
	}

	private resetDrawerWidth(): void {
		const root = this.workspaceRootEl();
		if (root) {
			root.style.removeProperty('--lb-project-drawer-width-expanded');
		}
		try {
			window.localStorage.removeItem(this.drawerWidthKey());
		} catch (err) {
			logger.warning('resetDrawerWidth: localStorage write failed', err);
		}
	}

	private wireDrawerResize(): void {
		const handle = document.getElementById('id_lb-project-drawer-resize');
		if (!handle) {
			return;
		}
		handle.addEventListener('pointerdown', evt => this.beginDrawerResize(evt));
		handle.addEventListener('dblclick', evt => {
			evt.preventDefault();
			this.resetDrawerWidth();
		});
	}

	private beginDrawerResize(event: PointerEvent): void {
		if (event.button !== 0) {
			return;
		}
		const drawer = document.getElementById('id_lb-project-detail-drawer');
		const root = this.workspaceRootEl();
		if (!drawer || !root) {
			return;
		}
		// Don't resize in rail mode (the handle is CSS-hidden there, but
		// guard anyway against a stray synthetic event).
		if (root.classList.contains('lb-project-workspace--drawer-rail')) {
			return;
		}
		event.preventDefault();
		event.stopPropagation();
		const startWidth = drawer.getBoundingClientRect().width;
		const startX = event.clientX;
		const clamp = (value: number) => Math.min(
			Math.max(value, ProjectDetailView.DRAWER_WIDTH_MIN),
			ProjectDetailView.DRAWER_WIDTH_MAX);
		let finalWidth = startWidth;

		root.classList.add('is-drawer-resizing');

		const move = (moveEvent: PointerEvent): void => {
			moveEvent.preventDefault();
			finalWidth = clamp(startWidth + (moveEvent.clientX - startX));
			this.applyDrawerWidth(finalWidth);
		};
		const end = (): void => {
			root.classList.remove('is-drawer-resizing');
			document.removeEventListener('pointermove', move, true);
			document.removeEventListener('pointerup', end, true);
			document.removeEventListener('pointercancel', end, true);
			try {
				window.localStorage.setItem(this.drawerWidthKey(), `${Math.round(finalWidth)}`);
			} catch (err) {
				logger.warning('beginDrawerResize: localStorage write failed', err);
			}
		};
		document.addEventListener('pointermove', move, true);
		document.addEventListener('pointerup', end, true);
		document.addEventListener('pointercancel', end, true);
	}

	// ───────── Side workspace panel resize (R2) ─────────
	//
	// Same pattern as the drawer (R1), but the side panel is right-
	// anchored (`right: 14px`), so the handle lives on its LEFT edge and
	// the drag delta is inverted: dragging left widens, dragging right
	// narrows. The custom width is written onto `--lb-side-panel-width`
	// on the workspace root; the panel's CSS still min()s it against a
	// viewport cap so it can never overflow. Persisted per-project.
	//
	// Works whether the panel is docked or has been free-floated via its
	// move grip: beginPanelDrag no longer freezes an inline width on the
	// side panel (the width stays variable-driven), and beginSidePanelResize
	// is anchor-aware — in the floated/left-anchored state it pins the
	// right edge by adjusting `left` as the width changes.

	private static readonly SIDE_PANEL_WIDTH_MIN = 320;
	private static readonly SIDE_PANEL_WIDTH_MAX = 560;

	private sidePanelWidthKey(): string {
		return `lb-side-panel-width:${this.slug || 'unknown'}`;
	}

	private applySidePanelWidth(width: number): void {
		const root = this.workspaceRootEl();
		if (!root) {
			return;
		}
		root.style.setProperty('--lb-side-panel-width', `${width}px`);
	}

	private restoreSidePanelWidth(): void {
		try {
			const saved = window.localStorage.getItem(this.sidePanelWidthKey());
			if (saved === null) {
				return;
			}
			const width = parseInt(saved, 10);
			if (!Number.isFinite(width)) {
				return;
			}
			const clamped = Math.min(
				Math.max(width, ProjectDetailView.SIDE_PANEL_WIDTH_MIN),
				ProjectDetailView.SIDE_PANEL_WIDTH_MAX);
			this.applySidePanelWidth(clamped);
		} catch (err) {
			logger.warning('restoreSidePanelWidth: localStorage unavailable', err);
		}
	}

	private resetSidePanelWidth(): void {
		const root = this.workspaceRootEl();
		if (root) {
			root.style.removeProperty('--lb-side-panel-width');
		}
		try {
			window.localStorage.removeItem(this.sidePanelWidthKey());
		} catch (err) {
			logger.warning('resetSidePanelWidth: localStorage write failed', err);
		}
	}

	private wireSidePanelResize(): void {
		const handle = document.getElementById('id_lb-side-panel-resize');
		if (!handle) {
			return;
		}
		handle.addEventListener('pointerdown', evt => this.beginSidePanelResize(evt));
		handle.addEventListener('dblclick', evt => {
			evt.preventDefault();
			this.resetSidePanelWidth();
		});
	}

	private beginSidePanelResize(event: PointerEvent): void {
		if (event.button !== 0) {
			return;
		}
		const panel = document.getElementById('lb-project-workspace-panel');
		const root = this.workspaceRootEl();
		if (!panel || !root) {
			return;
		}
		event.preventDefault();
		event.stopPropagation();
		const startRect = panel.getBoundingClientRect();
		const startWidth = startRect.width;
		const startX = event.clientX;
		// Anchor-aware resize. When the panel has been free-floated via
		// its move grip it's left-anchored (`right: auto`, inline `left`);
		// the left edge must follow the pointer with the RIGHT edge
		// pinned, so we adjust `left` as the width changes. When docked
		// it's right-anchored (CSS `right: 14px`), so a width change alone
		// moves the left edge and the handle tracks the pointer for free.
		const floated = panel.style.right === 'auto';
		const startRight = startRect.left + startWidth;
		const clamp = (value: number) => Math.min(
			Math.max(value, ProjectDetailView.SIDE_PANEL_WIDTH_MIN),
			ProjectDetailView.SIDE_PANEL_WIDTH_MAX);
		let finalWidth = startWidth;

		root.classList.add('is-side-panel-resizing');

		const move = (moveEvent: PointerEvent): void => {
			moveEvent.preventDefault();
			// Dragging left (clientX decreases) widens in both states.
			finalWidth = clamp(startWidth + (startX - moveEvent.clientX));
			this.applySidePanelWidth(finalWidth);
			if (floated) {
				// Pin the right edge: left = right - actual rendered width.
				// Reading back the rect width (rather than finalWidth)
				// keeps the right edge fixed even if the viewport cap
				// clamps the rendered width below the requested value.
				const actualWidth = panel.getBoundingClientRect().width;
				panel.style.left = `${startRight - actualWidth}px`;
			}
		};
		const end = (): void => {
			root.classList.remove('is-side-panel-resizing');
			document.removeEventListener('pointermove', move, true);
			document.removeEventListener('pointerup', end, true);
			document.removeEventListener('pointercancel', end, true);
			try {
				window.localStorage.setItem(this.sidePanelWidthKey(), `${Math.round(finalWidth)}`);
			} catch (err) {
				logger.warning('beginSidePanelResize: localStorage write failed', err);
			}
		};
		document.addEventListener('pointermove', move, true);
		document.addEventListener('pointerup', end, true);
		document.addEventListener('pointercancel', end, true);
	}

	private closePopups(): void {
		this.map.closeAllPopups();
	}

	destroy(): void {
		this.cancelSrc = {syncCounts: null, filterPreview: null};
		document.removeEventListener('keydown', this.documentEvent);
		window.removeEventListener('resize', this.windowEvent);
		if (this.primaryInputDomEl) {
			this.primaryInputDomEl.removeEventListener('click', this.primaryInputFocusEvent);
			this.primaryInputDomEl.removeEventListener('focus', this.primaryInputFocusEvent);
		}
		this.closePopups();
		this.destroyPaymentDialog();
		this.destroyAutoComplete();
		[
			this.undoView,
			this.filterMgr,
			this.primaryInput,
		].forEach(o => o && o.destroy());
		this.activeFilterSummaryEl = null;
		this.undoView = null;
		this.destroyUnifiedWorkspacePanel();
		this.primaryInputDomEl = null;
		this.primaryInput = null;
		this.mapCfgPk = null;
		this.places.clear();
		this.slug = '';
		super.destroy();
	}

	private destroyAutoComplete(): void {
		if (this.autoComplete) {
			Obj.disconnect(
				this.autoComplete, 'activated',
				this, 'autoCompleteActivated');
			this.autoComplete.destroy();
		}
		this.autoComplete = null;
	}

	private destroyParcelInfo(): void {
		if (this.parcelInfo) {
			Obj.disconnect(
				this.parcelInfo, 'actionActivated',
				this, 'parcelInfoActionActivated');
			this.parcelInfo.destroy();
		}
		this.parcelInfo = null;
	}

	private destroyPaymentDialog(): void {
		if (this.paymentDialog) {
			Obj.disconnect(
				this.paymentDialog, 'finished',
				this, 'endPayment');
			this.paymentDialog.destroy();
		}
		this.paymentDialog = null;
	}

	@bind
	private documentEvent(event: Event): void {
		switch (event.type) {
			case 'keydown': {
				this.documentKeyDownEvent(<KeyboardEvent>event);
				break;
			}
		}
	}

	private documentKeyDownEvent(event: KeyboardEvent): void {
		if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
			event.preventDefault();
			this.openUnifiedWorkspacePanel();
			return;
		}
		switch (event.key) {
			case 'Escape':
				if (this.workspacePanelOpen) {
					this.closeUnifiedWorkspacePanel();
				} else if (this.isSideWorkspacePanelOpen()) {
					this.closeSideWorkspacePanel();
				} else if (this.isAutoCompleteOpen()) {
					this.destroyAutoComplete();
				} else if (this.filterMgr.isFilterBoxOpen() || this.hasPopupsOpen()) {
					this.filterMgr.closeFilterBox();
					this.closePopups();
				} else {
					this.syncMapDrawTrashButtonVisibility();
					switch (this.mapInteractiveControlMode()) {
						case InteractiveMapControlMode.ShapeSelect:
							if (this.mapSelectedFeatureCount() > 0) {
								this.setSelectedMapFeatures([]);
							} else {
								this.filterMgr.stopEditingFilterShape();
								this.setMapInteractiveControlMode(
									InteractiveMapControlMode.NoMode);
							}
							break;
						case InteractiveMapControlMode.DrawShape:
							// If control mode is draw shape, the draw mode
							// should be listening for the escape key and,
							// when it's received, will execute default
							// cleanup behavior.
							//
							// Update: It doesn't. Quick fix:
							this.setMapInteractiveControlMode(
								InteractiveMapControlMode.NoMode);
							break;
						case InteractiveMapControlMode.InfoMode:
							this.setMapInteractiveControlMode(
								InteractiveMapControlMode.NoMode);
							break;
						default:
							// ESC default branch: toggle rail/expanded so the
							// drawer always remains at least partially visible
							// (the brand badge stays clickable as the
							// expand-back affordance). Symmetric with the
							// collapse chevron + rail badge click handlers.
							this.toggleDrawerMode();
							break;
					}
				}
				break;
			case 'Enter':
				if (this.filterMgr.currentlyEditingShape()) {
					this.filterMgr.stopEditingFilterShape();
				}
				break;
		}
	}

	closeUnifiedWorkspacePanel(): void {
		const wasEditing = this.unifiedPanelMode === 'edit';
		this.workspacePanelOpen = false;
		this.unifiedPanelMode = 'search';
		this.unifiedEditingFilter = null;
		this.unifiedEditorDraft = null;
		this.projectFilterDraft = null;
		this.projectFilterDraftLoading = false;
		this.projectFilterDraftError = '';
		this.projectFilterPendingFocusLeafId = null;
		this.projectFilterFocusGroupIndex = null;
		this.projectFilterFocusScrolled = false;
		this.projectFilterPreviewOk = false;
		this.projectFilterEditorActive = false;
		this.projectFilterDirty = false;
		this.projectApplyButtonEl = null;
		this.unifiedEditorRenameActive = false;
		this.unifiedEditorRenameDraft = '';
		this.filterList.setSelectedFilterId(null);
		if (wasEditing) {
			this.filterMgr.closeFilterBox();
		}
		this.syncUnifiedLauncherState();
		if (this.workspacePanelEl) {
			this.workspacePanelEl.classList.remove('lb-project-unified-panel--edit');
			this.workspacePanelEl.classList.remove('is-open');
			this.workspacePanelEl.setAttribute('aria-hidden', 'true');
		}
		if (this.workspaceLauncherEl) {
			this.workspaceLauncherEl.querySelectorAll('button').forEach(btn => btn.classList.remove('is-on'));
		}
	}

	private destroyUnifiedWorkspacePanel(): void {
		this.clearUnifiedSearchTimer();
		if (this.workspaceLauncherEl) {
			this.workspaceLauncherEl.remove();
		}
		this.workspaceLauncherEl = null;
		if (this.workspacePanelEl) {
			this.workspacePanelEl.remove();
		}
		this.workspacePanelEl = null;
		this.workspacePanelOpen = false;
		this.unifiedPanelInputEl = null;
		this.unifiedPanelMode = 'search';
		this.unifiedEditingFilter = null;
		this.unifiedEditorRenameActive = false;
		this.unifiedEditorRenameDraft = '';
		this.filterList.setSelectedFilterId(null);
		this.syncUnifiedLauncherState();
	}

	@SLOT
	private downloadButtonClicked(): void {
		window.location.assign('list/');
	}

	// @SLOT
	// private async downloadPayButtonClicked(): Promise<void> {
	// 	if (this.downloadPay.currentButton() === DownloadPayButton.PayButton) {
	// 		await this.payButtonClicked();
	// 	} else {
	// 		if ((this.downloadPay.currentButton() === DownloadPayButton.DownloadButton) && this.downloadPay.isButtonFlashing()) {
	// 			this.downloadPay.setButtonFlashing(false);
	// 		}
	// 	}
	// }

	@SLOT
	private async downloadPayButtonClicked(): Promise<void> {
		if (this.downloadPay.currentButton() === DownloadPayButton.PayButton) {
			await this.payButtonClicked();
		} else if (this.downloadPay.currentButton() === DownloadPayButton.DownloadButton) {
			if (this.downloadPay.isButtonFlashing()) {
				this.downloadPay.setButtonFlashing(false);
			}
			const dia = new Dialog({closeButton: true, title: 'Download'});
			dia.addClass('lb-redesign-surface', 'lb-project-download-dialog');
			const btnParent = new ElObj({
				classNames: [
					'lb-project-download-dialog__actions',
				],
				tagName: 'div',
			});
			dia.appendElObj(btnParent);
			this.activeDownloadDialog = dia;
			const mailingListBtn = new FancyPushButton({
				classNames: [
					'lb-project-download-dialog__action',
					'lb-project-download-dialog__action--primary',
				],
				filled: true,
				leadingIcon: {name: 'download'},
				parent: btnParent,
				text: 'Mailing list',
			});
			Obj.connect(mailingListBtn, 'clicked', this, 'mailingListDownloadClicked');
			if (this.downloadWithOwnerNameEnabled) {
				const ownerBtn = new FancyPushButton({
					classNames: [
						'lb-project-download-dialog__action',
						'lb-project-download-dialog__action--secondary',
					],
					leadingIcon: {name: 'badge'},
					parent: btnParent,
					text: 'With owner names',
				});
				Obj.connect(ownerBtn, 'clicked', this, 'mailingListWithOwnersDownloadClicked');
			}
			dia.open();
		}
	}

	@SLOT
	private mailingListDownloadClicked(): void {
		this.triggerListDownload(this.absoluteListUri);
	}

	@SLOT
	private mailingListWithOwnersDownloadClicked(): void {
		if (!this.absoluteListUri) {
			return;
		}
		this.triggerListDownload(`${this.absoluteListUri}?opt=owner`);
	}

	private triggerListDownload(url: string): void {
		if (!url) {
			return;
		}
		const a = document.createElement('a');
		a.href = url;
		a.setAttribute('download', '');
		a.style.display = 'none';
		document.body.appendChild(a);
		a.click();
		a.remove();
		const dia = this.activeDownloadDialog;
		this.activeDownloadDialog = null;
		if (dia) {
			// Defer close so the browser-initiated download navigation is not
			// torn down alongside MDCDialog's close animation in the same tick.
			window.setTimeout(() => dia.close(), 0);
		}
	}

	@SLOT
	private drawerClosed(): void {
		if (this.primaryInput) {
			this.primaryInput.setLeadingIcon({icon: 'search', title: 'Search places'});
		}
	}

	@SLOT
	private drawerOpened(): void {
		if (this.primaryInput) {
			this.primaryInput.setLeadingIcon({icon: 'search', title: 'Search places'});
		}
	}

	@SLOT
	private async drawerTitleSaved(title: string): Promise<void> {
		const project = await this.fetchProject();
		this.projectUpdated(await this.updateProject({...project, title}));
	}

	@SLOT
	private async endPayment(success: boolean): Promise<void> {
		if (!this.paymentDialog) {
			return;
		}
		if (success) {
			this.projectUpdated(await this.fetchProject({syncPayment: true}));

		}
		this.paymentProcessEnded(success);
		this.destroyPaymentDialog();
	}

	private async fetchInvoice(sync: boolean = false, cfg?: SafeReqCfg): Promise<IInvoice> {
		const proj = await this.fetchProject({syncInvoice: sync}, cfg);
		if (proj.invoiceId !== null) {
			return await svc.invoice.get(proj.invoiceId, cfg);
		}
		throw new Error('Project failed to return invoice');
	}

	private async fetchParcel(pk: ParcelPk): Promise<IParcel> {
		return await svc.parcel.get(pk);
	}

	private async fetchParcels(point: GeoCoordinate): Promise<Array<IParcel>> {
		return await svc.parcel.list(point);
	}

	// ───────── Hover quick-info popup ─────────
	//
	// As the pointer dwells (~300ms) over a parcel, fetch its detail once
	// (cached by area_pk) and show a compact tooltip that follows the
	// cursor: address + owner + total value + zoning. Tiles carry only
	// area_pk, so the info comes from svc.parcel.list at the hover coord.
	// Always-on, any map mode. The map fires parcelHovered/parcelHoverEnded.

	private static readonly HOVER_DWELL_MS = 300;
	private hoverTooltipEl: HTMLElement | null = null;
	private hoverDwellTimer: number | null = null;
	private hoverParcelCache: Map<string, IParcel> = new Map();
	private hoverActiveAreaPk: string | null = null;
	private hoverPendingAreaPk: string | null = null;
	private hoverLastX: number = 0;
	private hoverLastY: number = 0;

	@SLOT
	private onParcelHovered(areaPk: number | string, lng: number, lat: number, clientX: number, clientY: number): void {
		this.hoverLastX = clientX;
		this.hoverLastY = clientY;
		const key = String(areaPk);
		if (this.hoverActiveAreaPk === key) {
			// Same parcel, tooltip already shown → follow the cursor.
			this.positionHoverTooltip(clientX, clientY);
			return;
		}
		if (this.hoverPendingAreaPk === key) {
			// Dwell already counting down for this parcel — keep waiting.
			return;
		}
		// Moved onto a different parcel: reset.
		this.hoverPendingAreaPk = key;
		this.hoverActiveAreaPk = null;
		this.hideHoverTooltip();
		if (this.hoverDwellTimer !== null) {
			window.clearTimeout(this.hoverDwellTimer);
			this.hoverDwellTimer = null;
		}
		const cached = this.hoverParcelCache.get(key);
		if (cached) {
			this.revealHoverParcel(key, cached);
			return;
		}
		this.hoverDwellTimer = window.setTimeout(() => {
			this.hoverDwellTimer = null;
			void this.fetchHoverParcel(key, lng, lat);
		}, ProjectDetailView.HOVER_DWELL_MS);
	}

	@SLOT
	private onParcelHoverEnded(): void {
		this.hoverPendingAreaPk = null;
		this.hoverActiveAreaPk = null;
		if (this.hoverDwellTimer !== null) {
			window.clearTimeout(this.hoverDwellTimer);
			this.hoverDwellTimer = null;
		}
		this.hideHoverTooltip();
	}

	private async fetchHoverParcel(key: string, lng: number, lat: number): Promise<void> {
		// The pointer may have moved on while the dwell timer ran.
		if (this.hoverPendingAreaPk !== key) {
			return;
		}
		let parcel = this.hoverParcelCache.get(key) || null;
		if (!parcel) {
			try {
				const parcels = await this.fetchParcels(new GeoCoordinate(lat, lng));
				parcel = parcels.find(p => String(p.areaId) === key) || parcels[0] || null;
				if (parcel) {
					this.hoverParcelCache.set(key, parcel);
				}
			} catch (exc) {
				logger.warning('fetchHoverParcel: parcel fetch failed: %s', exc);
				return;
			}
		}
		// Pointer moved to another parcel (or left) during the fetch.
		if (this.hoverPendingAreaPk !== key || !parcel) {
			return;
		}
		this.revealHoverParcel(key, parcel);
	}

	private revealHoverParcel(key: string, parcel: IParcel): void {
		this.hoverActiveAreaPk = key;
		this.renderHoverTooltip(parcel);
		this.positionHoverTooltip(this.hoverLastX, this.hoverLastY);
	}

	private ensureHoverTooltip(): HTMLElement {
		if (!this.hoverTooltipEl) {
			const el = document.createElement('div');
			el.className = 'lb-parcel-hover-tip';
			document.body.appendChild(el);
			this.hoverTooltipEl = el;
		}
		return this.hoverTooltipEl;
	}

	private renderHoverTooltip(parcel: IParcel): void {
		const el = this.ensureHoverTooltip();
		el.textContent = '';
		const title = document.createElement('div');
		title.className = 'lb-parcel-hover-tip__title';
		title.textContent = parcel.streetAddress || this.parcelPrimaryTitle(parcel) || 'Parcel';
		el.appendChild(title);
		const ownerName = (parcel.owners && parcel.owners.length)
			? (parcel.owners[0].name || '').trim()
			: '';
		if (ownerName) {
			const owner = document.createElement('div');
			owner.className = 'lb-parcel-hover-tip__owner';
			owner.textContent = ownerName;
			el.appendChild(owner);
		}
		const stats = document.createElement('div');
		stats.className = 'lb-parcel-hover-tip__stats';
		const total = this.money(parcel.totalValue);
		const zoning = (parcel.zoning || '').trim();
		stats.textContent = [total ? `Total ${total}` : '', zoning].filter(Boolean).join(' · ');
		if (stats.textContent) {
			el.appendChild(stats);
		}
		el.style.display = 'block';
	}

	private positionHoverTooltip(clientX: number, clientY: number): void {
		const el = this.hoverTooltipEl;
		if (!el || el.style.display === 'none') {
			return;
		}
		const OFFSET = 14;
		const rect = el.getBoundingClientRect();
		const vw = window.innerWidth;
		const vh = window.innerHeight;
		let left = clientX + OFFSET;
		let top = clientY + OFFSET;
		if (left + rect.width > vw - 8) {
			left = clientX - OFFSET - rect.width;
		}
		if (top + rect.height > vh - 8) {
			top = clientY - OFFSET - rect.height;
		}
		el.style.left = `${Math.max(8, left)}px`;
		el.style.top = `${Math.max(8, top)}px`;
	}

	private hideHoverTooltip(): void {
		if (this.hoverTooltipEl) {
			this.hoverTooltipEl.style.display = 'none';
		}
	}

	private async fetchPaymentIntent(): Promise<IPaymentIntent> {
		return await svc.payment.create({projectSlug: this.slug});
	}

	private async fetchPlaces(name: string): Promise<Array<IPlace>> {
		return await svc.place.list(name);
	}

	private async fetchFilters(opt?: Partial<Omit<IFilterListOpt, 'projectSlug'>>): Promise<Array<IFilter>> {
		return await svc.filter.list({
			...(opt || {}),
			projectSlug: this.slug,
		});
	}

	private async fetchProject(opt?: Partial<{syncInvoice: boolean; syncPayment: boolean;}>, cfg?: SafeReqCfg): Promise<IProject> {
		return await svc.project.get(this.slug, opt, cfg);
	}

	private async fetchGeoMapConfiguration(pk: GeoMapConfigurationPk): Promise<IGeoMapConfiguration> {
		return await svc.map.get(pk);
	}

	private async fetchGeoRefs(): Promise<Array<IGeoRef>> {
		return await svc.geoRef.list();
	}

	@SLOT
	private async filterCreated(filter: IFilter): Promise<void> {
		this.clearPendingFilterCreateFeedback();
		if (filter.geometry) {
			this.filterMgr.flyToFilter(filter);
		}
	}

	flyTo(bbox: [number, number, number, number]): void {
		//  long                lat                long                 lat
		// [-77.82740230327359, 34.223342940030975, -77.78976892279576, 34.25637110856619]
		const [long0, lat0, long1, lat1] = bbox;
		const coord0 = new GeoCoordinate(lat0, long0);
		const coord1 = new GeoCoordinate(lat1, long1);
		this.map.fitViewportToGeoShape(
			new GeoRectangle(coord1, coord0),
			{padding: 64});
	}

	// Phase 3c: fly the project map to a parcel + drop a brief outline
	// highlight so the user can see what they navigated to. Wraps the
	// existing `flyTo(bbox)` primitive; the bbox comes from the parcel
	// area's PostGIS extent (`ParcelSerializer.area_bbox`, lng/lat).
	//
	// When the parcel argument already carries `areaBbox` (e.g. the
	// payload came from `/api/parcels/<uid>/` which `select_related`s
	// the area), we fly immediately. Otherwise we round-trip the
	// detail endpoint to pick up the bbox, then fly. Workspace-pin
	// and unified-search payloads omit the bbox to keep their query
	// plans cheap, so this fallback path runs in those flows.
	//
	// Convention (mirrors filter chips): icon click = fly-to motion,
	// title/body click = navigate to /parcels/<uid>/.
	flyToParcel(parcel: IParcel | {uid: string; areaBbox?: [number, number, number, number] | null}): void {
		void this.flyToParcelAsync(parcel);
	}

	private async flyToParcelAsync(parcel: IParcel | {uid: string; areaBbox?: [number, number, number, number] | null}): Promise<void> {
		const uid = parcel && parcel.uid;
		if (!uid) {
			return;
		}
		let bbox = parcel.areaBbox && parcel.areaBbox.length === 4
			? parcel.areaBbox as [number, number, number, number]
			: null;
		if (!bbox) {
			try {
				const fresh = await svc.parcel.get(uid);
				bbox = fresh && fresh.areaBbox && fresh.areaBbox.length === 4
					? fresh.areaBbox as [number, number, number, number]
					: null;
			} catch (exc) {
				logger.warning('flyToParcel: failed to resolve bbox for %s', uid, exc);
				return;
			}
		}
		if (!bbox) {
			logger.warning('flyToParcel: no areaBbox for parcel %s', uid);
			return;
		}
		this.flyTo(bbox);
		this.highlightParcelOnMap(uid, bbox);
	}

	// Static outline highlight on arrival (3s + 500ms fade). Drops a
	// GeoJSON polygon source + line layer keyed by parcel uid, then
	// schedules teardown. Re-flying to the same parcel cancels the
	// pending teardown so the outline resets, rather than fading mid-
	// stream. We outline the bbox rectangle, not the parcel polygon —
	// adequate for "look here" and keeps the highlight independent of
	// the project's filter-based parcel tile layers.
	private parcelHighlightTimer: number | null = null;

	private highlightParcelOnMap(uid: string, bbox: [number, number, number, number]): void {
		const SOURCE_ID = 'lb-parcel-highlight-src';
		const LINE_LAYER_ID = 'lb-parcel-highlight-line';
		if (this.parcelHighlightTimer !== null) {
			window.clearTimeout(this.parcelHighlightTimer);
			this.parcelHighlightTimer = null;
		}
		this.map.removeLayer(LINE_LAYER_ID);
		this.map.removeSource(SOURCE_ID);
		const [west, south, east, north] = bbox;
		const polygon = {
			type: 'Feature' as const,
			properties: {uid},
			geometry: {
				type: 'Polygon' as const,
				coordinates: [[
					[west, south],
					[east, south],
					[east, north],
					[west, north],
					[west, south],
				]],
			},
		};
		this.map.addSource(SOURCE_ID, {
			type: 'geojson',
			data: polygon,
		} as any);
		this.map.addLayer({
			id: LINE_LAYER_ID,
			type: 'line',
			source: SOURCE_ID,
			paint: {
				'line-color': '#f97316',  // accent orange — pops against base maps
				'line-width': 3,
				'line-opacity': 1,
			},
		} as any);
		// 3s static, then a 500ms opacity fade, then teardown. Total
		// 3.5s. Using line-opacity-transition for the fade — set on
		// next tick so mapbox applies the transition to the OPACITY
		// change rather than the initial paint.
		const HOLD_MS = 3000;
		const FADE_MS = 500;
		this.parcelHighlightTimer = window.setTimeout(() => {
			this.map.setLayerPaintProperty(
				LINE_LAYER_ID,
				'line-opacity-transition',
				{duration: FADE_MS, delay: 0},
			);
			this.map.setLayerPaintProperty(LINE_LAYER_ID, 'line-opacity', 0);
			this.parcelHighlightTimer = window.setTimeout(() => {
				this.map.removeLayer(LINE_LAYER_ID);
				this.map.removeSource(SOURCE_ID);
				this.parcelHighlightTimer = null;
			}, FADE_MS + 50);
		}, HOLD_MS);
	}

	// Wires the leading icon on a workspace-card-shaped parcel card to
	// trigger fly-to-parcel. Idempotent — re-rendering a card and
	// re-binding is fine because we replace the previous handler.
	private bindParcelFlyToOnIcon(card: HTMLElement, parcel: IParcel): void {
		const icon = card.querySelector<HTMLElement>('.workspace-card__icon');
		if (!icon) {
			return;
		}
		icon.classList.add('workspace-card__icon--clickable');
		icon.setAttribute('role', 'button');
		icon.setAttribute('tabindex', '0');
		icon.setAttribute('aria-label', 'Show parcel on map');
		icon.setAttribute('title', 'Show on map');
		const handler = (evt: Event) => {
			evt.preventDefault();
			evt.stopPropagation();
			this.flyToParcel(parcel);
		};
		icon.onclick = handler;
		icon.onkeydown = evt => {
			const ke = evt as KeyboardEvent;
			if (ke.key === 'Enter' || ke.key === ' ') {
				handler(evt);
			}
		};
	}

	syncFilterSummaryFromFilterList(): void {
		if (!this.activeFilterSummaryEl) {
			this.activeFilterSummaryEl = document.getElementById('lb-project-active-filter-summary');
		}
		if (!this.activeFilterSummaryEl) {
			return;
		}
		const total = this.filterList.count();
		const active = this.filterList.activeFilterCount();
		const label = total === 0 ?
			'No filters' :
			`${active} of ${total} active`;
		const labelEl = document.createElement('span');
		labelEl.className = 'lb-project-active-filter-summary__label';
		labelEl.textContent = 'Active filters';
		const valueEl = document.createElement('span');
		valueEl.className = 'lb-project-active-filter-summary__value';
		valueEl.textContent = `${active}`;
		this.activeFilterSummaryEl.textContent = '';
		this.activeFilterSummaryEl.appendChild(labelEl);
		this.activeFilterSummaryEl.appendChild(valueEl);
		this.activeFilterSummaryEl.classList.toggle('is-empty', total === 0);
		this.activeFilterSummaryEl.classList.toggle('is-paused', (total > 0) && (active === 0));
		this.activeFilterSummaryEl.setAttribute('title', `${label} filter${total === 1 ? '' : 's'}`);
		if (this.isSideWorkspacePanelOpen()) {
			window.requestAnimationFrame(() => this.syncSideWorkspacePanelPosition());
		}
	}

	@SLOT
	private async geoMapMapClicked(point: Point, coord: GeoCoordinate): Promise<void> {
		this.lastMousePos = point;
		if (this.isAutoCompleteOpen()) {
			this.destroyAutoComplete();
		}
		switch (this.mapInteractiveControlMode()) {
			case InteractiveMapControlMode.ShapeSelect:
			case InteractiveMapControlMode.DrawShape:
				return;
			case InteractiveMapControlMode.InfoMode:
				await this.openParcelPanelFromCoord(coord);
				return;
		}
	}

	@SLOT
	private geoMapPopupClosed(): void {
		this.destroyParcelInfo();
	}

	@SLOT
	private geoMapStyleChanged(newStyleUrl: string): void {
		this.saveGeoMapConfiguration();
	}

	private hasPopupsOpen(): boolean {
		return this.map.hasPopupsOpen();
	}

	private async init(): Promise<void> {
		document.addEventListener('keydown', this.documentEvent, true);
		window.addEventListener('resize', this.windowEvent);
		// Restore drawer mode (rail vs expanded) from localStorage for
		// this project slug; wire the collapse + rail-expand click
		// handlers. ESC also toggles via documentKeyDownEvent's default
		// branch. See setDrawerMode / toggleDrawerMode above.
		this.restoreDrawerMode();
		this.wireDrawerModeControls();
		// R1: restore a custom drawer width (if the user resized it on a
		// prior visit) and wire the drag-to-resize handle.
		this.restoreDrawerWidth();
		this.wireDrawerResize();
		// R2: same, for the side workspace panel.
		this.restoreSidePanelWidth();
		this.wireSidePanelResize();
		Obj.connect(
			this, 'parcelExclusionChanged',
			this, 'syncParcelInfo');
		Obj.connect(
			this, 'parcelExclusionChanged',
			this, 'syncDoNotMailGeoRefs');
		Obj.connect(
			this, 'parcelExclusionChanged',
			this, 'syncVisibleParcels');
		Obj.connect(
			this, 'projectUpdated',
			this, 'setDocumentForProject');
		Obj.connect(
			this, 'paymentProcessEnded',
			this, 'paymentEnded');
		Obj.connect(
			this.drawer, 'opened',
			this, 'drawerOpened');
		Obj.connect(
			this.drawer, 'closed',
			this, 'drawerClosed');
		Obj.connect(
			this.drawer, 'openChanged',
			this, 'updateAutoCompletePosition');
		const filterChipHost = document.getElementById('lb-project-filter-chip-list');
		const filterListEl = this.filterList.element();
		if (filterChipHost && filterListEl) {
			filterChipHost.appendChild(filterListEl);
		} else {
			this.drawer.appendChild(this.filterList);
		}
		this.initAddFilterButton();
		const statsRoot = this.stats.element();
		const statsHost = document.getElementById('lb-project-detail-stats-host');
		if (statsRoot && statsHost) {
			statsHost.appendChild(statsRoot);
		} else {
			this.drawer.appendChild(this.stats);
		}
		this.undoView = new UndoView(this);
		Obj.connect(
			this.undoView, 'actionActivated',
			this, 'undoViewActionActivated');
		const actionHost = document.getElementById('lb-project-detail-actions');
		const undoRoot = this.undoView.element();
		if (actionHost && undoRoot) {
			actionHost.appendChild(undoRoot);
		} else {
			this.drawer.appendChild(this.undoView);
		}
		const primaryInputRoot = document.getElementById('id_lb-project-detail-primary-input');
		if (primaryInputRoot) {
			this.primaryInput = new TextInput({
				leadingIcon: {
					interactive: true,
					outlined: false,
					icon: 'search',
					title: 'Search places',
				},
				noLabel: true,
				placeholder: 'Search a place, owner, or draw an area...',
				root: primaryInputRoot,
			});
			Obj.connect(
				this.primaryInput, 'textChanged',
				this, 'textInputTextChanged');
			Obj.connect(
				this.primaryInput, 'iconActivated',
				this, 'primaryInputIconActivated');
			this.primaryInputDomEl = primaryInputRoot.querySelector<HTMLInputElement>('.mdc-text-field__input');
			if (this.primaryInputDomEl) {
				this.primaryInputDomEl.addEventListener('click', this.primaryInputFocusEvent);
				this.primaryInputDomEl.addEventListener('focus', this.primaryInputFocusEvent);
			}
		} else {
			logger.error('init: Primary input root element was not found');
		}
		// Deal-forward PR D: read the server-resolved boot mode up front —
		// it gates the workspace-panel + drawer-nav setup below (deals-mode
		// hides the Print Jobs tab). The attribute is server-rendered on
		// the root, so it's available before any fetch.
		const projectDetailRoot = document.getElementById('id_lb-project-detail');
		if (projectDetailRoot) {
			const mode = projectDetailRoot.getAttribute('data-project-mode');
			this.projectMode = mode === 'deals' ? 'deals' : 'lists';
		}
		this.initUnifiedWorkspacePanel();
		this.initSideWorkspacePanel();
		this.activeFilterSummaryEl = document.getElementById('lb-project-active-filter-summary');
		this.syncFilterSummaryFromFilterList();
		const proj = await this.fetchProject();
		await this.syncPinnedWorkspaceItems().catch(exc => logger.error('init: Failed to sync pinned workspace items', exc));
		if (projectDetailRoot) {
			setProjectSurfaceData(projectDetailRoot, proj);
		}
		this.initProjectFlyBack(proj);
		this.projectFilterSchemaVersion = Number(proj.filterSchemaVersion) || 1;
		this.renderProjectOptInBanner();
		Obj.connect(
			this.downloadPay, 'buttonClicked',
			this, 'downloadPayButtonClicked');
		this.initDrawerWorkspaceNav();
		this.drawer.appendChild(this.downloadPay);
		await this.syncDownloadPayButton(proj);
		this.setDocumentTitle(proj.title);
		this.drawer.setTitle(proj.title);
		this.drawer.open();
		this.filterMgr.syncFilterList().catch(exc => logger.error('init: Failed to sync filter list', exc));
		Obj.connect(
			this.drawer, 'titleSaved',
			this, 'drawerTitleSaved');
		const mapCfg = proj.geoMapConfigurationId ?
			await this.fetchGeoMapConfiguration(proj.geoMapConfigurationId) :
			null;
		this.mapCfgPk = mapCfg ?
			mapCfg.id :
			null;
		Obj.connect(
			this.map, 'loaded',
			this, 'mapLoaded');
		Obj.connect(
			this.map, 'mapClicked',
			this, 'geoMapMapClicked');
		Obj.connect(
			this.map, 'parcelHovered',
			this, 'onParcelHovered');
		Obj.connect(
			this.map, 'parcelHoverEnded',
			this, 'onParcelHoverEnded');
		Obj.connect(
			this.map, 'cameraChanged',
			this, 'saveGeoMapConfiguration');
		Obj.connect(
			this.map, 'interactiveControlModeChanged',
			this, 'mapInteractiveControlModeChanged');
		Obj.connect(
			this.map, 'popupClosed',
			this, 'geoMapPopupClosed');
		Obj.connect(
			this.map, 'styleChanged',
			this, 'geoMapStyleChanged');
		Obj.connect(
			this.map, 'featureSelectionChanged',
			this, 'mapFeatureSelectionChanged');
		if (mapCfg) {
			this.map.load(
				'id_lb-project-detail-map',
				geoMapConfigurationToMapOpts(mapCfg));
		} else {
			logger.error('init: Project failed to create map configuration');
		}
		Obj.connect(
			this.filterMgr, 'filterCreated',
			this, 'filterCreated');
		Obj.connect(
			this.filterMgr, 'filterCreated',
			this, 'syncCounts');
		Obj.connect(
			this.filterMgr, 'filterCreated',
			this, 'syncVisibleParcels');
		Obj.connect(
			this.filterMgr, 'filterUpdated',
			this, 'syncCounts');
		Obj.connect(
			this.filterMgr, 'filterUpdated',
			this, 'syncVisibleParcels');
		Obj.connect(
			this.filterMgr, 'filterDeleted',
			this, 'syncCounts');
		Obj.connect(
			this.filterMgr, 'filterDeleted',
			this, 'syncVisibleParcels');
		setTimeout(() => this.syncCounts(), 9);
		if (proj.readOnly !== this.disabled) {
			this.setDisabled(proj.readOnly);
		}
		// Deal-forward PR D: deal-shaped projects boot to the Pinned
		// workspace (pinned parcels + scenarios via the shared DealBlock
		// component) instead of the filters-first default. The filter
		// chip strip + drawer stay present, just secondary. Lists-mode is
		// unchanged.
		if (this.projectMode === 'deals') {
			this.openSideWorkspacePanel('owners');
		}
	}

	private initAutoComplete(): void {
		if (!this.autoComplete) {
			this.autoComplete = new AutoComplete();
			Obj.connect(
				this.autoComplete, 'activated',
				this, 'autoCompleteActivated');
			ElObj.body().appendChild(this.autoComplete);
			this.updateAutoCompletePosition();
		}
	}

	private initAddFilterButton(): void {
		const addFilterButton = document.getElementById('lb-project-add-filter-chip');
		if (addFilterButton instanceof HTMLButtonElement) {
			// Lane E PR 1e-ii — "+ Add filter" opens the two-zone editor (with a ready
			// "+ Add condition" row) instead of the search panel, so attribute
			// conditions are reachable even on an empty project. Place/owner/address
			// search is still available from the top search bar.
			addFilterButton.addEventListener('click', () => this.openProjectFilterEditor());
		}
	}

	private initDrawerWorkspaceNav(): void {
		const drawerInner = document.getElementById('id_lb-project-detail-drawer-inner');
		if (!drawerInner || drawerInner.querySelector('.lb-project-drawer-workspace-nav')) {
			return;
		}
		const nav = document.createElement('nav');
		nav.className = 'lb-project-drawer-workspace-nav drawer__nav';
		nav.setAttribute('aria-label', 'Project workspace');
		const label = document.createElement('div');
		label.className = 'lb-project-drawer-workspace-nav__title section-label';
		label.textContent = 'Workspace';
		nav.appendChild(label);
		// Deal-forward: deals-mode hides the Print Jobs entry here too
		// (mirrors the workspace-panel tab hidden in initSideWorkspacePanel).
		([
			['print', 'print', 'Print Jobs'],
			['owners', 'push_pin', 'Pinned'],
			['parcel', 'select_all', 'Parcel'],
		] as Array<[ProjectWorkspaceTab, string, string]>)
			.filter(([tab]) => !(this.projectMode === 'deals' && tab === 'print'))
			.forEach(([tab, icon, text]) => {
				const button = document.createElement('button');
				button.type = 'button';
				button.className = 'nav-item';
				button.dataset.workspaceTab = tab;
				button.innerHTML = `<span class="material-icons" aria-hidden="true">${icon}</span><span>${text}</span>`;
				button.addEventListener('click', () => this.openSideWorkspacePanel(tab));
				nav.appendChild(button);
			});
		// Phase 5C polish — discoverability slice: "Deals" + "Discover"
		// sub-tabs in the project workspace sidebar. Unlike the panel-
		// switcher buttons above, these navigate away to project-
		// scoped Deal Calc surfaces. Rendered as `<a>` for the
		// platform's native middle-click / cmd-click behaviors.
		const deals = document.createElement('a');
		deals.className = 'nav-item nav-item--link';
		deals.href = `/${encodeURIComponent(this.slug)}/deals/`;
		deals.innerHTML = '<span class="material-icons" aria-hidden="true">calculate</span><span>Deals</span>';
		nav.appendChild(deals);
		const discover = document.createElement('a');
		discover.className = 'nav-item nav-item--link';
		discover.href = `/${encodeURIComponent(this.slug)}/discover/`;
		discover.innerHTML = '<span class="material-icons" aria-hidden="true">travel_explore</span><span>Discover</span>';
		nav.appendChild(discover);
		// Deal-forward PR D follow-up: per-project mode toggle. Reflects
		// the resolved boot mode; picking the other option pins it
		// explicitly and reloads so Project Detail re-boots into that
		// layout. 'auto' stays the untouched default until the user picks.
		const modeLabel = document.createElement('div');
		modeLabel.className = 'lb-project-drawer-workspace-nav__title section-label';
		modeLabel.textContent = 'Mode';
		nav.appendChild(modeLabel);
		([
			['lists', 'Lists'],
			['deals', 'Deals'],
		] as Array<[ProjectMode, string]>).forEach(([mode, text]) => {
			const active = this.projectMode === mode;
			const button = document.createElement('button');
			button.type = 'button';
			button.className = active ? 'nav-item is-active' : 'nav-item';
			button.setAttribute('aria-pressed', active ? 'true' : 'false');
			const icon = active ? 'radio_button_checked' : 'radio_button_unchecked';
			button.innerHTML = `<span class="material-icons" aria-hidden="true">${icon}</span><span>${text}</span>`;
			if (!active) {
				button.addEventListener('click', () => void this.setProjectMode(mode));
			}
			nav.appendChild(button);
		});
		drawerInner.appendChild(nav);
	}

	// Persist the project's boot mode, then reload so the server-resolved
	// layout (data-project-mode + deal-centric workspace boot) re-applies.
	private async setProjectMode(mode: ProjectMode): Promise<void> {
		try {
			await svc.project.setMode(this.slug, mode);
		} catch (exc) {
			logger.error('setProjectMode: failed to persist mode', exc);
			return;
		}
		window.location.reload();
	}

	private initProjectFlyBack(project: IProject): void {
		const backLink = document.getElementById('id_lb-project-detail-drawer-title');
		if (!(backLink instanceof HTMLAnchorElement)) {
			return;
		}
		backLink.addEventListener('click', event => {
			event.preventDefault();
			const camera = this.map.camera();
			writeProjectFlyBack({
				center: camera ? camera.center : undefined,
				projectId: project.slug,
				zoom: camera ? camera.zoom : undefined,
			});
			document.body.classList.add('lb-project-is-flying-back');
			window.setTimeout(() => {
				window.location.assign(backLink.href);
			}, 360);
		});
	}

	private isAutoCompleteOpen(): boolean {
		return Boolean(this.autoComplete && this.autoComplete.isOpen());
	}

	private focusOwnerSearch(): void {
		this.openSideWorkspacePanel('owners');
	}

	private initSideWorkspacePanel(): void {
		const panel = document.getElementById('lb-project-workspace-panel');
		if (!(panel instanceof HTMLElement)) {
			return;
		}
		this.sideWorkspacePanelEl = panel;
		const grip = panel.querySelector<HTMLButtonElement>('.up-grip');
		if (grip) {
			this.bindPanelGrip(grip, panel);
		}
		const closeButton = document.getElementById('lb-project-workspace-panel-close');
		if (closeButton instanceof HTMLButtonElement) {
			closeButton.addEventListener('click', () => this.closeSideWorkspacePanel());
		}
		panel.querySelectorAll<HTMLButtonElement>('[data-workspace-tab]').forEach(button => {
			const tab = button.dataset.workspaceTab as ProjectWorkspaceTab | undefined;
			if (tab) {
				button.addEventListener('click', () => this.setSideWorkspaceTab(tab));
			}
		});

		const printPanel = document.getElementById('lb-print-sync-panel');
		const printHost = document.getElementById('lb-project-workspace-print-host');
		if (printPanel && printHost) {
			printHost.appendChild(printPanel);
		}
		// Deal-forward: deals-mode projects hide the Print Jobs tab + its
		// panel (printing is a list/mailing concern, not a deal one) and
		// default to the Pinned tab. `style.display` (not the `hidden`
		// attr) so it wins over the `.ws-tab` display rule.
		const dealsMode = this.projectMode === 'deals';
		if (dealsMode) {
			panel.querySelectorAll<HTMLElement>(
				'[data-workspace-tab="print"], [data-workspace-panel="print"]',
			).forEach(el => {
				el.style.display = 'none';
			});
		}
		this.setSideWorkspaceTab(dealsMode ? 'owners' : 'print');
		this.closeSideWorkspacePanel();
	}

	private closeSideWorkspacePanel(): void {
		if (!this.sideWorkspacePanelEl) {
			return;
		}
		this.sideWorkspacePanelEl.classList.remove('is-open');
		this.sideWorkspacePanelEl.setAttribute('aria-hidden', 'true');
	}

	private openSideWorkspacePanel(tab: ProjectWorkspaceTab = this.sideWorkspaceTab): void {
		if (!this.sideWorkspacePanelEl) {
			return;
		}
		this.closeUnifiedWorkspacePanel();
		this.setSideWorkspaceTab(tab);
		this.syncSideWorkspacePanelPosition();
		this.sideWorkspacePanelEl.classList.add('is-open');
		this.sideWorkspacePanelEl.setAttribute('aria-hidden', 'false');
	}

	private isSideWorkspacePanelOpen(): boolean {
		return Boolean(this.sideWorkspacePanelEl && this.sideWorkspacePanelEl.classList.contains('is-open'));
	}

	private setSideWorkspaceTab(tab: ProjectWorkspaceTab): void {
		this.sideWorkspaceTab = tab;
		const panel = this.sideWorkspacePanelEl || document.getElementById('lb-project-workspace-panel');
		if (!panel) {
			return;
		}
		const subtitles: {[key in ProjectWorkspaceTab]: string} = {
			owners: 'Pinned owners, parcels, places, and boundaries.',
			parcel: 'Click a parcel with the map info tool to inspect and pin it.',
			print: 'Print and download workflow for this project.',
		};
		const title = document.getElementById('lb-project-workspace-panel-title');
		const subtitle = document.getElementById('lb-project-workspace-panel-subtitle');
		if (title) {
			title.textContent = 'Workspace';
		}
		if (subtitle) {
			subtitle.textContent = subtitles[tab];
		}
		panel.querySelectorAll<HTMLElement>('[data-workspace-panel]').forEach(host => {
			host.hidden = host.dataset.workspacePanel !== tab;
		});
		panel.querySelectorAll<HTMLButtonElement>('[data-workspace-tab]').forEach(button => {
			button.classList.toggle('is-on', button.dataset.workspaceTab === tab);
			button.classList.toggle('ws-tab--active', button.dataset.workspaceTab === tab);
		});
		this.renderSideWorkspacePanel();
	}

	private renderSideWorkspacePanel(): void {
		this.renderPinnedWorkspacePanel();
		this.renderParcelWorkspacePanel();
	}

	private workspaceItemToPinnedItem(item: IWorkspaceItem): IPinnedWorkspaceItem | null {
		const payload = item.payloadJson || {};
		if (item.itemType === 'parcel') {
			const parcel = payload.parcel;
			if (!parcel || !parcel.uid) {
				return null;
			}
			// Phase 2: default the scenario list to expanded when the
			// parcel already has in-project scenarios — the picker is
			// the primary affordance for that parcel and a click-to-
			// reveal toggle adds friction. Parcels with no in-project
			// scenarios (and only cross-project / standalone hints, or
			// nothing) stay collapsed so the card body remains compact.
			const attached = item.attachedDeals || [];
			const hasInProjectScenarios = attached.some(
				d => !!d.projectSlug && d.projectSlug === this.slug,
			);
			return {
				expanded: false,
				kind: 'parcel',
				parcel,
				workspaceItemId: item.id,
				attachedDeals: attached,
				scenariosExpanded: hasInProjectScenarios,
			};
		}
		if (item.itemType === 'owner') {
			const row = payload.row;
			if (!row) {
				return null;
			}
			return {
				expanded: false,
				key: item.targetKey,
				kind: 'owner',
				row,
				workspaceItemId: item.id,
			};
		}
		return null;
	}

	private upsertPinnedWorkspaceItem(item: IPinnedWorkspaceItem): void {
		const index = this.pinnedWorkspaceItems.findIndex(existing => {
			if (existing.kind !== item.kind) {
				return false;
			}
			if (existing.kind === 'parcel' && item.kind === 'parcel') {
				return existing.parcel.uid === item.parcel.uid;
			}
			if (existing.kind === 'owner' && item.kind === 'owner') {
				return existing.key === item.key;
			}
			return false;
		});
		if (index >= 0) {
			this.pinnedWorkspaceItems[index] = item;
		} else {
			this.pinnedWorkspaceItems.unshift(item);
		}
	}

	private async syncPinnedWorkspaceItems(): Promise<void> {
		const items = await svc.workspaceItem.list(this.slug);
		this.pinnedWorkspaceItems = items
			.map(item => this.workspaceItemToPinnedItem(item))
			.filter((item): item is IPinnedWorkspaceItem => Boolean(item));
		this.renderPinnedWorkspacePanel();
	}

	// Phase 4: promote a standalone deal (no project_block) into this
	// project. The server creates a fresh ProjectBlock(type=DEAL) and
	// attaches the deal; we then refresh workspace items so the deal
	// flips from the "Also in other projects" footnote into the in-
	// project scenario list. The button is disabled mid-flight to
	// prevent double-fire; restored on failure.
	private async promoteStandaloneDealIntoProject(
		dealSlug: string,
		btn: HTMLButtonElement,
	): Promise<void> {
		btn.disabled = true;
		const originalText = btn.textContent;
		btn.textContent = 'Moving…';
		try {
			await svc.deal.promoteToProject(dealSlug, this.slug);
		} catch (exc) {
			btn.disabled = false;
			btn.textContent = originalText;
			throw exc;
		}
		// Re-fetch workspace items so attachedDeals reflect the new
		// home. The promoted deal's projectSlug is now this project, so
		// it'll partition into the in-project list on re-render.
		await this.syncPinnedWorkspaceItems();
	}

	private async unpinWorkspaceItem(index: number): Promise<void> {
		const item = this.pinnedWorkspaceItems[index];
		if (!item) {
			return;
		}
		if (item.workspaceItemId !== undefined) {
			await svc.workspaceItem.delete(this.slug, item.workspaceItemId);
		}
		this.pinnedWorkspaceItems.splice(index, 1);
		this.renderPinnedWorkspacePanel();
	}

	private renderPinnedWorkspacePanel(): void {
		const host = document.getElementById('lb-project-workspace-owner-host');
		if (!(host instanceof HTMLElement)) {
			return;
		}
		host.textContent = '';
		if (!this.pinnedWorkspaceItems.length) {
			host.appendChild(this.workspaceEmptyCard(
				'Nothing pinned yet.',
				'Pin parcels from the Parcel tab or owner groups from Owner Search without changing active filters.'));
			return;
		}
		// Drop selected slugs that no longer exist in the current pin
		// set (e.g. the underlying scenario was deleted elsewhere).
		this.pruneDealCompareSelection();
		const listEl = document.createElement('div');
		listEl.className = 'workspace-card-list';
		this.pinnedWorkspaceItems.forEach((item, index) => {
			listEl.appendChild(item.kind === 'parcel' ?
				this.renderPinnedParcelCard(item, index) :
				this.renderPinnedOwnerCard(item, index));
		});
		host.appendChild(listEl);
		this.renderDealCompareBar(host);
	}

	// In-memory selection of Deal scenarios across the project's
	// pinned parcels, used to launch the comparison view (Phase 5B Q6:
	// multi-select up to 2 scenarios → "Compare" CTA).
	private dealCompareSelection: Set<string> = new Set();

	private pruneDealCompareSelection(): void {
		// Only in-project scenarios are selectable on this surface; the
		// "elsewhere" footnote rows have no checkbox. So a previously-
		// selected slug that's no longer in-project (deal got deleted
		// or — once Phase 4 lands — moved to another project) needs to
		// be dropped from the selection here.
		const live: Set<string> = new Set();
		for (const item of this.pinnedWorkspaceItems) {
			if (item.kind !== 'parcel') {
				continue;
			}
			for (const d of item.attachedDeals) {
				if (this.isAttachedDealInThisProject(d)) {
					live.add(d.slug);
				}
			}
		}
		for (const slug of Array.from(this.dealCompareSelection)) {
			if (!live.has(slug)) {
				this.dealCompareSelection.delete(slug);
			}
		}
	}

	// Cohort sizing must match the dealcalc view's COHORT_MIN /
	// COHORT_MAX (Phase 5E). Frontend constants intentionally
	// duplicated rather than imported because projectdetail and
	// dealcalc are otherwise independent bundles' worth of code.
	private static readonly COHORT_MIN: number = 2;
	private static readonly COHORT_MAX: number = 6;

	private toggleDealCompareSelection(slug: string): boolean {
		if (this.dealCompareSelection.has(slug)) {
			this.dealCompareSelection.delete(slug);
			return false;
		}
		if (this.dealCompareSelection.size >= ProjectDetailView.COHORT_MAX) {
			return false;
		}
		this.dealCompareSelection.add(slug);
		return true;
	}

	private renderDealCompareBar(host: HTMLElement): void {
		if (this.dealCompareSelection.size === 0) {
			return;
		}
		const bar = document.createElement('div');
		bar.className = 'workspace-card-compare-bar';
		const summary = document.createElement('span');
		summary.className = 'workspace-card-compare-bar__summary';
		const n = this.dealCompareSelection.size;
		const min = ProjectDetailView.COHORT_MIN;
		const max = ProjectDetailView.COHORT_MAX;
		summary.textContent = n >= min
			? `${n} scenarios selected${n < max ? ' — add more or compare' : ' (max)'}`
			: `${n} scenario selected — pick at least ${min - n} more`;
		bar.appendChild(summary);
		const btn = document.createElement('button');
		btn.type = 'button';
		btn.className = 'workspace-card__button workspace-card-compare-bar__cta';
		btn.disabled = n < min;
		btn.textContent = 'Compare';
		btn.addEventListener('click', () => {
			if (this.dealCompareSelection.size < min) {
				return;
			}
			const slugs = Array.from(this.dealCompareSelection).map(encodeURIComponent);
			window.location.assign(`/deals/compare/${slugs.join('/')}/`);
		});
		bar.appendChild(btn);
		const clear = document.createElement('button');
		clear.type = 'button';
		clear.className = 'workspace-card__button';
		clear.textContent = 'Clear';
		clear.addEventListener('click', () => {
			this.dealCompareSelection.clear();
			this.renderPinnedWorkspacePanel();
		});
		bar.appendChild(clear);
		host.appendChild(bar);
	}

	private renderParcelWorkspacePanel(): void {
		const host = document.getElementById('lb-project-workspace-parcel-host');
		if (!(host instanceof HTMLElement)) {
			return;
		}
		host.textContent = '';
		if (!this.parcelWorkspaceParcels.length) {
			host.appendChild(this.workspaceEmptyCard(
				'Select a parcel with the map info tool.',
				'Parcel details now open here instead of the legacy popup inspector.'));
			return;
		}
		const listEl = document.createElement('div');
		listEl.className = 'workspace-card-list';
		if (this.parcelWorkspaceParcels.length > 1) {
			const note = document.createElement('div');
			note.className = 'workspace-note';
			note.textContent = `${numberFormat(this.parcelWorkspaceParcels.length)} parcels found at this click point.`;
			listEl.appendChild(note);
		}
		this.parcelWorkspaceParcels.forEach((parcel, index) => {
			listEl.appendChild(this.renderParcelDetailCard(parcel, {
				actions: 'parcel-tab',
				index,
			}));
		});
		host.appendChild(listEl);
	}

	private workspaceEmptyCard(title: string, body: string): HTMLElement {
		const empty = document.createElement('div');
		empty.className = 'lb-project-workspace-empty';
		const strong = document.createElement('strong');
		strong.textContent = title;
		const span = document.createElement('span');
		span.textContent = body;
		empty.appendChild(strong);
		empty.appendChild(span);
		return empty;
	}

	private renderPinnedParcelCard(item: IPinnedParcelWorkspaceItem, index: number): HTMLElement {
		if (item.expanded) {
			return this.renderParcelDetailCard(item.parcel, {
				actions: 'pinned',
				index,
				attachedDeals: item.attachedDeals,
				scenariosExpanded: item.scenariosExpanded,
				onToggleScenarios: () => {
					item.scenariosExpanded = !item.scenariosExpanded;
					this.renderPinnedWorkspacePanel();
				},
			});
		}
		const card = this.workspaceCard('select_all');
		card.classList.add('workspace-card--collapsed');
		const body = card.querySelector<HTMLElement>('.workspace-card__body');
		if (!body) {
			return card;
		}
		// Phase 3c: icon click flies the map to + briefly outlines the
		// parcel. Title click navigates to /parcels/<uid>/ (PR #68's
		// `appendParcelTitleLink`). Convention mirrors the filter
		// chips.
		this.bindParcelFlyToOnIcon(card, item.parcel);
		this.appendParcelTitleLink(body, item.parcel);
		appendTextDiv(body, this.parcelSummaryLine(item.parcel), 'workspace-card__meta');
		this.appendAttachedScenarios(body, item.attachedDeals, item.scenariosExpanded, () => {
			item.scenariosExpanded = !item.scenariosExpanded;
			this.renderPinnedWorkspacePanel();
		});
		const actions = this.workspaceCardActions(card);
		// D3: drag-out-to-unpin grip (collapsed parcel card).
		actions.appendChild(this.makeDragGrip('Drag to reorder · out to unpin',
			ev => this.beginPinnedCardDrag(ev, card, index)));
		actions.appendChild(this.workspaceSmallButton('Expand', () => {
			item.expanded = true;
			this.renderPinnedWorkspacePanel();
		}));
		// CTA label reflects in-project scenarios only — cross-project
		// scenarios on the same parcel don't make this project's deal
		// flow a "new scenario" continuation. If the user has zero deals
		// scoped to THIS project for THIS parcel, the affordance reads
		// as the first-time "Run Deal Calc"; once they create one here,
		// it flips to "New scenario".
		const inProjectCount = item.attachedDeals.filter(
			d => this.isAttachedDealInThisProject(d),
		).length;
		const dealLabel = inProjectCount > 0 ? 'New scenario' : 'Run Deal Calc';
		actions.appendChild(this.workspaceSmallButton(dealLabel, () => {
			this.openDealCalcForParcel(item.parcel);
		}));
		actions.appendChild(this.workspaceSmallButton('Unpin', () => {
			this.unpinWorkspaceItem(index).catch(exc => logger.error('renderPinnedParcelCard: Failed to unpin parcel', exc));
		}));
		return card;
	}

	// True when this attached deal is scoped to the project being viewed.
	// Same-parcel deals scoped to OTHER projects (or standalone) return
	// false — they're surfaced as passive cross-links, not interactive
	// in-project scenarios.
	private isAttachedDealInThisProject(deal: IAttachedDealRef): boolean {
		return !!deal.projectSlug && deal.projectSlug === this.slug;
	}

	// Renders the inline "N scenarios" summary + a click-to-expand list
	// of attached deals on a parcel pin card. Phase 5B Q7a: count badge +
	// expandable list of scenarios with open links. Same-parcel deals
	// scoped to OTHER projects render as a passive "Also in …" footnote
	// at the bottom — cross-aware hint rather than broken in-project
	// links (the Q5A scoping decision: per-project scoping is the rule;
	// cross-project visibility is a hint, never an actionable in-project
	// row).
	private appendAttachedScenarios(
		body: HTMLElement,
		attachedDeals: ReadonlyArray<IAttachedDealRef>,
		expanded: boolean,
		onToggle: () => void,
	): void {
		if (attachedDeals.length === 0) {
			return;
		}
		const here: IAttachedDealRef[] = [];
		const elsewhere: IAttachedDealRef[] = [];
		for (const deal of attachedDeals) {
			if (this.isAttachedDealInThisProject(deal)) {
				here.push(deal);
			} else {
				elsewhere.push(deal);
			}
		}
		const summary = document.createElement('button');
		summary.type = 'button';
		summary.className = 'workspace-card__scenarios-toggle';
		// Badge framing: "scenarios" always counts in-project rows so the
		// number matches the interactive CTA semantics ("New scenario"
		// once one exists in this project). Cross-project / standalone
		// counts are surfaced separately so the user knows about them
		// without inflating the "this project" badge.
		const hereCount = here.length;
		const word = hereCount === 1 ? 'scenario' : 'scenarios';
		const elsewhereTail = elsewhere.length > 0
			? ` · ${elsewhere.length} elsewhere`
			: '';
		summary.textContent = expanded
			? `Hide ${hereCount} ${word}${elsewhereTail}`
			: `${hereCount} ${word}${elsewhereTail}`;
		summary.addEventListener('click', evt => {
			evt.stopPropagation();
			onToggle();
		});
		body.appendChild(summary);
		if (!expanded) {
			return;
		}
		if (hereCount > 0) {
			const list = document.createElement('ul');
			list.className = 'workspace-card__scenarios';
			// Phase 5B comparison foundation: each per-scenario checkbox
			// participates in the project-level multi-select pool (capped
			// at COHORT_MAX). Disabled when the cap is reached unless
			// already selected. Only in-project deals are selectable here —
			// cross-project compare runs from the flat /deals/ surface.
			// Deal-forward PR B: the row itself is the shared DealBlock
			// component (`variant: 'row'`); these refs carry no
			// `state_json`, so no mode token renders and slug is hidden to
			// keep the in-card row compact.
			here.forEach(deal => {
				const checked = this.dealCompareSelection.has(deal.slug);
				list.appendChild(renderDealBlock(
					{slug: deal.slug, title: deal.title, projectSlug: deal.projectSlug},
					{
						variant: 'row',
						href: `/${encodeURIComponent(this.slug)}/deals/${encodeURIComponent(deal.slug)}/`,
						showSlug: false,
						compare: {
							checked,
							disabled: !checked && this.dealCompareSelection.size >= ProjectDetailView.COHORT_MAX,
							onChange: () => {
								this.toggleDealCompareSelection(deal.slug);
								this.renderPinnedWorkspacePanel();
							},
						},
					},
				));
			});
			body.appendChild(list);
		}
		if (elsewhere.length > 0) {
			const heading = document.createElement('div');
			heading.className = 'workspace-card__scenarios-elsewhere-heading';
			heading.textContent = 'Also in other projects';
			body.appendChild(heading);
			const elsewhereList = document.createElement('ul');
			elsewhereList.className = 'workspace-card__scenarios workspace-card__scenarios--elsewhere';
			elsewhere.forEach(deal => {
				const li = document.createElement('li');
				li.className = 'workspace-card__scenario workspace-card__scenario--elsewhere';
				const a = document.createElement('a');
				a.className = 'workspace-card__scenario-link workspace-card__scenario-link--elsewhere';
				// Link to the deal's REAL home, not this project's URL.
				// Standalone deals (no projectSlug) go to the flat
				// /deals/<slug>/ surface. This is what closes the
				// /LERLA/deals/AQ6HF/ 404 leak: a deal scoped to 45FKG
				// or standalone never gets a /LERLA/deals/ link.
				a.href = deal.projectSlug
					? `/${encodeURIComponent(deal.projectSlug)}/deals/${encodeURIComponent(deal.slug)}/`
					: `/deals/${encodeURIComponent(deal.slug)}/`;
				a.textContent = deal.title || deal.slug;
				li.appendChild(a);
				const tag = document.createElement('span');
				tag.className = 'workspace-card__scenario-elsewhere-tag';
				tag.textContent = deal.projectSlug
					? `↗ in ${deal.projectSlug}`
					: '↗ standalone';
				li.appendChild(tag);
				// Phase 4: standalone deals can be promoted into this
				// project — one-way move. Surfaces as a small "Move
				// here" affordance next to the standalone tag. Already-
				// scoped cross-project deals don't get this control
				// (cross-project moves are rejected server-side).
				if (deal.projectSlug === null || deal.projectSlug === undefined) {
					const promote = document.createElement('button');
					promote.type = 'button';
					promote.className = 'workspace-card__scenario-promote';
					promote.textContent = 'Move here';
					promote.title = `Attach this standalone scenario to ${this.slug}`;
					promote.addEventListener('click', evt => {
						evt.preventDefault();
						evt.stopPropagation();
						this.promoteStandaloneDealIntoProject(deal.slug, promote)
							.catch(exc => logger.error(
								'promoteStandaloneDealIntoProject: failed',
								exc,
							));
					});
					li.appendChild(promote);
				}
				elsewhereList.appendChild(li);
			});
			body.appendChild(elsewhereList);
		}
	}

	// Opens the project-scoped Deal Calc new-deal flow with the parcel
	// pre-populated. Phase 5B Q1 + Q2: lands at
	// `/<project_slug>/deals/new/?parcel=<uid>`, which causes the editor
	// view to fetch the parcel, seed `state.property`, and POST a new
	// Deal with `projectSlug` set so the API attaches a
	// `ProjectBlock(type=DEAL)` to this project.
	private openDealCalcForParcel(parcel: IParcel): void {
		if (!parcel || !parcel.uid) {
			return;
		}
		const url = `/${encodeURIComponent(this.slug)}/deals/new/?parcel=${encodeURIComponent(parcel.uid)}`;
		window.location.assign(url);
	}

	private renderPinnedOwnerCard(item: IPinnedOwnerWorkspaceItem, index: number): HTMLElement {
		const card = this.workspaceCard('person_search');
		card.classList.add('workspace-card--collapsed');
		const body = card.querySelector<HTMLElement>('.workspace-card__body');
		if (!body) {
			return card;
		}
		appendTextDiv(body, this.ownerPinnedTitle(item.row), 'workspace-card__title');
		appendTextDiv(body, this.ownerSearchAddressLine(item.row), 'workspace-card__sub');
		appendTextDiv(body, this.ownerSearchMetaLine(item.row), 'workspace-card__meta');
		if (item.expanded) {
			const details = document.createElement('div');
			details.className = 'workspace-detail-grid';
			const samples = Array.isArray(item.row.samples) ? item.row.samples : [];
			this.appendWorkspaceField(details, 'Owner UID', safeString(item.row.ownerUid) || 'Unavailable');
			this.appendWorkspaceField(details, 'Address UID', safeString(item.row.addressUid) || 'Unavailable');
			this.appendWorkspaceField(
				details,
				'Sample parcels',
				samples.map(sample => safeString(sample.pin || sample.parcelId || sample.parcelUid)).filter(Boolean).join(', ') || 'Unavailable');
			body.appendChild(details);
		}
		const actions = this.workspaceCardActions(card);
		// D3: drag-out-to-unpin grip (owner card).
		actions.appendChild(this.makeDragGrip('Drag to reorder · out to unpin',
			ev => this.beginPinnedCardDrag(ev, card, index)));
		actions.appendChild(this.workspaceSmallButton(item.expanded ? 'Collapse' : 'Expand', () => {
			item.expanded = !item.expanded;
			this.renderPinnedWorkspacePanel();
		}));
		actions.appendChild(this.workspaceSmallButton('Unpin', () => {
			this.unpinWorkspaceItem(index).catch(exc => logger.error('renderPinnedOwnerCard: Failed to unpin owner', exc));
		}));
		return card;
	}

	private renderParcelDetailCard(parcel: IParcel, opt: {
		actions: 'parcel-tab' | 'pinned';
		index: number;
		attachedDeals?: ReadonlyArray<IAttachedDealRef>;
		scenariosExpanded?: boolean;
		onToggleScenarios?: () => void;
	}): HTMLElement {
		const card = this.workspaceCard(parcel.doNotMail ? 'block' : 'select_all');
		card.classList.add('workspace-card--parcel');
		const body = card.querySelector<HTMLElement>('.workspace-card__body');
		if (!body) {
			return card;
		}
		// Phase 3c: icon click flies to + briefly outlines the parcel
		// on the project map. Applies to both card use sites — the
		// Parcels-tab detail card (which is also where a map-click
		// lands) and the inline expanded pinned-card variant.
		this.bindParcelFlyToOnIcon(card, parcel);
		const head = document.createElement('div');
		head.className = 'workspace-card__title-row';
		this.appendParcelTitleLink(head, parcel);
		const badge = document.createElement('span');
		badge.className = parcel.doNotMail ? 'status status--warn' : 'status';
		badge.textContent = parcel.doNotMail ? 'Ignored' : 'Active';
		head.appendChild(badge);
		body.appendChild(head);
		appendTextDiv(body, this.parcelSummaryLine(parcel), 'workspace-card__sub');
		appendTextDiv(body, parcel.zoning || 'Zoning unavailable', 'workspace-card__meta');

		const details = document.createElement('div');
		details.className = 'workspace-detail-grid';
		this.appendWorkspaceField(details, 'Address', parcel.streetAddress || 'Unavailable');
		this.appendWorkspaceField(details, 'Zoning', parcel.zoning || 'Unavailable');
		this.appendWorkspaceField(details, 'Total value', this.money(parcel.totalValue));
		this.appendWorkspaceField(details, 'Land value', this.money(parcel.landValue));
		this.appendWorkspaceField(details, 'Structures value', this.money(parcel.structuresValue));
		this.appendWorkspaceField(details, 'Other value', this.money(parcel.otherValue));
		body.appendChild(details);

		const uid = document.createElement('div');
		uid.className = 'workspace-record-id';
		uid.textContent = `Listblox global ID: ${parcel.uid}`;
		body.appendChild(uid);

		// Phase 5B: surface attached scenarios inline on the expanded
		// pinned card too, so users always see the scenario list whether
		// they're looking at the collapsed or detailed view.
		if (opt.actions === 'pinned' && opt.attachedDeals) {
			this.appendAttachedScenarios(
				body,
				opt.attachedDeals,
				!!opt.scenariosExpanded,
				opt.onToggleScenarios || (() => undefined),
			);
		}

		const actions = this.workspaceCardActions(card);
		if (opt.actions === 'parcel-tab') {
			// D2: a drag grip at the top of the parcel block lets the user
			// drag it onto the Pinned tab to pin it — the drag equivalent
			// of the "Pin parcel" button below (which stays for parity /
			// accessibility). A dedicated grip (rather than the whole card)
			// keeps the icon fly-to + title link + buttons clickable, since
			// beginDrag preventDefaults the initiating pointerdown.
			const grip = this.makeDragGrip('Drag to pin',
				ev => this.beginParcelDragToPin(ev, card, parcel));
			head.insertBefore(grip, head.firstChild);

			actions.appendChild(this.workspaceSmallButton('Pin parcel', () => {
				this.pinParcelToWorkspace(parcel).catch(exc => logger.error('renderParcelDetailCard: Failed to pin parcel', exc));
			}));
		} else {
			// D3: pinned cards get a drag-out-to-unpin grip in their
			// actions row (beside the "Unpin" button, which stays). Drag
			// the card out of the side panel and release to unpin.
			actions.insertBefore(
				this.makeDragGrip('Drag to reorder · out to unpin',
					ev => this.beginPinnedCardDrag(ev, card, opt.index)),
				actions.firstChild);
			actions.appendChild(this.workspaceSmallButton('Collapse', () => {
				const item = this.pinnedWorkspaceItems[opt.index];
				if (item && item.kind === 'parcel') {
					item.expanded = false;
				}
				this.renderPinnedWorkspacePanel();
			}));
			// Match the collapsed-card CTA semantics: in-project scenarios
			// flip the affordance to "New scenario"; cross-project /
			// standalone deals on the same parcel don't.
			const expandedInProjectCount = (opt.attachedDeals || []).filter(
				d => this.isAttachedDealInThisProject(d),
			).length;
			const dealLabel = expandedInProjectCount > 0
				? 'New scenario'
				: 'Run Deal Calc';
			actions.appendChild(this.workspaceSmallButton(dealLabel, () => {
				this.openDealCalcForParcel(parcel);
			}));
			actions.appendChild(this.workspaceSmallButton('Unpin', () => {
				this.unpinWorkspaceItem(opt.index).catch(exc => logger.error('renderParcelDetailCard: Failed to unpin parcel', exc));
			}));
		}
		actions.appendChild(this.workspaceSmallButton(
			parcel.doNotMail ? 'Un-ignore' : 'Ignore',
			() => this.toggleParcelDoNotMail(parcel, opt.index)));
		return card;
	}

	private workspaceCard(icon: string): HTMLElement {
		const card = document.createElement('article');
		card.className = 'workspace-card';
		card.appendChild(materialIcon(icon, 'workspace-card__icon'));
		const body = document.createElement('div');
		body.className = 'workspace-card__body';
		card.appendChild(body);
		return card;
	}

	private workspaceCardActions(card: HTMLElement): HTMLElement {
		let actions = card.querySelector<HTMLElement>('.workspace-card__actions');
		if (!actions) {
			actions = document.createElement('div');
			actions.className = 'workspace-card__actions';
			card.appendChild(actions);
		}
		return actions;
	}

	private workspaceSmallButton(label: string, callback: () => void): HTMLButtonElement {
		const button = document.createElement('button');
		button.type = 'button';
		button.className = 'workspace-card__button';
		button.textContent = label;
		button.addEventListener('click', callback);
		return button;
	}

	private appendWorkspaceField(parent: HTMLElement, label: string, value: string): void {
		const row = document.createElement('div');
		row.className = 'workspace-field';
		appendTextSpan(row, label, 'workspace-field__label');
		appendTextSpan(row, value, 'workspace-field__value');
		parent.appendChild(row);
	}

	private parcelDisplayId(parcel: IParcel): string {
		return safeString(parcel.pin || parcel.mapIdKey || parcel.uid);
	}

	private parcelOwnerLine(parcel: IParcel): string {
		return parcel.owners
			.map(owner => safeString(owner.name))
			.filter(Boolean)
			.join(', ');
	}

	private parcelPrimaryTitle(parcel: IParcel): string {
		return safeString(parcel.streetAddress)
			|| this.parcelOwnerLine(parcel)
			|| this.parcelDisplayId(parcel);
	}

	// Phase 3a: the parcel title on workspace cards links to the
	// standalone parcel page at `/parcels/<uid>/`. The page hosts the
	// cross-project scenario rollup + the parcel-only Deal Calc entry
	// point. Icon click (3c) will fly-to-parcel; title click is the
	// navigate-to-detail motion, matching the filter-chip convention.
	private appendParcelTitleLink(parent: HTMLElement, parcel: IParcel): HTMLAnchorElement {
		const a = document.createElement('a');
		a.className = 'workspace-card__title workspace-card__title--link';
		a.textContent = this.parcelPrimaryTitle(parcel);
		const uid = parcel && parcel.uid ? parcel.uid : '';
		// Defensive: a parcel payload without a uid would generate a
		// broken `/parcels//` URL. Skip the href in that case so the
		// title still renders, just inert. Should never happen in
		// practice — the API guarantees uid.
		if (uid) {
			a.href = `/parcels/${encodeURIComponent(uid)}/`;
		}
		parent.appendChild(a);
		return a;
	}

	private parcelSummaryLine(parcel: IParcel): string {
		const title = this.parcelPrimaryTitle(parcel);
		const ownerLine = this.parcelOwnerLine(parcel);
		const parts = [
			title === ownerLine ? '' : ownerLine,
			this.money(parcel.totalValue),
		].filter(Boolean);
		if (parts.length < 1 && title !== this.parcelDisplayId(parcel)) {
			parts.push(this.parcelDisplayId(parcel));
		}
		return parts.join(' | ');
	}

	private ownerPinnedTitle(row: IOwnerSearchRow): string {
		return safeString(row.owner) || 'Owner workspace item';
	}

	private money(value: unknown): string {
		const numeric = Number(value || 0);
		return Number.isFinite(numeric) ?
			`$${numberFormat(numeric)}` :
			'$0';
	}

	private syncSideWorkspacePanelPosition(): void {
		const panel = this.sideWorkspacePanelEl || document.getElementById('lb-project-workspace-panel');
		if (!(panel instanceof HTMLElement)) {
			return;
		}
		const chipStrip = document.getElementById('lb-project-filter-chip-strip');
		const fallbackTop = window.matchMedia('(max-width: 720px)').matches ? 268 : 92;
		let top = fallbackTop;
		if (chipStrip instanceof HTMLElement) {
			const rect = chipStrip.getBoundingClientRect();
			if (rect.bottom > 0) {
				top = Math.max(fallbackTop, Math.ceil(rect.bottom + 12));
			}
		}
		const maxTop = Math.max(72, window.innerHeight - 220);
		panel.style.setProperty('--lb-side-workspace-panel-top', `${Math.min(top, maxTop)}px`);
	}

	private initUnifiedWorkspacePanel(): void {
		if (this.workspacePanelEl) {
			return;
		}
		const host = document.getElementById('id_lb-project-detail-body');
		if (!host) {
			return;
		}
		const panel = document.createElement('section');
		panel.id = 'lb-project-unified-panel';
		panel.setAttribute('aria-hidden', 'true');
		host.appendChild(panel);
		this.workspacePanelEl = panel;
		this.renderUnifiedWorkspacePanel();
	}

	private openUnifiedWorkspacePanel(
		opt: Partial<{focusInput: boolean; query: string;}> = {},
	): void {
		const previousResultsScrollTop = this.workspacePanelEl ?
			this.workspacePanelEl.querySelector<HTMLElement>('.lb-project-unified-panel__body.results')?.scrollTop || 0 :
			0;
		if (this.unifiedPanelMode === 'edit') {
			this.filterMgr.closeFilterBox();
		}
		this.unifiedPanelMode = 'search';
		this.unifiedEditingFilter = null;
		this.unifiedEditorRenameActive = false;
		this.unifiedEditorRenameDraft = '';
		this.filterList.setSelectedFilterId(null);
		this.workspacePanelOpen = true;
		if (opt.query !== undefined) {
			this.unifiedPanelQuery = opt.query;
		}
		this.unifiedSearchError = '';
		this.renderUnifiedWorkspacePanel({preserveResultsScroll: true, resultsScrollTop: previousResultsScrollTop});
		this.scheduleUnifiedSearch();
		if (this.workspacePanelEl) {
			this.workspacePanelEl.classList.add('is-open');
			this.workspacePanelEl.setAttribute('aria-hidden', 'false');
		}
		this.syncUnifiedLauncherState();
		if (opt.focusInput !== false) {
			window.setTimeout(() => this.focusUnifiedPanelInput(), 0);
		}
	}

	private renderUnifiedWorkspacePanel(
		opt: Partial<{preserveResultsScroll: boolean; resultsScrollTop: number;}> = {},
	): void {
		if (!this.workspacePanelEl) {
			return;
		}
		const panel = this.workspacePanelEl;
		const previousResultsScrollTop = opt.resultsScrollTop !== undefined ?
			opt.resultsScrollTop :
			panel.querySelector<HTMLElement>('.lb-project-unified-panel__body.results')?.scrollTop || 0;
		panel.innerHTML = '';
		panel.classList.toggle('lb-project-unified-panel--edit', this.unifiedPanelMode === 'edit');

		if (this.unifiedPanelMode === 'edit' && (this.unifiedEditingFilter || this.projectFilterEditorActive)) {
			// Lane E PR 1e-ii — the editor is now PROJECT-scoped: one editor owns the
			// whole Include/Exclude set, loaded from / saved to the filter-tree IR. It
			// opens either from a chip (unifiedEditingFilter) or from "+ Add filter"
			// on an empty project (projectFilterEditorActive, no seed chip).
			this.renderProjectFilterEditorPanel(panel);
			return;
		}

		const input = document.createElement('div');
		input.className = 'lb-project-unified-panel__input unified-panel__input';
		const searchIcon = document.createElement('span');
		searchIcon.className = 'material-icons';
		searchIcon.setAttribute('aria-hidden', 'true');
		searchIcon.textContent = 'search';
		const textInput = document.createElement('input');
		textInput.className = 'up-input-real';
		textInput.type = 'search';
		textInput.autocomplete = 'off';
		textInput.placeholder = this.workspacePanelTitle();
		textInput.value = this.unifiedPanelQuery;
		textInput.addEventListener('input', () => this.setUnifiedQuery(textInput.value));
		textInput.addEventListener('keydown', event => this.unifiedPanelInputKeyDownEvent(event));
		this.unifiedPanelInputEl = textInput;
		input.appendChild(searchIcon);
		input.appendChild(textInput);
		input.appendChild(this.unifiedGripButton());

		const esc = document.createElement('span');
		esc.className = 'kbd';
		esc.textContent = 'Esc';
		input.appendChild(esc);
		const close = document.createElement('button');
		close.type = 'button';
		close.className = 'lb-dash-icon-btn icon-btn lb-project-panel-close-btn';
		close.title = 'Close search panel';
		close.setAttribute('aria-label', 'Close search panel');
		close.innerHTML = '<span class="material-icons" aria-hidden="true">close</span>';
		close.addEventListener('click', () => this.closeUnifiedWorkspacePanel());
		input.appendChild(close);
		panel.appendChild(input);

		const body = document.createElement('div');
		body.className = 'lb-project-unified-panel__body results';
		this.renderUnifiedPanelBody(body);
		panel.appendChild(body);
		panel.appendChild(this.unifiedPanelFooter(this.unifiedFooterMessage(), this.unifiedFooterActions()));
		if (opt.preserveResultsScroll) {
			body.scrollTop = previousResultsScrollTop;
		}
	}

	openUnifiedFilterEditor(filter: IFilter): void {
		this.unifiedEditingFilter = filter;
		this.unifiedEditorDraft = this.filterToUnifiedEditorDraft(filter);
		this.unifiedEditorRenameDraft = this.filterDisplayLabel(filter);
		this.filterList.setSelectedFilterId(filter.id);
		this.enterProjectFilterEditor();
	}

	// Lane E PR 1e-ii — open the project-scoped two-zone editor with NO seed chip
	// (the "+ Add filter" entry). Works on an empty project: the Include zone shows
	// a guided "+ Add condition" row.
	openProjectFilterEditor(): void {
		this.unifiedEditingFilter = null;
		this.unifiedEditorDraft = null;
		this.unifiedEditorRenameDraft = '';
		this.projectFilterPendingFocusLeafId = null;
		this.projectFilterFocusGroupIndex = null;
		this.projectFilterFocusScrolled = false;
		this.filterList.setSelectedFilterId(null);
		this.enterProjectFilterEditor();
	}

	// Lane E Phase 2 (Stage 2) — open the editor focused on the group that scopes a
	// given boundary leaf (the map per-polygon popup's "Edit filter group" action),
	// so the user lands on the right group to add criteria to. The leaf id is
	// resolved to a group once the draft loads (loadProjectFilterDraft).
	openProjectFilterEditorForLeaf(leafId: number): void {
		this.unifiedEditingFilter = null;
		this.unifiedEditorDraft = null;
		this.unifiedEditorRenameDraft = '';
		this.projectFilterPendingFocusLeafId = leafId;
		this.projectFilterFocusGroupIndex = null;
		this.projectFilterFocusScrolled = false;
		this.filterList.setSelectedFilterId(null);
		this.enterProjectFilterEditor();
	}

	// Which group (index into draft.groups) carries the opaque leaf with this
	// persisted filter id (a drawn polygon / county / selection). null if none.
	private projectGroupIndexContainingLeaf(leafId: number): number | null {
		const draft = this.projectFilterDraft;
		if (!draft) {
			return null;
		}
		for (let i = 0; i < draft.groups.length; ++i) {
			const g = draft.groups[i];
			const leaves = [...g.places, ...g.selections];
			if (leaves.some(leaf => leaf.snapshot && leaf.snapshot.id === leafId)) {
				return i;
			}
		}
		return null;
	}

	private enterProjectFilterEditor(): void {
		this.unifiedPanelMode = 'edit';
		this.projectFilterEditorActive = true;
		this.unifiedEditorRenameActive = false;
		this.workspacePanelOpen = true;
		this.clearUnifiedSearchTimer();
		this.unifiedSearchLoading = false;
		this.unifiedSearchError = '';
		this.unifiedSelectedOwnerKeys.clear();
		this.unifiedSelectedPlaceKeys.clear();
		this.unifiedSelectedAddressKeys.clear();
		// The editor renders the PROJECT's whole Include/Exclude set. Load the tree as
		// IR; the panel shows a loading state until it resolves.
		this.loadProjectFilterDraft();
		this.renderUnifiedWorkspacePanel();
		if (this.workspacePanelEl) {
			this.workspacePanelEl.classList.add('is-open');
			this.workspacePanelEl.setAttribute('aria-hidden', 'false');
		}
		this.syncUnifiedLauncherState();
	}

	// Lane E PR 1e-ii — fetch the project's filter tree as IR and convert it into
	// the two-zone draft. Re-renders the panel when it resolves.
	private async loadProjectFilterDraft(): Promise<void> {
		this.projectFilterDraft = null;
		this.projectFilterDraftLoading = true;
		this.projectFilterDraftError = '';
		this.projectFilterPreviewOk = false;
		this.projectFilterDirty = false;
		try {
			const ir = await svc.project.filterTree(this.slug);
			this.projectFilterDraft = this.projectIRToDraft(ir);
		} catch (err) {
			logger.warning('loadProjectFilterDraft: failed to load filter tree', err);
			this.projectFilterDraftError = 'Could not load this project’s filters. Try reopening the editor.';
			this.projectFilterDraft = this.emptyProjectFilterDraft();
		}
		this.seedEmptyIncludeIfNeeded();
		// Resolve a pending boundary-focus (opened from the map popup) to a group now
		// that the draft exists, so the editor can highlight + scroll to it on render.
		if (this.projectFilterPendingFocusLeafId !== null) {
			this.projectFilterFocusGroupIndex = this.projectGroupIndexContainingLeaf(this.projectFilterPendingFocusLeafId);
			this.projectFilterPendingFocusLeafId = null;
			this.projectFilterFocusScrolled = false;
		}
		this.projectFilterDraftLoading = false;
		if (this.unifiedPanelMode === 'edit') {
			this.renderUnifiedWorkspacePanel();
		}
	}

	// Depth-1 first run: a project with no editable conditions or leaves gets one
	// guided, ready-to-edit Include row so "+ Add filter" lands on something to fill.
	private seedEmptyIncludeIfNeeded(): void {
		const draft = this.projectFilterDraft;
		if (!draft) {
			return;
		}
		const inc = this.projectIncludeGroups(draft)[0];
		if (inc && !inc.conditions.length && !inc.selections.length && !inc.places.length && draft.groups.length === 1) {
			const cond = this.defaultUnifiedEditorCondition();
			cond.editing = true;
			inc.conditions.push(cond);
		}
	}

	// ===================================================================
	// Lane E PR 1e-ii — project-scoped two-zone Include/Exclude editor
	// ===================================================================

	private emptyZoneDraft(match: FilterIRMatch): IFilterEditorZoneDraft {
		return {match, conditions: [], selections: [], places: []};
	}

	private emptyGroupDraft(direction: FilterIRDirection, match: FilterIRMatch): IProjectFilterGroupDraft {
		return {direction, ...this.emptyZoneDraft(match)};
	}

	private emptyProjectFilterDraft(): IProjectFilterEditorDraft {
		return {groups: [this.emptyGroupDraft('include', 'all')], match: 'any'};
	}

	private projectIncludeGroups(draft: IProjectFilterEditorDraft): Array<IProjectFilterGroupDraft> {
		return draft.groups.filter(g => g.direction === 'include');
	}

	private projectExcludeGroup(draft: IProjectFilterEditorDraft): IProjectFilterGroupDraft | null {
		return draft.groups.find(g => g.direction === 'exclude') || null;
	}

	// ---- IR -> draft (load) -------------------------------------------

	private projectIRToDraft(ir: IFilterTreeIR): IProjectFilterEditorDraft {
		const {includeGroups, excludeGroup, match} = splitIRToGroups(ir);
		const groups: Array<IProjectFilterGroupDraft> = [];
		for (const g of includeGroups) {
			groups.push({direction: 'include', ...this.irGroupToZone(g, 'all')});
		}
		if (excludeGroup) {
			groups.push({direction: 'exclude', ...this.irGroupToZone(excludeGroup, 'any')});
		}
		if (!groups.length) {
			return this.emptyProjectFilterDraft();
		}
		return {groups, match};
	}

	private irGroupToZone(group: IFilterIRGroup | null | undefined, fallbackMatch: FilterIRMatch): IFilterEditorZoneDraft {
		const zone = this.emptyZoneDraft(fallbackMatch);
		if (!group) {
			return zone;
		}
		// Recover the zone's all/any from the group structure. NOT simply
		// group.match — the leaf-scope "any" case nests the user's match in a
		// sub-group under an "all" wrapper (see zoneMatchForGroup).
		zone.match = zoneMatchForGroup(group);
		this.flattenIntoZone(group, zone);
		return zone;
	}

	// Pull a group's conditions + opaque leaves into the flat zone, recursing through
	// nested groups (the leaf-scope structure zoneToIRGroup writes, plus the OR
	// sub-group an "is one of" lowers to — collapsed back to a single row). Phase 1
	// caps the editor at one any/all per zone, so nested logic flattens best-effort.
	private flattenIntoZone(group: IFilterIRGroup, zone: IFilterEditorZoneDraft): void {
		for (const c of group.conditions || []) {
			zone.conditions.push(this.irConditionToDraft(c));
		}
		for (const sel of group.selections || []) {
			zone.selections.push(this.irLeafSnapshot(sel, 'selection'));
		}
		for (const place of group.places || []) {
			zone.places.push(this.irLeafSnapshot(place, 'place'));
		}
		for (const sub of group.groups || []) {
			const collapsed = this.collapseInGroup(sub);
			if (collapsed) {
				zone.conditions.push(collapsed);
			} else {
				this.flattenIntoZone(sub, zone);
			}
		}
	}

	private collapseInGroup(sub: IFilterIRGroup): IFilterEditorConditionDraft | null {
		const conds = sub.conditions || [];
		if (sub.match !== 'any' || conds.length < 1) {
			return null;
		}
		const field = conds[0].field;
		if (!conds.every(c => c.field === field && c.op === 'exact')) {
			return null;
		}
		const lhs = IR_FIELD_TO_TOKEN[field];
		if (!lhs) {
			return null;
		}
		const values = conds.map(c => this.irValueToString(c.value));
		return {
			lhs,
			operator: 'in',
			rhs: values[0] || '',
			values,
			displayValue: this.projectFilterValueDisplay(lhs, 'in', values[0] || '', undefined, values),
			enabled: true,
		};
	}

	private irConditionToDraft(c: IFilterIRCondition): IFilterEditorConditionDraft {
		const lhs = IR_FIELD_TO_TOKEN[c.field] || c.field;
		const op = c.op || this.defaultOperatorForField(lhs);
		if (op === 'between' && Array.isArray(c.value)) {
			return {
				lhs, operator: 'between',
				rhs: this.irValueToString(c.value[0]),
				rhsHigh: this.irValueToString(c.value[1]),
				enabled: c.enabled !== false,
			};
		}
		if (op === 'in' && Array.isArray(c.value)) {
			const values = c.value.map((v: any) => this.irValueToString(v));
			return {lhs, operator: 'in', rhs: values[0] || '', values, enabled: c.enabled !== false};
		}
		const rhs = this.irValueToString(c.value);
		// The endpoint emits snake_case `display_value`; the TS type is camelCase.
		const display = (c as any).display_value || c.displayValue || rhs;
		return {lhs, operator: op, rhs, displayValue: display, enabled: c.enabled !== false};
	}

	private irLeafSnapshot(raw: any, kind: 'selection' | 'place'): IFilterEditorLeafSnapshot {
		const full = raw && raw.snapshot ? raw.snapshot : raw;
		const label = this.leafSnapshotLabel(full, kind);
		const enabled = !(full && full.enabled === false);
		const id = full && typeof full.id === 'number' ? full.id : null;
		// Keep only a lightweight ref on the wire when the leaf has a persisted id —
		// a county boundary's full geometry is many MB and overflows the request
		// body (nginx 413). The backend rehydrates the full snapshot from this id.
		// Fall back to the full snapshot only when there's no id to reference.
		// Carry `enabled` ON the ref so a draft-side Hide/Show of a NESTED leaf
		// survives Apply — otherwise rehydration restores the DB's enabled and the
		// toggle is silently lost (_resolve_leaf_refs honors this override).
		const snapshot = id !== null ? {id, ref: true, enabled} : full;
		return {kind, snapshot, label, enabled};
	}

	private irValueToString(value: any): string {
		return value === null || value === undefined ? '' : String(value);
	}

	// ---- draft -> IR (preview + save) ---------------------------------

	private projectDraftToIR(draft: IProjectFilterEditorDraft): IFilterTreeIR | null {
		const includeGroups: Array<IFilterIRGroup> = [];
		let excludeGroup: IFilterIRGroup | null = null;
		for (const g of draft.groups) {
			const ir = this.zoneToIRGroup(g, g.direction);
			if (!ir) {
				continue;
			}
			if (g.direction === 'exclude') {
				excludeGroup = ir;   // Stage 1: at most one exclude group
			} else {
				includeGroups.push(ir);
			}
		}
		return combineGroupsToIR(includeGroups, excludeGroup, draft.match);
	}

	private zoneToIRGroup(zone: IFilterEditorZoneDraft, direction: FilterIRDirection): IFilterIRGroup | null {
		const conditions: Array<IFilterIRCondition> = [];
		const inGroups: Array<IFilterIRGroup> = [];
		for (const c of zone.conditions) {
			const compiled = this.compileConditionToIR(c);
			if (!compiled) {
				continue;
			}
			if (compiled.op === 'in') {
				// `is one of` lowers to N exact rows; wrap in an OR sub-group so they OR
				// even when the zone's match is "all" (AND'd exact rows = empty set).
				inGroups.push({kind: 'group', match: 'any', conditions: [compiled], selections: [], places: [], groups: []});
			} else {
				conditions.push(compiled);
			}
		}
		const selections = zone.selections.map(leaf => ({kind: 'selection', snapshot: leaf.snapshot}));
		const places = zone.places.map(leaf => ({kind: 'place', snapshot: leaf.snapshot}));
		// Structure (the leaf-scope nesting + the match it encodes) lives in the pure,
		// unit-tested filter_ir_zone module so the round-trip can be verified DB-free.
		return assembleZoneIRGroup({conditions, inGroups, selections, places, match: zone.match, direction});
	}

	private compileConditionToIR(c: IFilterEditorConditionDraft): IFilterIRCondition | null {
		const field = TOKEN_TO_IR_FIELD[c.lhs];
		if (!field || c.enabled === false || !c.operator) {
			return null;
		}
		if (c.operator === 'between') {
			const lo = stripThousands((c.rhs || '').trim());
			const hi = stripThousands((c.rhsHigh || '').trim());
			if (!lo || !hi) {
				return null;
			}
			return {kind: 'condition', field, op: 'between', value: [lo, hi], enabled: true};
		}
		if (c.operator === 'in') {
			const values = (c.values || []).map(v => (v || '').trim()).filter(Boolean);
			if (!values.length) {
				return null;
			}
			return {kind: 'condition', field, op: 'in', value: values, enabled: true};
		}
		const numeric = NUMERIC_IR_TOKENS.has(c.lhs);
		const rhs = numeric ? stripThousands((c.rhs || '').trim()) : (c.rhs || '').trim();
		if (!rhs) {
			return null;
		}
		return {kind: 'condition', field, op: c.operator, value: rhs, enabled: true, displayValue: c.displayValue};
	}

	// ---- render: panel + zones ----------------------------------------

	private renderProjectFilterEditorPanel(panel: HTMLElement): void {
		const bar = document.createElement('div');
		bar.className = 'editor-bar editor-bar--header';
		const crumb = document.createElement('div');
		crumb.className = 'editor-bar__crumb';
		appendTextSpan(crumb, 'Filters', 'editor-bar__title');
		appendTextSpan(crumb, '->', 'editor-bar__sep');
		appendTextSpan(crumb, 'Include & exclude', 'editor-bar__title');
		bar.appendChild(crumb);
		const actions = document.createElement('div');
		actions.className = 'editor-bar__actions';
		const esc = document.createElement('span');
		esc.className = 'kbd';
		esc.textContent = 'Esc';
		esc.setAttribute('role', 'button');
		esc.addEventListener('click', () => this.closeUnifiedWorkspacePanel());
		actions.appendChild(this.unifiedGripButton());
		actions.appendChild(esc);
		actions.appendChild(this.unifiedCloseButton('Close filter editor'));
		bar.appendChild(actions);
		panel.appendChild(bar);

		const body = document.createElement('div');
		body.className = 'editor-body lb-project-unified-editor-body';

		if (this.projectFilterDraftLoading || !this.projectFilterDraft) {
			const loading = document.createElement('div');
			loading.className = 'flt-zone__loading';
			loading.textContent = this.projectFilterDraftError || 'Loading filters…';
			body.appendChild(loading);
			panel.appendChild(body);
			return;
		}

		if (this.projectFilterDraftError) {
			const err = document.createElement('div');
			err.className = 'flt-error';
			err.textContent = this.projectFilterDraftError;
			body.appendChild(err);
		}

		const draft = this.projectFilterDraft;
		// "Match ANY/ALL of these groups" — only meaningful with >1 include group.
		if (this.projectIncludeGroups(draft).length > 1) {
			body.appendChild(this.renderProjectGroupsMatchSelect(draft));
		}
		draft.groups.forEach((group, i) => body.appendChild(this.renderProjectFilterZone(group, i)));
		body.appendChild(this.renderProjectFilterAddGroupBar(draft));
		panel.appendChild(body);
		panel.appendChild(this.renderProjectFilterFooter(draft));
	}

	// Project-level "Match ANY / ALL of these groups" — drives the project
	// filter_match (how the include groups combine; union by default).
	private renderProjectGroupsMatchSelect(draft: IProjectFilterEditorDraft): HTMLElement {
		const wrap = document.createElement('div');
		wrap.className = 'flt-groups-match';
		appendTextSpan(wrap, 'Match', 'flt-groups-match__lead');
		const sel = document.createElement('select');
		sel.className = 'combo flt-groups-match__select';
		([['any', 'any of these groups'], ['all', 'all of these groups']] as Array<[FilterIRMatch, string]>).forEach(([value, label]) => {
			const o = document.createElement('option');
			o.value = value;
			o.textContent = label;
			o.selected = draft.match === value;
			sel.appendChild(o);
		});
		sel.addEventListener('change', () => {
			draft.match = sel.value === 'all' ? 'all' : 'any';
			this.invalidateProjectPreview();
			this.renderUnifiedWorkspacePanel();
		});
		wrap.appendChild(sel);
		return wrap;
	}

	// "+ Add group" (a new include group) + "+ Add an exclusion" (the single
	// exclude group, when none exists yet).
	private renderProjectFilterAddGroupBar(draft: IProjectFilterEditorDraft): HTMLElement {
		const wrap = document.createElement('div');
		wrap.className = 'flt-add-group-wrap';

		const addGroup = document.createElement('button');
		addGroup.type = 'button';
		addGroup.className = 'add-btn flt-add-group';
		addGroup.textContent = '+ Add group';
		addGroup.title = 'Add another include group with its own area and conditions';
		addGroup.addEventListener('click', () => {
			const group = this.emptyGroupDraft('include', 'all');
			const cond = this.defaultUnifiedEditorCondition();
			cond.editing = true;
			group.conditions.push(cond);
			// Insert after the last include group, before any exclude group.
			const exclude = this.projectExcludeGroup(draft);
			const at = exclude ? draft.groups.indexOf(exclude) : draft.groups.length;
			draft.groups.splice(at, 0, group);
			this.invalidateProjectPreview();
			this.renderUnifiedWorkspacePanel();
		});
		wrap.appendChild(addGroup);

		if (!this.projectExcludeGroup(draft)) {
			const addExcl = document.createElement('button');
			addExcl.type = 'button';
			addExcl.className = 'flt-add-exclusion';
			addExcl.textContent = '+ Add an exclusion';
			addExcl.title = 'Remove parcels that match a set of conditions';
			addExcl.addEventListener('click', () => {
				// Exclude defaults to "any" — drop a parcel if it hits any exclusion reason.
				const group = this.emptyGroupDraft('exclude', 'any');
				const cond = this.defaultUnifiedEditorCondition();
				cond.editing = true;
				if (cond.operator === 'ne') {
					cond.operator = this.projectFilterDefaultOperator('exclude', cond.lhs);
				}
				group.conditions.push(cond);
				draft.groups.push(group);   // exclude always last
				this.invalidateProjectPreview();
				this.renderUnifiedWorkspacePanel();
			});
			wrap.appendChild(addExcl);
		}
		return wrap;
	}

	private renderProjectFilterZone(group: IProjectFilterGroupDraft, index: number): HTMLElement {
		const kind = group.direction;
		const zone: IFilterEditorZoneDraft = group;
		const zoneEl = document.createElement('section');
		zoneEl.className = `flt-zone flt-zone--${kind}`;
		if (index === this.projectFilterFocusGroupIndex) {
			// Opened from a boundary on the map — highlight + scroll to this group once.
			zoneEl.classList.add('flt-zone--focused');
			if (!this.projectFilterFocusScrolled) {
				this.projectFilterFocusScrolled = true;
				window.setTimeout(() => zoneEl.scrollIntoView({block: 'nearest', behavior: 'smooth'}), 0);
			}
		}
		const rail = document.createElement('span');
		rail.className = 'flt-zone__rail';
		rail.setAttribute('aria-hidden', 'true');
		zoneEl.appendChild(rail);

		const inner = document.createElement('div');
		inner.className = 'flt-zone__inner';

		const head = document.createElement('div');
		head.className = 'flt-zone__head';
		const badge = document.createElement('span');
		badge.className = 'flt-zone__badge';
		badge.textContent = kind === 'include' ? 'Include' : 'Exclude';
		head.appendChild(badge);
		appendTextSpan(
			head,
			kind === 'include' ? 'parcels that match' : 'then remove parcels that match',
			'flt-zone__verb');
		if (zone.conditions.length >= 2) {
			head.appendChild(this.renderProjectFilterMatchSelect(zone));
		}
		if (kind === 'exclude') {
			// Eyeball — hide/show the excluded (red) parcels on the map. VISUAL
			// ONLY: distinct from the chip enable toggle, it never changes filter
			// membership / counts / totals. Session-only state lives on the map.
			const hidden = this.map.excludeGroupHidden();
			const eye = this.unifiedIconButton(
				hidden ? 'Show excluded parcels on the map' : 'Hide excluded parcels on the map',
				hidden ? 'visibility_off' : 'visibility');
			eye.classList.add('flt-zone__eye');
			eye.addEventListener('click', () => {
				const nextHidden = !this.map.excludeGroupHidden();
				this.map.setExcludeGroupHidden(nextHidden);
				eye.title = nextHidden ?
					'Show excluded parcels on the map' :
					'Hide excluded parcels on the map';
				eye.textContent = '';
				eye.appendChild(materialIcon(nextHidden ? 'visibility_off' : 'visibility'));
			});
			head.appendChild(eye);
		}
		// The group is removable when it's an exclude group, or an include group and
		// there's more than one include group (never strand zero include groups).
		const draft = this.projectFilterDraft;
		const removable = draft && (kind === 'exclude' || this.projectIncludeGroups(draft).length > 1);
		if (removable) {
			const removeZone = this.unifiedIconButton(
				kind === 'exclude' ? 'Remove the exclude group' : 'Remove this group', 'close');
			removeZone.classList.add('flt-zone__remove');
			removeZone.addEventListener('click', () => {
				if (this.projectFilterDraft) {
					this.projectFilterDraft.groups.splice(index, 1);
				}
				this.invalidateProjectPreview();
				this.renderUnifiedWorkspacePanel();
			});
			head.appendChild(removeZone);
		}
		inner.appendChild(head);

		const rows = document.createElement('div');
		rows.className = 'flt-zone__rows';
		zone.places.forEach((leaf, i) => rows.appendChild(this.renderProjectFilterLeaf(zone, 'places', leaf, i)));
		zone.selections.forEach((leaf, i) => rows.appendChild(this.renderProjectFilterLeaf(zone, 'selections', leaf, i)));
		zone.conditions.forEach((c, i) => rows.appendChild(this.renderProjectFilterRow(kind, zone, c, i)));
		inner.appendChild(rows);

		const add = document.createElement('button');
		add.type = 'button';
		add.className = 'add-btn flt-zone__add';
		add.textContent = kind === 'include' ? '+ Add condition' : '+ Add exclusion';
		add.addEventListener('click', () => {
			const cond = this.defaultUnifiedEditorCondition();
			cond.editing = true;
			if (kind === 'exclude' && cond.operator === 'ne') {
				cond.operator = this.projectFilterDefaultOperator('exclude', cond.lhs);
			}
			zone.conditions.push(cond);
			this.invalidateProjectPreview();
			this.renderUnifiedWorkspacePanel();
		});
		inner.appendChild(add);

		zoneEl.appendChild(inner);
		return zoneEl;
	}

	private renderProjectFilterMatchSelect(zone: IFilterEditorZoneDraft): HTMLElement {
		const sel = document.createElement('select');
		sel.className = 'combo flt-zone__match';
		([['all', 'all of these'], ['any', 'any of these']] as Array<[FilterIRMatch, string]>).forEach(([value, label]) => {
			const o = document.createElement('option');
			o.value = value;
			o.textContent = label;
			o.selected = zone.match === value;
			sel.appendChild(o);
		});
		sel.addEventListener('change', () => {
			zone.match = sel.value === 'any' ? 'any' : 'all';
			this.invalidateProjectPreview();
			this.renderUnifiedWorkspacePanel();
		});
		return sel;
	}

	private renderProjectFilterRow(
		kind: 'include' | 'exclude',
		zone: IFilterEditorZoneDraft,
		condition: IFilterEditorConditionDraft,
		index: number,
	): HTMLElement {
		this.ensureUnifiedEditorConditionDefaults(condition);
		const editing = condition.editing === true;
		const unsupported = this.projectFilterConditionUnsupported(condition);
		const row = document.createElement('div');
		row.className = `flt-row${editing ? ' flt-row--editing' : ''}${unsupported ? ' expr-row--unsupported' : ''}`;

		const handle = document.createElement('span');
		handle.className = 'flt-row__handle';
		handle.setAttribute('aria-hidden', 'true');
		handle.textContent = '⠿';
		row.appendChild(handle);

		// Per-row on/off toggle (the green checkmark): a paused condition is ignored
		// by the compile, so the live count reflects it — for quick experimentation.
		const enabled = condition.enabled !== false;
		const toggle = document.createElement('button');
		toggle.type = 'button';
		toggle.className = `flt-row__toggle expr-row__toggle${enabled ? ' is-on' : ''}`;
		toggle.title = enabled ? 'Condition is active — click to pause' : 'Condition is paused — click to activate';
		toggle.setAttribute('aria-pressed', enabled ? 'true' : 'false');
		toggle.appendChild(materialIcon(enabled ? 'check_circle' : 'pause_circle'));
		toggle.addEventListener('click', () => {
			condition.enabled = condition.enabled === false;
			this.invalidateProjectPreview();
			this.renderUnifiedWorkspacePanel();
		});
		row.appendChild(toggle);

		if (editing) {
			row.appendChild(this.renderProjectFilterRowEditor(kind, condition));
		} else {
			const sentence = document.createElement('button');
			sentence.type = 'button';
			sentence.className = 'flt-row__sentence';
			appendTextSpan(sentence, this.filterEditorFieldLabel(condition.lhs), 'flt-row__field');
			appendTextSpan(sentence, this.projectFilterOperatorLabel(condition.operator), 'flt-row__op');
			const value = this.projectFilterValueDisplay(
				condition.lhs, condition.operator, condition.rhs, condition.rhsHigh, condition.values);
			appendTextSpan(sentence, value || 'set a value', value ? 'flt-row__value' : 'flt-row__value flt-row__value--empty');
			if (condition.enabled === false) {
				appendTextSpan(sentence, 'paused', 'flt-row__paused');
			}
			if (SELECTION_BACKED_TOKENS.has(condition.lhs)) {
				appendTextSpan(sentence, '— owner/address filters must come from search; remove this row', 'flt-row__note');
			}
			sentence.addEventListener('click', () => {
				condition.editing = true;
				this.renderUnifiedWorkspacePanel();
			});
			row.appendChild(sentence);
		}

		const remove = this.unifiedIconButton('Remove this condition', 'close');
		remove.classList.add('flt-row__remove');
		remove.addEventListener('click', () => {
			zone.conditions.splice(index, 1);
			this.invalidateProjectPreview();
			this.renderUnifiedWorkspacePanel();
		});
		row.appendChild(remove);
		return row;
	}

	private renderProjectFilterRowEditor(
		kind: 'include' | 'exclude',
		condition: IFilterEditorConditionDraft,
	): HTMLElement {
		const wrap = document.createElement('div');
		wrap.className = 'flt-row__edit';

		const field = document.createElement('select');
		field.className = 'combo combo--field';
		this.appendGroupedFieldOptions(field, condition);
		field.addEventListener('change', () => {
			condition.lhs = field.value;
			condition.operator = this.projectFilterDefaultOperator(kind, field.value);
			condition.values = undefined;
			condition.rhsHigh = undefined;
			this.applyDefaultValueForField(condition);
			this.invalidateProjectPreview();
			this.renderUnifiedWorkspacePanel();
		});
		wrap.appendChild(field);

		const operator = document.createElement('select');
		operator.className = 'combo combo--op';
		const ops = this.projectFilterOperatorsForField(kind, condition.lhs);
		if (!ops.length && condition.operator) {
			const o = document.createElement('option');
			o.value = condition.operator;
			o.textContent = this.projectFilterOperatorLabel(condition.operator);
			o.selected = true;
			operator.appendChild(o);
		}
		for (const op of ops) {
			const o = document.createElement('option');
			o.value = op;
			o.textContent = this.projectFilterOperatorLabel(op);
			o.selected = op === condition.operator;
			operator.appendChild(o);
		}
		operator.addEventListener('change', () => {
			condition.operator = operator.value;
			if (operator.value !== 'between') {
				condition.rhsHigh = undefined;
			}
			if (operator.value !== 'in') {
				condition.values = undefined;
			}
			this.invalidateProjectPreview();
			this.renderUnifiedWorkspacePanel();
		});
		wrap.appendChild(operator);

		wrap.appendChild(this.renderProjectFilterOperand(condition));

		const done = document.createElement('button');
		done.type = 'button';
		done.className = 'icon-btn flt-row__done';
		done.title = 'Done editing this condition';
		done.setAttribute('aria-label', 'Collapse this condition');
		done.appendChild(materialIcon('check'));
		done.addEventListener('click', () => {
			condition.editing = false;
			this.renderUnifiedWorkspacePanel();
		});
		wrap.appendChild(done);
		return wrap;
	}

	private renderProjectFilterOperand(condition: IFilterEditorConditionDraft): HTMLElement {
		const op = condition.operator;
		if (op === 'between') {
			const group = document.createElement('div');
			group.className = 'flt-operand flt-operand--range';
			group.appendChild(this.numericOperandInput(condition, 'rhs', 'Min'));
			appendTextSpan(group, 'and', 'flt-operand__and');
			group.appendChild(this.numericOperandInput(condition, 'rhsHigh', 'Max'));
			return group;
		}
		if (op === 'in') {
			return this.renderProjectFilterChips(condition);
		}
		const choices = this.filterEditorChoicesForField(condition.lhs);
		if (choices.length > 0) {
			const select = document.createElement('select');
			select.className = 'expr-input expr-input--select flt-operand';
			if (!condition.rhs) {
				const ph = document.createElement('option');
				ph.value = '';
				ph.textContent = 'Choose value';
				ph.disabled = true;
				ph.selected = true;
				select.appendChild(ph);
			}
			for (const ch of choices) {
				const o = document.createElement('option');
				o.value = ch.value;
				o.textContent = ch.label;
				o.selected = ch.value === condition.rhs;
				select.appendChild(o);
			}
			select.addEventListener('change', () => {
				condition.rhs = select.value;
				condition.displayValue = select.options[select.selectedIndex]?.textContent || select.value;
				this.invalidateProjectPreview();
			});
			return select;
		}
		return this.numericOperandInput(condition, 'rhs', 'Value');
	}

	private numericOperandInput(
		condition: IFilterEditorConditionDraft,
		key: 'rhs' | 'rhsHigh',
		placeholder: string,
	): HTMLInputElement {
		const input = document.createElement('input');
		input.className = 'expr-input flt-operand';
		const numeric = NUMERIC_IR_TOKENS.has(condition.lhs);
		const current = (key === 'rhs' ? condition.rhs : condition.rhsHigh) || '';
		input.value = numeric ? formatThousands(current) : current;
		input.placeholder = placeholder;
		if (numeric) {
			input.setAttribute('inputmode', 'decimal');
		}
		input.addEventListener('input', () => {
			if (numeric) {
				const clean = stripThousands(input.value);
				const formatted = formatThousands(clean);
				if (key === 'rhs') {
					condition.rhs = clean;
					condition.displayValue = formatted;
				} else {
					condition.rhsHigh = clean;
				}
				if (input.value !== formatted) {
					input.value = formatted;
					input.setSelectionRange(formatted.length, formatted.length);
				}
			} else if (key === 'rhs') {
				condition.rhs = input.value;
				condition.displayValue = input.value;
			} else {
				condition.rhsHigh = input.value;
			}
			this.invalidateProjectPreview();
		});
		return input;
	}

	private renderProjectFilterChips(condition: IFilterEditorConditionDraft): HTMLElement {
		const wrap = document.createElement('div');
		wrap.className = 'flt-operand flt-operand--chips';
		if (!condition.values) {
			condition.values = [];
		}
		const values = condition.values;
		const choices = this.filterEditorChoicesForField(condition.lhs);
		values.forEach((val, i) => {
			const chip = document.createElement('span');
			chip.className = 'flt-chip';
			const label = choices.find(ch => ch.value === val)?.label || val;
			appendTextSpan(chip, label, 'flt-chip__label');
			const x = document.createElement('button');
			x.type = 'button';
			x.className = 'flt-chip__x';
			x.title = 'Remove value';
			x.appendChild(materialIcon('close'));
			x.addEventListener('click', () => {
				values.splice(i, 1);
				this.syncInValueDisplay(condition);
				this.invalidateProjectPreview();
				this.renderUnifiedWorkspacePanel();
			});
			chip.appendChild(x);
			wrap.appendChild(chip);
		});
		if (choices.length > 0) {
			const select = document.createElement('select');
			select.className = 'expr-input expr-input--select flt-chip-add';
			const ph = document.createElement('option');
			ph.value = '';
			ph.textContent = '+ Add';
			ph.selected = true;
			select.appendChild(ph);
			for (const ch of choices) {
				if (values.indexOf(ch.value) >= 0) {
					continue;
				}
				const o = document.createElement('option');
				o.value = ch.value;
				o.textContent = ch.label;
				select.appendChild(o);
			}
			select.addEventListener('change', () => {
				if (select.value) {
					values.push(select.value);
					this.syncInValueDisplay(condition);
					this.invalidateProjectPreview();
					this.renderUnifiedWorkspacePanel();
				}
			});
			wrap.appendChild(select);
		} else {
			const input = document.createElement('input');
			input.className = 'expr-input flt-chip-input';
			input.placeholder = 'Type a value, press Enter';
			input.addEventListener('keydown', event => {
				if (event.key === 'Enter') {
					event.preventDefault();
					const v = input.value.trim();
					if (v && values.indexOf(v) < 0) {
						values.push(v);
						this.syncInValueDisplay(condition);
						this.invalidateProjectPreview();
						this.renderUnifiedWorkspacePanel();
					}
				}
			});
			wrap.appendChild(input);
		}
		return wrap;
	}

	private syncInValueDisplay(condition: IFilterEditorConditionDraft): void {
		condition.rhs = (condition.values && condition.values[0]) || '';
		condition.displayValue = this.projectFilterValueDisplay(
			condition.lhs, 'in', condition.rhs, undefined, condition.values);
	}

	private renderProjectFilterLeaf(
		zone: IFilterEditorZoneDraft,
		key: 'places' | 'selections',
		leaf: IFilterEditorLeafSnapshot,
		index: number,
	): HTMLElement {
		const row = document.createElement('div');
		const paused = leaf.enabled === false;
		row.className = `flt-row flt-leaf${paused ? ' flt-leaf--paused' : ''}`;
		const lock = materialIcon('lock');
		lock.classList.add('flt-leaf__lock');
		row.appendChild(lock);
		appendTextSpan(row, leaf.label || this.leafSnapshotLabel(leaf.snapshot, leaf.kind), 'flt-leaf__text');
		if (paused) {
			appendTextSpan(row, 'paused', 'flt-leaf__paused');
		}

		const actions = document.createElement('div');
		actions.className = 'flt-leaf__actions';
		const leafId = leaf.snapshot && typeof leaf.snapshot.id === 'number' ? leaf.snapshot.id : null;
		// Edit shape — re-surfaces the map vertex editor for geometry (place) leaves.
		// The shape's geometry is edited + saved on the map (direct, v1/v2); this just
		// switches the user into that mode, closing the logic panel.
		if (key === 'places' && leafId !== null) {
			const edit = this.unifiedIconButton('Edit this shape on the map', 'edit');
			edit.classList.add('flt-leaf__edit');
			edit.addEventListener('click', () => {
				this.closeUnifiedWorkspacePanel();
				this.filterMgr.beginEditingFilterShape(leafId);
			});
			actions.appendChild(edit);
		}
		const remove = this.unifiedIconButton(
			key === 'places' ? 'Remove this boundary' : 'Remove this selection', 'close');
		remove.classList.add('flt-leaf__remove');
		remove.addEventListener('click', () => {
			zone[key].splice(index, 1);
			this.invalidateProjectPreview();
			this.renderUnifiedWorkspacePanel();
		});
		actions.appendChild(remove);
		row.appendChild(actions);
		return row;
	}

	private leafSnapshotLabel(snapshot: any, kind: 'selection' | 'place'): string {
		const base = snapshot && snapshot.label ? String(snapshot.label) : '';
		if (base) {
			return base;
		}
		if (kind === 'place') {
			return snapshot && snapshot.geometry ? 'Drawn boundary' : 'County boundary';
		}
		return 'Owner / address selection';
	}

	// ---- field + operator helpers (two-zone) --------------------------

	private appendGroupedFieldOptions(select: HTMLSelectElement, condition: IFilterEditorConditionDraft): void {
		// Selection-backed fields (owner/address) are not addable raw conditions — they
		// don't resolve and would zero the list. Keep a pre-existing one as a fallback
		// option so the row still shows its field and can be deleted, but never offer
		// them as new choices.
		const attrs = this.filterEditorAttributes().filter(a => !SELECTION_BACKED_TOKENS.has(a.value));
		const used = new Set<string>();
		const offered = attrs.some(a => a.value === condition.lhs);
		if (condition.lhs && !offered) {
			const o = document.createElement('option');
			o.value = condition.lhs;
			o.textContent = this.filterEditorFieldLabel(condition.lhs);
			o.selected = true;
			select.appendChild(o);
		}
		const addOption = (parent: HTMLElement, attr: IExpressionAttribute) => {
			used.add(attr.value);
			const o = document.createElement('option');
			o.value = attr.value;
			o.textContent = this.filterEditorFieldLabel(attr.value);
			o.selected = attr.value === condition.lhs;
			o.disabled = Boolean(attr.disabled);
			parent.appendChild(o);
		};
		for (const grp of FILTER_FIELD_GROUPS) {
			const members = attrs.filter(a => grp.tokens.indexOf(a.value) >= 0);
			if (!members.length) {
				continue;
			}
			const og = document.createElement('optgroup');
			og.label = grp.label;
			for (const a of members) {
				addOption(og, a);
			}
			select.appendChild(og);
		}
		const others = attrs.filter(a => !used.has(a.value));
		if (others.length) {
			const og = document.createElement('optgroup');
			og.label = 'Other';
			for (const a of others) {
				addOption(og, a);
			}
			select.appendChild(og);
		}
	}

	// A row is "unsupported" if its field isn't in the capability map OR it is a
	// selection-backed token (owner/address) that can't be a raw condition.
	private projectFilterConditionUnsupported(condition: IFilterEditorConditionDraft): boolean {
		return SELECTION_BACKED_TOKENS.has(condition.lhs)
			|| this.filterEditorConditionUsesUnsupportedField(condition);
	}

	private projectFilterOperatorsForField(kind: 'include' | 'exclude', lhs: string): Array<string> {
		const base = this.filterEditorOperatorsForField(lhs).map(op => op.value);
		const result: Array<string> = [];
		for (const op of base) {
			// "is not" is offered only inside Include — in Exclude the zone supplies the
			// negation, so a per-row `ne` would double-negate (the foot-gun).
			if (kind === 'exclude' && op === 'ne') {
				continue;
			}
			result.push(op);
		}
		if (base.indexOf('gte') >= 0 && base.indexOf('lte') >= 0 && NUMERIC_IR_TOKENS.has(lhs)) {
			const at = result.indexOf('lte');
			result.splice(at >= 0 ? at + 1 : result.length, 0, 'between');
		}
		if (base.indexOf('exact') >= 0 && IN_CAPABLE_TOKENS.has(lhs)) {
			const at = result.indexOf('exact');
			result.splice(at >= 0 ? at + 1 : result.length, 0, 'in');
		}
		return result;
	}

	private projectFilterDefaultOperator(kind: 'include' | 'exclude', lhs: string): string {
		const ops = this.projectFilterOperatorsForField(kind, lhs);
		if (ops.indexOf('contains') >= 0) {
			return 'contains';
		}
		if (ops.indexOf('exact') >= 0) {
			return 'exact';
		}
		return ops[0] || this.defaultOperatorForField(lhs);
	}

	private projectFilterOperatorLabel(op: string): string {
		return FILTER_IR_OPERATOR_LABELS[op] || this.filterEditorOperatorLabel(op);
	}

	private projectFilterValueDisplay(
		lhs: string,
		op: string,
		rhs: string,
		rhsHigh: string | undefined,
		values: Array<string> | undefined,
	): string {
		const numeric = NUMERIC_IR_TOKENS.has(lhs);
		const fmt = (v: string) => numeric ? formatThousands(stripThousands(v || '')) : (v || '');
		if (op === 'between') {
			const lo = fmt(rhs);
			const hi = fmt(rhsHigh || '');
			return (lo || hi) ? `${lo} and ${hi}`.trim() : '';
		}
		if (op === 'in') {
			const choices = this.filterEditorChoicesForField(lhs);
			const labels = (values || []).map(v => choices.find(c => c.value === v)?.label || v);
			if (!labels.length) {
				return '';
			}
			if (labels.length === 1) {
				return labels[0];
			}
			return `${labels.slice(0, -1).join(', ')} or ${labels[labels.length - 1]}`;
		}
		const choices = this.filterEditorChoicesForField(lhs);
		if (choices.length) {
			const found = choices.find(c => c.value === rhs);
			if (found) {
				return found.label;
			}
		}
		return fmt(rhs);
	}

	// ---- footer: live count + summary + Apply -------------------------

	private projectLiveCountSeq = 0;
	private projectLiveCountTimer: number | null = null;
	private projectApplyButtonEl: HTMLButtonElement | null = null;

	private invalidateProjectPreview(): void {
		this.projectFilterPreviewOk = false;
		this.projectFilterDraftError = '';
		// The user has changed the draft — from here the footer runs the live dry-run
		// (before this, it shows the project's already-known count).
		this.projectFilterDirty = true;
	}

	private renderProjectFilterFooter(draft: IProjectFilterEditorDraft): HTMLElement {
		const footer = document.createElement('div');
		footer.className = 'editor-foot flt-foot';
		const preview = document.createElement('div');
		preview.className = 'editor-foot__preview flt-foot__preview';
		this.renderProjectFilterLiveCount(preview, draft);

		const actions = document.createElement('div');
		actions.className = 'editor-foot__actions';
		// On a v2 project, offer the reversible "switch back to original (v1)".
		if (this.projectFilterSchemaVersion >= 2) {
			const revert = this.unifiedButton('Switch back to original', 'btn-ghost btn-sm');
			revert.title = 'Return this project to its original (v1) filters; exclude rules become inert.';
			revert.addEventListener('click', () => this.switchProjectBackToV1(revert));
			actions.appendChild(revert);
		}
		const cancel = this.unifiedButton('Cancel', 'btn-ghost');
		cancel.addEventListener('click', () => this.closeUnifiedWorkspacePanel());
		const apply = this.unifiedButton('Apply to list', 'btn-primary');
		const ir = this.projectDraftToIR(draft);
		// Apply needs an edit (a pristine open is a no-op) AND a successful live count.
		apply.disabled = !ir || !this.projectFilterDirty || !this.projectFilterPreviewOk;
		apply.title = apply.disabled ?
			(this.projectFilterDirty ? 'Wait for the live count before applying.' : 'Make a change to apply.') :
			'';
		this.projectApplyButtonEl = apply;
		apply.addEventListener('click', async () => {
			if (apply.disabled) {
				return;
			}
			await this.runWithButtonPending(apply, 'Applying…', async () => {
				await this.applyProjectFilterDraft(draft);
			});
		});
		actions.appendChild(cancel);
		actions.appendChild(apply);
		footer.appendChild(preview);
		footer.appendChild(actions);
		return footer;
	}

	private renderProjectFilterLiveCount(container: HTMLElement, draft: IProjectFilterEditorDraft): void {
		const countEl = document.createElement('span');
		countEl.className = 'diff diff-up flt-foot__count';
		const summary = document.createElement('div');
		summary.className = 'diff-meta flt-summary';
		container.appendChild(countEl);
		container.appendChild(summary);
		this.renderProjectFilterSummary(summary, draft);

		const ir = this.projectDraftToIR(draft);
		if (ir === null) {
			countEl.textContent = this.projectFilterDegenerateCount(draft);
			return;
		}
		// Pristine open (no edits yet): the project's list count is already known —
		// show it instantly and skip the cold, cache-bypassed dry-run (which can take
		// 5-8s over a county-sized set). The dry-run runs once the user edits.
		if (!this.projectFilterDirty) {
			countEl.textContent = this.lastKnownAddressCount !== null ?
				`${numberFormat(this.lastKnownAddressCount)} addresses · current list` :
				'Current list';
			return;
		}
		countEl.textContent = 'Counting…';
		const seq = ++this.projectLiveCountSeq;
		if (this.projectLiveCountTimer !== null) {
			window.clearTimeout(this.projectLiveCountTimer);
		}
		this.projectLiveCountTimer = window.setTimeout(() => {
			void this.runProjectLivePreview(ir, seq, countEl);
		}, 400);
	}

	// Classify a failed preview so the Apply gate can react sensibly: a cancelled
	// (superseded) request is ignored; a 400 means the IR is invalid (keep Apply
	// gated); anything else (timeout / 5xx / network) is "unavailable" — we still
	// let the user Apply, because Apply's save is lighter than this double-count
	// dry-run, so a slow preview shouldn't trap a valid filter.
	private projectPreviewErrorKind(err: any): 'cancel' | 'invalid' | 'unavailable' {
		if (svc.project.isCancellation(err)) {
			return 'cancel';
		}
		const status = err && err.response && err.response.status;
		return status === 400 ? 'invalid' : 'unavailable';
	}

	private projectPreviewInvalidText(err: any): string {
		const detail = err && err.response && err.response.data && err.response.data.detail;
		return typeof detail === 'string' && detail ? detail : 'These filters aren’t valid yet.';
	}

	private setProjectPreviewState(seq: number, countEl: HTMLElement, text: string, ok: boolean): void {
		if (seq !== this.projectLiveCountSeq) {
			return;
		}
		countEl.textContent = text;
		this.projectFilterPreviewOk = ok;
		if (this.projectApplyButtonEl) {
			this.projectApplyButtonEl.disabled = !(ok && this.projectFilterDirty);
			this.projectApplyButtonEl.title = this.projectApplyButtonEl.disabled ?
				'Adjust the filters until the list is valid.' :
				'';
		}
	}

	// Two-step preview: a FAST parcel-only count (unblocks Apply right away), then
	// the slower address-key (billing) count chained on. Both share one cancel
	// token so a newer edit aborts the in-flight pair instead of stacking heavy
	// dry-runs on the backend.
	private async runProjectLivePreview(ir: IFilterTreeIR, seq: number, countEl: HTMLElement): Promise<void> {
		if (this.cancelSrc.filterPreview) {
			this.cancelSrc.filterPreview.cancel();
		}
		this.cancelSrc.filterPreview = svc.cancelTokenSource();
		const cancelToken = this.cancelSrc.filterPreview.token;

		let total: number;
		try {
			const fast = await svc.project.filterTreePreview(this.slug, ir, {includeQuantity: false}, {cancelToken});
			total = fast.total;
		} catch (err) {
			const kind = this.projectPreviewErrorKind(err);
			if (kind === 'cancel') {
				return;
			}
			logger.warning('runProjectLivePreview: fast preview failed', err);
			if (kind === 'invalid') {
				this.setProjectPreviewState(seq, countEl, this.projectPreviewInvalidText(err), false);
			} else {
				this.setProjectPreviewState(seq, countEl, 'Count unavailable — you can still Apply.', true);
			}
			return;
		}
		if (seq !== this.projectLiveCountSeq) {
			return;
		}
		const base = `${numberFormat(total)} parcels`;
		this.setProjectPreviewState(seq, countEl, `${base} · counting addresses…`, true);

		try {
			const full = await svc.project.filterTreePreview(this.slug, ir, {includeQuantity: true}, {cancelToken});
			if (seq !== this.projectLiveCountSeq) {
				return;
			}
			const quantity = full.quantity;
			let text = `${numberFormat(full.total)} parcels · ${quantity === null ? '—' : numberFormat(quantity)} addresses`;
			// Before/after: compare the draft (New) against the saved list (Current).
			if (this.lastKnownAddressCount !== null && quantity !== null) {
				const delta = quantity - this.lastKnownAddressCount;
				const sign = delta > 0 ? '+' : '';
				text += ` · was ${numberFormat(this.lastKnownAddressCount)} (${sign}${numberFormat(delta)})`;
			}
			this.setProjectPreviewState(seq, countEl, text, true);
		} catch (err) {
			const kind = this.projectPreviewErrorKind(err);
			if (kind === 'cancel') {
				return;
			}
			logger.warning('runProjectLivePreview: address-count preview failed', err);
			// Keep the parcel count + Apply enabled; just note addresses couldn't be counted.
			this.setProjectPreviewState(seq, countEl, `${base} · address count unavailable`, true);
		}
	}

	private groupHasContent(group: IProjectFilterGroupDraft): boolean {
		return this.projectFilterUsableConditions(group).length > 0
			|| group.selections.length > 0
			|| group.places.length > 0;
	}

	// An include group with conditions but NO scope leaf (boundary / county /
	// owner-or-address selection) resolves UNBOUNDED — its conditions run across
	// every county (the whole parcel universe), which silently explodes the list
	// and est. price. Used to warn before Apply (the deleted-the-polygon trap).
	private groupIsUnbounded(group: IProjectFilterGroupDraft): boolean {
		return group.direction === 'include'
			&& group.selections.length === 0
			&& group.places.length === 0
			&& this.projectFilterUsableConditions(group).length > 0;
	}

	private projectFilterDegenerateCount(draft: IProjectFilterEditorDraft): string {
		const anyInclude = this.projectIncludeGroups(draft).some(g => this.groupHasContent(g));
		return anyInclude ? 'Count pending' : 'No list yet';
	}

	private projectFilterUsableConditions(zone: IFilterEditorZoneDraft): Array<IFilterEditorConditionDraft> {
		return zone.conditions.filter(c => this.compileConditionToIR(c) !== null);
	}

	private renderProjectFilterSummary(host: HTMLElement, draft: IProjectFilterEditorDraft): void {
		host.textContent = '';
		const includeGroups = this.projectIncludeGroups(draft);
		const excludeGroup = this.projectExcludeGroup(draft);
		const exc = excludeGroup ? this.projectFilterUsableConditions(excludeGroup) : [];
		const populatedIncludes = includeGroups.filter(g => this.groupHasContent(g));
		if (!populatedIncludes.length) {
			appendTextSpan(
				host,
				exc.length ?
					'Add at least one Include condition — exclusions only remove from an included set.' :
					'Add a condition to start building your list.',
				exc.length ? 'flt-summary__warn' : 'flt-summary__hint');
			return;
		}
		if (populatedIncludes.length === 1) {
			const g = populatedIncludes[0];
			const conds = this.projectFilterUsableConditions(g);
			const leaves = g.selections.length + g.places.length;
			appendTextSpan(host, this.projectFilterIncludeClause(g, conds, leaves), 'flt-summary__include');
		} else {
			// Multiple groups — the exact wording lives per-card; the headline count
			// (dry-run of the compiled IR) is the source of truth. Keep the summary
			// honest and short about how the groups combine.
			const word = draft.match === 'all' ? 'ALL' : 'ANY';
			appendTextSpan(host, `Showing parcels that match ${word} of ${populatedIncludes.length} groups.`, 'flt-summary__include');
		}
		// #134 guard: a populated include group with no area runs across ALL counties.
		// Warn before Apply so a deleted boundary can't silently blow up the list/price.
		const unbounded = populatedIncludes.filter(g => this.groupIsUnbounded(g)).length;
		if (unbounded) {
			const msg = populatedIncludes.length > 1 ?
				(unbounded === 1 ?
					' One group has no area — it matches ALL counties. Add a boundary to scope it.' :
					` ${unbounded} groups have no area — they match ALL counties. Add a boundary to scope them.`) :
				' This has no area — it matches ALL counties (a very large list). Add a boundary to scope it.';
			appendTextSpan(host, msg, 'flt-summary__warn');
		}
		if (excludeGroup && exc.length) {
			appendTextSpan(host, this.projectFilterExcludeClause(excludeGroup, exc), 'flt-summary__exclude');
		} else if (excludeGroup && excludeGroup.conditions.length
				&& !excludeGroup.selections.length && !excludeGroup.places.length) {
			// The exclude group has condition rows but none compile (e.g. a blank value),
			// so projectDraftToIR drops the whole exclude block (zoneToIRGroup -> null).
			// Warn instead of silently dropping it on Apply — the "exclude vanished after
			// Apply" report (45FKF): an incomplete exclusion row produced exclude:null with
			// no feedback. Apply stays enabled (the include set is still valid).
			appendTextSpan(host, ' One exclusion is incomplete — add a value or it won’t be saved.', 'flt-summary__warn');
		}
		appendTextSpan(host, ' Your Do-Not-Mail list is always removed.', 'flt-summary__dnm');
	}

	private projectFilterIncludeClause(
		zone: IFilterEditorZoneDraft,
		conds: Array<IFilterEditorConditionDraft>,
		leafCount: number,
	): string {
		const join = zone.match === 'any' ? ' or ' : ' and ';
		if (!conds.length && leafCount) {
			return 'Showing parcels from your saved selections.';
		}
		const hoist = this.projectFilterZoningHoist(conds);
		if (hoist) {
			const rest = conds.filter(c => c !== hoist.condition).map(c => this.projectFilterConditionPhrase(c));
			const noun = `${hoist.adjective} parcels`;
			return rest.length ? `Showing ${noun} where ${rest.join(join)}.` : `Showing ${noun}.`;
		}
		const phrases = conds.map(c => this.projectFilterConditionPhrase(c));
		return `Showing parcels where ${phrases.join(join)}.`;
	}

	private projectFilterZoningHoist(
		conds: Array<IFilterEditorConditionDraft>,
	): {condition: IFilterEditorConditionDraft; adjective: string;} | null {
		// Strictly scoped: zoning-only, single-value, include-only, first-row-only.
		const first = conds[0];
		if (!first || first.lhs !== 'parcel__zoning__name') {
			return null;
		}
		let value = '';
		if (first.operator === 'exact') {
			value = first.rhs;
		} else if (first.operator === 'in' && first.values && first.values.length === 1) {
			value = first.values[0];
		} else {
			return null;
		}
		if (!value) {
			return null;
		}
		const choices = this.filterEditorChoicesForField(first.lhs);
		const label = choices.find(c => c.value === value)?.label || value;
		return {condition: first, adjective: label.toLowerCase()};
	}

	private projectFilterExcludeClause(
		zone: IFilterEditorZoneDraft,
		conds: Array<IFilterEditorConditionDraft>,
	): string {
		const join = zone.match === 'any' ? ' or ' : ' and ';
		const phrases = conds.map(c => this.projectFilterConditionPhrase(c));
		return ` Then removing parcels where ${phrases.join(join)}.`;
	}

	private projectFilterConditionPhrase(c: IFilterEditorConditionDraft): string {
		const field = this.filterEditorFieldLabel(c.lhs);
		const op = this.projectFilterOperatorLabel(c.operator);
		const value = this.projectFilterValueDisplay(c.lhs, c.operator, c.rhs, c.rhsHigh, c.values);
		return `${field} ${op} ${value}`.trim();
	}

	private async applyProjectFilterDraft(draft: IProjectFilterEditorDraft): Promise<void> {
		const ir = this.projectDraftToIR(draft);
		if (!ir) {
			return;
		}
		try {
			// Lane E PR 1e-iii — Apply opts the project into v2 (the include/exclude
			// engine) atomically with the save: the version is set before the IR is
			// lowered, so an Apply that adds an exclude succeeds.
			await svc.project.saveFilterTree(this.slug, ir, {filterSchemaVersion: 2});
		} catch (err) {
			logger.warning('applyProjectFilterDraft: save failed', err);
			this.projectFilterDraftError = this.projectSaveErrorMessage(err);
			this.renderUnifiedWorkspacePanel();
			return;
		}
		this.projectFilterSchemaVersion = 2;
		await this.filterMgr.reloadFiltersAfterTreeSave();
		this.closeUnifiedWorkspacePanel();
		this.renderProjectOptInBanner();
		// The IR save rewrites the whole tree but emits none of the per-filter
		// signals that drive syncCounts / syncVisibleParcels (the normal chip-edit
		// path goes through filterUpdated/Created/Deleted). Refresh the headline
		// list count + map directly so they reflect the just-applied tree.
		this.syncVisibleParcels();
		// The rewrite deletes the old top-level filters and creates new ids, so the
		// per-filter parcel-fill tile layers keyed by the OLD ids go stale (and the
		// map keeps requesting `?filter=<old id>` tiles -> 400s). Re-sync with
		// removeMissing to drop the dead-id layers and add the fresh ones.
		await this.syncAllParcelFilterTiles();
		await this.syncCounts();
	}

	private projectSaveErrorMessage(err: any): string {
		const detail = err && err.response && err.response.data && err.response.data.detail;
		if (typeof detail === 'string' && detail) {
			return detail;
		}
		return 'Could not apply these filters. If this project is not on the new filter engine, exclusions can’t be saved yet.';
	}

	// ---- 1e-iii: opt-in banner / duplicate-and-try / switch-back ------

	// A non-modal banner above the chip strip on v1 projects, inviting the user into
	// the include/exclude editor (their list stays as-is until Apply). Hidden once the
	// project is v2 or the user dismisses it.
	private renderProjectOptInBanner(): void {
		const existing = document.getElementById('lb-project-filter-optin-banner');
		if (existing) {
			existing.remove();
		}
		const strip = document.getElementById('lb-project-filter-chip-strip');
		if (!strip || !strip.parentElement) {
			return;
		}
		if (this.projectFilterSchemaVersion >= 2 || this.projectOptInBannerDismissed || this.disabled) {
			return;
		}
		const banner = document.createElement('div');
		banner.id = 'lb-project-filter-optin-banner';
		banner.className = 'flt-optin';

		const text = document.createElement('div');
		text.className = 'flt-optin__text';
		appendTextSpan(text, '✨ New filter editor', 'flt-optin__title');
		const stays = this.lastKnownAddressCount !== null ?
			` Your current list (${numberFormat(this.lastKnownAddressCount)} addresses) stays as-is until you apply.` :
			'';
		appendTextSpan(text, `Build include & exclude filters with a live count.${stays}`, 'flt-optin__body');
		banner.appendChild(text);

		const actions = document.createElement('div');
		actions.className = 'flt-optin__actions';
		const tryBtn = this.unifiedButton('Try the new editor', 'btn-primary btn-sm');
		tryBtn.addEventListener('click', () => this.openProjectFilterEditor());
		const dupBtn = this.unifiedButton('Duplicate & try', 'btn-ghost btn-sm');
		dupBtn.addEventListener('click', () => this.duplicateProjectAndTry(dupBtn));
		const close = this.unifiedIconButton('Dismiss', 'close');
		close.addEventListener('click', () => {
			this.projectOptInBannerDismissed = true;
			this.renderProjectOptInBanner();
		});
		actions.appendChild(tryBtn);
		actions.appendChild(dupBtn);
		actions.appendChild(close);
		banner.appendChild(actions);

		strip.parentElement.insertBefore(banner, strip);
	}

	private async duplicateProjectAndTry(btn: HTMLButtonElement): Promise<void> {
		await this.runWithButtonPending(btn, 'Duplicating…', async () => {
			try {
				const copy = await svc.project.clone(this.slug, {filterSchemaVersion: 2});
				if (copy && copy.absoluteUrl) {
					window.location.href = copy.absoluteUrl;
				}
			} catch (err) {
				logger.warning('duplicateProjectAndTry: clone failed', err);
			}
		});
	}

	// "Switch back to original" — flip the project to v1 without touching the tree
	// (v1's group-parse guard makes any exclude groups inert). Surfaced in the editor
	// footer for v2 projects.
	private async switchProjectBackToV1(btn: HTMLButtonElement): Promise<void> {
		await this.runWithButtonPending(btn, 'Switching…', async () => {
			try {
				await svc.project.setFilterSchemaVersion(this.slug, 1);
			} catch (err) {
				logger.warning('switchProjectBackToV1: failed', err);
				return;
			}
			this.projectFilterSchemaVersion = 1;
			await this.filterMgr.reloadFiltersAfterTreeSave();
			this.closeUnifiedWorkspacePanel();
			this.renderProjectOptInBanner();
			this.syncVisibleParcels();
			await this.syncAllParcelFilterTiles();
			await this.syncCounts();
		});
	}

	updateUnifiedFilterEditor(filter: IFilter): void {
		if (!this.unifiedEditingFilter || this.unifiedEditingFilter.id !== filter.id) {
			return;
		}
		this.unifiedEditingFilter = filter;
		if (!this.unifiedEditorDraft || this.unifiedEditorDraft.filterId !== filter.id) {
			this.unifiedEditorDraft = this.filterToUnifiedEditorDraft(filter);
		}
		if (!this.unifiedEditorRenameActive) {
			this.unifiedEditorRenameDraft = this.filterDisplayLabel(filter);
		}
		this.renderUnifiedWorkspacePanel();
	}

	clearUnifiedFilterEditorIfFilter(filterId: number): void {
		if (this.unifiedEditingFilter && this.unifiedEditingFilter.id === filterId) {
			this.closeUnifiedWorkspacePanel();
		}
	}

	unifiedFilterEditorHost(): HTMLElement | null {
		return document.getElementById('lb-project-unified-filter-editor-host');
	}

	private renderUnifiedFilterEditorPanel(panel: HTMLElement, filter: IFilter): void {
		const editorBar = document.createElement('div');
		editorBar.className = 'editor-bar editor-bar--header';
		const crumb = document.createElement('div');
		crumb.className = 'editor-bar__crumb';
		const miniChip = document.createElement('span');
		miniChip.className = `chip chip--mini ${filter.enabled ? 'chip--enabled' : 'chip--paused'}`;
		const scope = document.createElement('span');
		scope.className = 'chip__scope material-icons';
		scope.setAttribute('aria-hidden', 'true');
		scope.textContent = this.filterEditorIcon(filter);
		miniChip.appendChild(scope);
		this.renderUnifiedFilterRenameField(miniChip, filter);
		crumb.appendChild(miniChip);
		appendTextSpan(crumb, '->', 'editor-bar__sep');
		appendTextSpan(crumb, 'Editing filter', 'editor-bar__title');
		editorBar.appendChild(crumb);

		const actions = document.createElement('div');
		actions.className = 'editor-bar__actions';
		const deleteButton = this.unifiedButton('Delete', 'btn-ghost btn-sm danger');
		deleteButton.title = 'Delete this filter';
		deleteButton.addEventListener('click', () => this.filterMgr.deleteFilterFromEditor(filter.id));
		const esc = document.createElement('span');
		esc.className = 'kbd';
		esc.textContent = 'Esc';
		esc.setAttribute('role', 'button');
		esc.addEventListener('click', () => this.closeUnifiedWorkspacePanel());
		actions.appendChild(deleteButton);
		actions.appendChild(this.unifiedGripButton());
		actions.appendChild(esc);
		actions.appendChild(this.unifiedCloseButton('Close filter editor'));
		editorBar.appendChild(actions);
		panel.appendChild(editorBar);

		const body = document.createElement('div');
		body.className = 'editor-body lb-project-unified-editor-body';
		this.renderUnifiedFilterEditorExpression(body, filter);
		panel.appendChild(body);

		const footer = document.createElement('div');
		footer.className = 'editor-foot';
		const preview = document.createElement('div');
		preview.className = 'editor-foot__preview';
		this.renderUnifiedFilterLiveCount(preview, filter);
		const footerActions = document.createElement('div');
		footerActions.className = 'editor-foot__actions';
		const cancel = this.unifiedButton('Cancel', 'btn-ghost');
		cancel.addEventListener('click', () => this.closeUnifiedWorkspacePanel());
		const save = this.unifiedButton('Save filter', 'btn-primary');
		save.disabled = this.unifiedEditorDraft ? !this.unifiedEditorDraftCanSave(filter, this.unifiedEditorDraft) : false;
		save.addEventListener('click', async () => {
			if (save.disabled) {
				return;
			}
			await this.runWithButtonPending(save, 'Saving...', async () => {
				await this.commitUnifiedEditorRename(filter, false);
				if (this.unifiedEditorDraft) {
					await this.filterMgr.saveUnifiedFilterEditorDraft(filter.id, this.unifiedEditorDraft);
				}
				this.closeUnifiedWorkspacePanel();
			});
		});
		footerActions.appendChild(cancel);
		footerActions.appendChild(save);
		footer.appendChild(preview);
		footer.appendChild(footerActions);
		panel.appendChild(footer);
	}

	private unifiedLiveCountSeq = 0;
	private unifiedLiveCountTimer: number | null = null;

	// Lane E PR 1e-i — replaces the stale "Preview applies after save" count with
	// a debounced live count from the dry-run endpoint, plus a plain-English
	// sentence generated from the same draft. The dry-run lowers this draft as the
	// whole project and counts it, so the number reflects exactly what Apply would
	// produce for this filter set. Falls back to the persisted per-filter count
	// when the draft can't be compiled to IR (owner-search selections, or no
	// complete conditions yet) or the request fails.
	private renderUnifiedFilterLiveCount(container: HTMLElement, filter: IFilter): void {
		const draft = this.unifiedEditorDraft || this.filterToUnifiedEditorDraft(filter);
		const countEl = document.createElement('span');
		countEl.className = `diff ${filter.enabled ? 'diff-up' : ''}`;
		const sentence = document.createElement('span');
		sentence.className = 'diff-meta';
		sentence.textContent = this.filterDraftSentence(draft);

		const staleMetrics = filter.metrics && filter.metrics.status === 'ready' && isFinite(Number(filter.metrics.uniqueAddressCount)) ?
			Number(filter.metrics.uniqueAddressCount) :
			null;
		const fallbackText = staleMetrics === null ? 'Count pending' : `${numberFormat(staleMetrics)} addresses`;

		const ir = this.unifiedDraftToFilterIR(draft);
		container.appendChild(countEl);
		container.appendChild(sentence);
		if (ir === null) {
			countEl.textContent = fallbackText;
			return;
		}

		countEl.textContent = 'Counting...';
		const seq = ++this.unifiedLiveCountSeq;
		if (this.unifiedLiveCountTimer !== null) {
			window.clearTimeout(this.unifiedLiveCountTimer);
		}
		this.unifiedLiveCountTimer = window.setTimeout(async () => {
			let text = fallbackText;
			try {
				const preview = await svc.project.filterTreePreview(this.slug, ir);
				text = `${preview.quantity === null ? '—' : numberFormat(preview.quantity)} addresses`;
			} catch (err) {
				logger.warning('renderUnifiedFilterLiveCount: live count failed', err);
			}
			if (seq === this.unifiedLiveCountSeq) {
				countEl.textContent = text;
			}
		}, 400);
	}

	private unifiedDraftToFilterIR(draft: IFilterEditorDraft): IFilterTreeIR | null {
		const groups = draft.groups || [];
		// Owner/address-search selections are opaque — not previewable as
		// conditions (collapsing them would change which parcels match).
		if (groups.some(group => group.ownerSelectionGroup)) {
			return null;
		}
		const compile = (c: IFilterEditorConditionDraft): IFilterIRCondition | null => {
			const field = TOKEN_TO_IR_FIELD[c.lhs];
			const rhs = (c.rhs || '').trim();
			if (!field || c.enabled === false || !c.operator || !rhs) {
				return null;
			}
			return {kind: 'condition', field, op: c.operator, value: rhs, enabled: true, displayValue: c.displayValue};
		};
		const usable = groups.filter(group => group.conditions.some(c => compile(c) !== null));
		const include: IFilterIRGroup = {kind: 'group', match: 'all', conditions: [], selections: [], places: [], groups: []};
		if (usable.length === 1) {
			include.match = usable[0].logic === FILTER_GROUP_LOGIC_OR ? 'any' : 'all';
			for (const c of usable[0].conditions) {
				const cond = compile(c);
				if (cond) {
					include.conditions.push(cond);
				}
			}
		} else if (usable.length > 1) {
			for (const group of usable) {
				const sub: IFilterIRGroup = {
					kind: 'group',
					match: group.logic === FILTER_GROUP_LOGIC_OR ? 'any' : 'all',
					conditions: [], selections: [], places: [], groups: [],
				};
				for (const c of group.conditions) {
					const cond = compile(c);
					if (cond) {
						sub.conditions.push(cond);
					}
				}
				if (sub.conditions.length) {
					include.groups.push(sub);
				}
			}
		}
		if (!include.conditions.length && !include.groups.length) {
			return null;
		}
		return {ir_version: 1, match: 'all', include, exclude: null, provenance: {source: 'ui', unresolved: []}};
	}

	private filterDraftSentence(draft: IFilterEditorDraft): string {
		const parts: Array<string> = [];
		for (const group of draft.groups || []) {
			if (group.ownerSelectionGroup) {
				if (group.conditions.some(c => (c.rhs || '').trim())) {
					parts.push('selected owners');
				}
				continue;
			}
			const phrases = group.conditions
				.filter(c => c.lhs && c.operator && (c.rhs || '').trim() && c.enabled !== false)
				.map(c => {
					const value = NUMERIC_IR_TOKENS.has(c.lhs) ?
						formatThousands(c.rhs) :
						(c.displayValue || c.rhs || '').trim();
					return `${FILTER_IR_FIELD_LABELS[c.lhs] || c.lhs} ${FILTER_IR_OPERATOR_LABELS[c.operator] || c.operator} ${value}`;
				});
			if (phrases.length) {
				parts.push(phrases.join(group.logic === FILTER_GROUP_LOGIC_OR ? ' or ' : ' and '));
			}
		}
		if (!parts.length) {
			return 'Add a condition to start building your list.';
		}
		return `Showing parcels where ${parts.join(' and ')}.`;
	}

	private renderUnifiedFilterEditorExpression(body: HTMLElement, filter: IFilter): void {
		const draft = this.unifiedEditorDraft && this.unifiedEditorDraft.filterId === filter.id ?
			this.unifiedEditorDraft :
			this.filterToUnifiedEditorDraft(filter);
		this.unifiedEditorDraft = draft;
		const expr = document.createElement('div');
		expr.className = 'expr';
		draft.groups.forEach((group, groupIndex) => {
			expr.appendChild(this.renderUnifiedFilterEditorGroup(draft, group, groupIndex));
		});
		body.appendChild(expr);
	}

	private renderUnifiedFilterEditorGroup(
		draft: IFilterEditorDraft,
		group: IFilterEditorGroupDraft,
		groupIndex: number,
	): HTMLElement {
		const groupEl = document.createElement('div');
		groupEl.className = `expr__group${groupIndex > 0 ? ' expr__group--nested' : ''}`;
		const head = document.createElement('div');
		head.className = 'expr__group-head';
		const logic = document.createElement('select');
		logic.className = 'combo expr__logic-select';
		[
			[FILTER_GROUP_LOGIC_AND, 'All of'],
			[FILTER_GROUP_LOGIC_OR, 'Any of'],
		].forEach(([value, label]) => {
			const option = document.createElement('option');
			option.value = value;
			option.textContent = label;
			option.selected = group.logic === value;
			logic.appendChild(option);
		});
		logic.disabled = Boolean(group.ownerSelectionGroup);
		logic.addEventListener('change', () => {
			group.logic = logic.value === FILTER_GROUP_LOGIC_OR ? FILTER_GROUP_LOGIC_OR : FILTER_GROUP_LOGIC_AND;
			this.renderUnifiedWorkspacePanel();
		});
		head.appendChild(logic);
		appendTextSpan(
			head,
			group.ownerSelectionGroup ?
				'selected owner/address groups keep this chip bounded' :
				(group.logic === FILTER_GROUP_LOGIC_OR ? 'match at least one condition (OR)' : 'match every condition (AND)'),
			'expr__hint');
		if (draft.groups.length > 1 && !group.ownerSelectionGroup) {
			const removeGroup = this.unifiedIconButton('Remove group', 'close');
			removeGroup.addEventListener('click', () => {
				draft.groups.splice(groupIndex, 1);
				if (!draft.groups.length) {
					draft.groups.push(this.defaultUnifiedEditorGroup());
				}
				this.renderUnifiedWorkspacePanel();
			});
			head.appendChild(removeGroup);
		}
		groupEl.appendChild(head);
		if (group.ownerSelectionGroup && !group.conditions.length) {
			const empty = document.createElement('div');
			empty.className = 'expr__empty-state';
			empty.textContent = 'No saved owner/address selections were found for this chip. Recreate the owner-search filter instead of saving this empty state.';
			groupEl.appendChild(empty);
		} else {
			group.conditions.forEach((condition, conditionIndex) => {
				groupEl.appendChild(this.renderUnifiedFilterEditorCondition(group, condition, conditionIndex));
			});
		}
		const addRow = document.createElement('div');
		addRow.className = 'expr__add-row';
		if (!group.ownerSelectionGroup) {
			const addCondition = document.createElement('button');
			addCondition.type = 'button';
			addCondition.className = 'add-btn';
			addCondition.textContent = '+ Condition';
			addCondition.addEventListener('click', () => {
				group.conditions.push(this.defaultUnifiedEditorCondition());
				this.renderUnifiedWorkspacePanel();
			});
			addRow.appendChild(addCondition);
		}
		if (groupIndex === draft.groups.length - 1) {
			const addGroup = document.createElement('button');
			addGroup.type = 'button';
			addGroup.className = 'add-btn';
			addGroup.textContent = '+ Group';
			addGroup.addEventListener('click', () => {
				draft.groups.push(this.defaultUnifiedEditorGroup());
				this.renderUnifiedWorkspacePanel();
			});
			addRow.appendChild(addGroup);
		}
		groupEl.appendChild(addRow);
		return groupEl;
	}

	private renderUnifiedFilterEditorCondition(
		group: IFilterEditorGroupDraft,
		condition: IFilterEditorConditionDraft,
		conditionIndex: number,
	): HTMLElement {
		const row = document.createElement('div');
		this.ensureUnifiedEditorConditionDefaults(condition);
		const conditionEnabled = condition.enabled !== false;
		const unsupported = this.filterEditorConditionUsesUnsupportedField(condition);
		row.className = `expr-row${conditionEnabled ? '' : ' expr-row--paused'}${unsupported ? ' expr-row--unsupported' : ''}`;
		const enabled = document.createElement('button');
		enabled.type = 'button';
		enabled.className = `expr-row__toggle${conditionEnabled ? ' is-on' : ''}`;
		enabled.title = conditionEnabled ? 'Condition is active' : 'Condition is ignored';
		enabled.setAttribute('aria-label', conditionEnabled ? 'Disable condition' : 'Enable condition');
		enabled.setAttribute('aria-pressed', conditionEnabled ? 'true' : 'false');
		enabled.innerHTML = `<span class="material-icons" aria-hidden="true">${conditionEnabled ? 'check_circle' : 'pause_circle'}</span>`;
		enabled.addEventListener('click', () => {
			condition.enabled = condition.enabled === false;
			this.renderUnifiedWorkspacePanel();
		});
		row.appendChild(enabled);
		const field = document.createElement('select');
		field.className = 'combo combo--field';
		if (unsupported) {
			const option = document.createElement('option');
			option.value = condition.lhs;
			option.textContent = this.filterEditorFieldLabel(condition.lhs);
			option.selected = true;
			field.appendChild(option);
		}
		for (const attr of this.filterEditorAttributes()) {
			const option = document.createElement('option');
			option.value = attr.value;
			option.textContent = this.filterEditorFieldLabel(attr.value);
			option.selected = attr.value === condition.lhs;
			option.disabled = Boolean(attr.disabled);
			field.appendChild(option);
		}
		field.disabled = Boolean(group.ownerSelectionGroup);
		field.addEventListener('change', () => {
			condition.lhs = field.value;
			condition.operator = this.defaultOperatorForField(field.value);
			this.applyDefaultValueForField(condition);
			this.renderUnifiedWorkspacePanel();
		});
		row.appendChild(field);

		const operator = document.createElement('select');
		operator.className = 'combo combo--op';
		const operators = this.filterEditorOperatorsForField(condition.lhs);
		if (!operators.length && condition.operator) {
			const option = document.createElement('option');
			option.value = condition.operator;
			option.textContent = this.filterEditorOperatorLabel(condition.operator);
			option.selected = true;
			operator.appendChild(option);
		}
		for (const op of operators) {
			const option = document.createElement('option');
			option.value = op.value;
			option.textContent = this.filterEditorOperatorLabel(op.value);
			option.selected = op.value === condition.operator;
			operator.appendChild(option);
		}
		operator.addEventListener('change', () => {
			condition.operator = operator.value;
			this.renderUnifiedWorkspacePanel();
		});
		row.appendChild(operator);

		const choices = this.filterEditorChoicesForField(condition.lhs);
		if (choices.length > 0) {
			const valueSelect = document.createElement('select');
			valueSelect.className = 'expr-input expr-input--select';
			if (!condition.rhs) {
				const placeholder = document.createElement('option');
				placeholder.value = '';
				placeholder.textContent = 'Choose value';
				placeholder.disabled = true;
				placeholder.selected = true;
				valueSelect.appendChild(placeholder);
			}
			for (const choice of choices) {
				const option = document.createElement('option');
				option.value = choice.value;
				option.textContent = choice.label;
				option.selected = choice.value === condition.rhs;
				valueSelect.appendChild(option);
			}
			valueSelect.addEventListener('change', () => {
				condition.rhs = valueSelect.value;
				condition.displayValue = valueSelect.options[valueSelect.selectedIndex]?.textContent || valueSelect.value;
			});
			row.appendChild(valueSelect);
		} else {
			const value = document.createElement('input');
			value.className = 'expr-input';
			const numericField = NUMERIC_IR_TOKENS.has(condition.lhs);
			value.value = numericField ?
				formatThousands(condition.rhs || condition.displayValue || '') :
				(condition.displayValue || condition.rhs);
			value.placeholder = 'Value';
			if (numericField) {
				value.setAttribute('inputmode', 'decimal');
			}
			const ownerBackedValue = (
				condition.lhs === 'parcel__owner_uid'
				&& Boolean(condition.displayValue)
				&& condition.displayValue !== condition.rhs
			);
			value.readOnly = ownerBackedValue;
			if (ownerBackedValue) {
				value.title = 'Owner search selections keep the internal owner ID while showing the owner name.';
			}
			value.addEventListener('input', () => {
				if (ownerBackedValue) {
					return;
				}
				if (numericField) {
					// Store clean digits; show thousands-separators as typed.
					const clean = stripThousands(value.value);
					condition.rhs = clean;
					const formatted = formatThousands(clean);
					condition.displayValue = formatted;
					if (value.value !== formatted) {
						value.value = formatted;
						value.setSelectionRange(formatted.length, formatted.length);
					}
				} else {
					condition.rhs = value.value;
					condition.displayValue = value.value;
				}
			});
			row.appendChild(value);
		}

		const remove = this.unifiedIconButton('Remove condition', 'close');
		remove.addEventListener('click', () => {
			group.conditions.splice(conditionIndex, 1);
			if (!group.conditions.length) {
				group.conditions.push(this.defaultUnifiedEditorCondition());
			}
			this.renderUnifiedWorkspacePanel();
		});
		row.appendChild(remove);
		return row;
	}

	private ensureUnifiedEditorConditionDefaults(condition: IFilterEditorConditionDraft): void {
		if (!condition.operator && condition.lhs) {
			condition.operator = this.defaultOperatorForField(condition.lhs);
		}
	}

	private applyDefaultValueForField(condition: IFilterEditorConditionDraft): void {
		const choices = this.filterEditorChoicesForField(condition.lhs);
		if (choices.length > 0) {
			condition.rhs = choices[0].value;
			condition.displayValue = choices[0].label;
		} else {
			condition.rhs = '';
			condition.displayValue = '';
		}
	}

	private unifiedIconButton(title: string, icon: string): HTMLButtonElement {
		const button = document.createElement('button');
		button.type = 'button';
		button.className = 'icon-btn icon-btn--ghost';
		button.title = title;
		button.appendChild(materialIcon(icon));
		return button;
	}

	private unifiedGripButton(): HTMLButtonElement {
		const grip = document.createElement('button');
		grip.type = 'button';
		grip.className = 'up-grip';
		grip.title = 'Panel drag handle';
		grip.setAttribute('aria-label', 'Panel drag handle');
		grip.addEventListener('pointerdown', event => this.beginPanelDrag(event, this.workspacePanelEl));
		const dots = document.createElement('span');
		dots.className = 'up-grip__dots';
		for (let i = 0; i < 6; ++i) {
			dots.appendChild(document.createElement('span'));
		}
		grip.appendChild(dots);
		return grip;
	}

	private bindPanelGrip(grip: HTMLButtonElement, panel: HTMLElement): void {
		grip.addEventListener('pointerdown', event => this.beginPanelDrag(event, panel));
	}

	// Panel move — drag a floating panel (unified search / side
	// workspace) to reposition it. Refactored onto the shared DnD
	// controller (ui/dnd.ts) in D1: MOVE mode (no ghost, no drop
	// targets), with `escCancels: false` so ESC keeps its existing
	// meaning (drawer toggle / panel close) during a move. Behavior is
	// identical to the pre-D1 hand-rolled loop, including the R2-aware
	// width-freeze gating in onStart.
	private beginPanelDrag(event: PointerEvent, panel: HTMLElement | null): void {
		if (!panel) {
			return;
		}
		const startRect = panel.getBoundingClientRect();
		const maxLeft = Math.max(0, window.innerWidth - startRect.width);
		const maxTop = Math.max(0, window.innerHeight - startRect.height);
		const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

		beginDrag(event, panel, {
			escCancels: false,
			onStart: () => {
				panel.classList.add('is-dragging');
				panel.style.left = `${startRect.left}px`;
				panel.style.top = `${startRect.top}px`;
				panel.style.right = 'auto';
				panel.style.bottom = 'auto';
				panel.style.transform = 'none';
				if (panel.id === 'lb-project-workspace-panel') {
					// R2: leave the side panel's WIDTH under
					// --lb-side-panel-width control (its width is
					// position-independent, so freezing it inline is
					// redundant) so the left-edge resize keeps working after
					// a move. An inline width here would override the CSS
					// variable and make resize/reset silently no-op while
					// floated. Height is still frozen to avoid vertical
					// reflow.
					panel.style.height = `${startRect.height}px`;
				} else {
					panel.style.width = `${startRect.width}px`;
				}
			},
			onMove: ctx => {
				panel.style.left = `${clamp(startRect.left + ctx.deltaX, 0, maxLeft)}px`;
				panel.style.top = `${clamp(startRect.top + ctx.deltaY, 0, maxTop)}px`;
			},
			onEnd: () => {
				panel.classList.remove('is-dragging');
			},
		});
	}

	private unifiedCloseButton(title: string): HTMLButtonElement {
		const close = document.createElement('button');
		close.type = 'button';
		close.className = 'icon-btn lb-project-panel-close-btn';
		close.title = title;
		close.setAttribute('aria-label', title);
		close.appendChild(materialIcon('close'));
		close.addEventListener('click', () => this.closeUnifiedWorkspacePanel());
		return close;
	}

	private filterToUnifiedEditorDraft(filter: IFilter): IFilterEditorDraft {
		const groups: Array<IFilterEditorGroupDraft> = [];
		const directConditions: Array<IFilterEditorConditionDraft> = [];
		if (filter.expression && !this.isOwnerSearchSelectionFilter(filter) && !this.isUnifiedEditorGroupFilter(filter)) {
			directConditions.push(this.conditionDraftFromFilter(filter));
		}
		for (const child of filter.children || []) {
			if (this.isUnifiedEditorGroupFilter(child)) {
				groups.push({
					conditions: (child.children || [])
						.filter(grandchild => Boolean(grandchild.expression))
						.map(grandchild => this.conditionDraftFromFilter(grandchild)),
					logic: this.filterGroupLogic(child),
				});
			} else if (child.expression) {
				directConditions.push(this.conditionDraftFromFilter(child));
			}
		}
		if (directConditions.length > 0) {
			groups.unshift({
				conditions: directConditions,
				logic: this.isOwnerSearchSelectionFilter(filter) ? FILTER_GROUP_LOGIC_OR : FILTER_GROUP_LOGIC_AND,
			});
		}
		if (this.filterLooksLikeOwnerSearch(filter)) {
			return {filterId: filter.id, groups: this.ownerSearchEditorGroups(groups)};
		}
		if (!groups.length) {
			groups.push(this.emptyUnifiedEditorGroup());
		}
		return {filterId: filter.id, groups};
	}

	private filterLooksLikeOwnerSearch(filter: IFilter): boolean {
		return Boolean(
			this.isOwnerSearchSelectionFilter(filter)
			|| /^Owner Search:/i.test(filter.label || ''));
	}

	private ownerSearchEditorGroups(groups: Array<IFilterEditorGroupDraft>): Array<IFilterEditorGroupDraft> {
		const ownerConditions: Array<IFilterEditorConditionDraft> = [];
		const refinements: Array<IFilterEditorGroupDraft> = [];
		for (const group of groups) {
			let groupHadOwnerConditions = false;
			const nonOwnerConditions: Array<IFilterEditorConditionDraft> = [];
			for (const condition of group.conditions) {
				if (!this.isCompleteUnifiedEditorCondition(condition)) {
					continue;
				}
				if (condition.lhs === 'parcel__owner_uid') {
					groupHadOwnerConditions = true;
					ownerConditions.push(condition);
				} else {
					nonOwnerConditions.push(condition);
				}
			}
			if (nonOwnerConditions.length > 0) {
				refinements.push({
					conditions: nonOwnerConditions,
					logic: group.logic === FILTER_GROUP_LOGIC_OR && !groupHadOwnerConditions ?
						FILTER_GROUP_LOGIC_OR :
						FILTER_GROUP_LOGIC_AND,
				});
			}
		}
		return [
			{
				conditions: ownerConditions,
				logic: FILTER_GROUP_LOGIC_OR,
				ownerSelectionGroup: true,
			},
			...refinements,
		];
	}

	private unifiedEditorDraftCanSave(filter: IFilter, draft: IFilterEditorDraft): boolean {
		if (!this.filterLooksLikeOwnerSearch(filter)) {
			return true;
		}
		const ownerGroup = draft.groups.find(group => Boolean(group.ownerSelectionGroup));
		return Boolean(ownerGroup && ownerGroup.conditions.some(condition => (
			condition.lhs === 'parcel__owner_uid'
			&& this.isCompleteUnifiedEditorCondition(condition)
		)));
	}

	private isCompleteUnifiedEditorCondition(condition: IFilterEditorConditionDraft): boolean {
		return Boolean(condition.lhs && condition.operator && condition.rhs);
	}

	private isUnifiedEditorGroupFilter(filter: IFilter): boolean {
		const expr = filter.expression;
		return Boolean(
			expr
			&& expr.lhs === FILTER_GROUP_EXPRESSION_LHS
			&& expr.operator === FILTER_GROUP_EXPRESSION_OPERATOR);
	}

	private filterGroupLogic(filter: IFilter): FilterEditorGroupLogic {
		return filter.expression && filter.expression.rhs === FILTER_GROUP_LOGIC_OR ?
			FILTER_GROUP_LOGIC_OR :
			FILTER_GROUP_LOGIC_AND;
	}

	private isOwnerSearchSelectionFilter(filter: IFilter): boolean {
		return Boolean(
			filter.expression
			&& filter.expression.lhs === 'parcel__owner_search_selection'
			&& filter.expression.operator === 'exact');
	}

	private conditionDraftFromFilter(filter: IFilter): IFilterEditorConditionDraft {
		const expr = filter.expression || {lhs: '', operator: '', rhs: ''};
		const label = this.displayValueForConditionFilter(filter);
		return {
			displayValue: label || expr.rhs,
			enabled: filter.enabled,
			lhs: expr.lhs,
			operator: expr.operator,
			rhs: expr.rhs,
		};
	}

	private displayValueForConditionFilter(filter: IFilter): string {
		if (!filter.expression || filter.expression.lhs !== 'parcel__owner_uid') {
			return '';
		}
		return this.cleanOwnerConditionDisplayValue(filter.label);
	}

	private cleanOwnerConditionDisplayValue(value: string): string {
		let cleaned = value
			.replace(/\s+\([\d,]+\s+parcels?\)$/i, '')
			.trim();
		let previous = '';
		while (previous !== cleaned) {
			previous = cleaned;
			cleaned = cleaned
				.replace(/^(Owner\s+(?:name|ID)|Owner ID)\s*(?:equals|does not equal|=|!=)\s*/i, '')
				.trim();
		}
		return cleaned;
	}

	private defaultUnifiedEditorGroup(logic: FilterEditorGroupLogic = FILTER_GROUP_LOGIC_AND): IFilterEditorGroupDraft {
		return {
			conditions: [this.defaultUnifiedEditorCondition()],
			logic,
		};
	}

	private emptyUnifiedEditorGroup(logic: FilterEditorGroupLogic = FILTER_GROUP_LOGIC_AND): IFilterEditorGroupDraft {
		return {
			conditions: [],
			logic,
		};
	}

	private defaultUnifiedEditorCondition(): IFilterEditorConditionDraft {
		const attr = this.filterEditorAttributes()[0];
		const lhs = attr ? attr.value : '';
		const condition = {
			displayValue: '',
			enabled: true,
			lhs,
			operator: this.defaultOperatorForField(lhs),
			rhs: '',
		};
		this.applyDefaultValueForField(condition);
		return condition;
	}

	private filterEditorAttributes(): Array<IExpressionAttribute> {
		const token = this.filterMgr.expressionTokenForUnifiedEditor();
		return (token.attributes || []).filter(attr => Boolean(attr.value));
	}

	private filterEditorOperatorsForField(lhs: string): Array<IExpressionOperator> {
		const token = this.filterMgr.expressionTokenForUnifiedEditor();
		const attr = (token.attributes || []).find(obj => obj.value === lhs);
		const allowed = attr ? attr.operators : [];
		return (token.operators || []).filter(op => Boolean(op.value) && allowed.indexOf(op.value) >= 0);
	}

	private filterEditorChoicesForField(lhs: string): Array<IExpressionAttributeChoice> {
		const token = this.filterMgr.expressionTokenForUnifiedEditor();
		const attr = (token.attributes || []).find(obj => obj.value === lhs);
		return attr ? (attr.choices || []) : [];
	}

	private defaultOperatorForField(lhs: string): string {
		const operators = this.filterEditorOperatorsForField(lhs);
		const contains = operators.find(op => op.value === 'contains');
		if (contains) {
			return contains.value;
		}
		const exact = operators.find(op => op.value === 'exact');
		return exact ? exact.value : (operators[0] ? operators[0].value : '');
	}

	private filterEditorFieldLabel(lhs: string): string {
		switch (lhs) {
			case 'parcel__structures_value':
				return 'Unsupported Condition: Structures Value';
			case 'parcel__owner_01':
				return 'Owner name';
			case 'parcel__owner_uid':
				// The internal owner id (search-backed), NOT a raw-filterable name —
				// see SELECTION_BACKED_TOKENS. Owner-name filtering uses owner_01.
				return 'Owner ID';
			case 'parcel__owner_address_uid':
				return 'Owner mailing address ID';
			default:
				return this.filterMgr.expressionTokenForUnifiedEditor().attributes.find(attr => attr.value === lhs)?.label || lhs;
		}
	}

	private filterEditorConditionUsesUnsupportedField(condition: IFilterEditorConditionDraft): boolean {
		if (!condition.lhs) {
			return false;
		}
		return !this.filterMgr.expressionTokenForUnifiedEditor().attributes.some(attr => attr.value === condition.lhs);
	}

	private filterEditorOperatorLabel(operator: string): string {
		switch (operator) {
			case 'exact':
				return 'equals';
			case 'ne':
				return 'does not equal';
			case 'gt':
				return 'greater than';
			case 'gte':
				return 'at least';
			case 'lt':
				return 'less than';
			case 'lte':
				return 'at most';
			case 'contains':
				return 'contains';
			default:
				return this.filterMgr.expressionTokenForUnifiedEditor().operators.find(op => op.value === operator)?.label || operator;
		}
	}

	private renderUnifiedFilterRenameField(parent: HTMLElement, filter: IFilter): void {
		if (this.unifiedEditorRenameActive) {
			const input = document.createElement('input');
			input.className = 'rename-input chip__label chip__label--rename';
			input.value = this.unifiedEditorRenameDraft;
			input.setAttribute('aria-label', 'Filter label');
			input.addEventListener('click', event => event.stopPropagation());
			input.addEventListener('input', () => {
				this.unifiedEditorRenameDraft = input.value;
			});
			input.addEventListener('keydown', event => {
				event.stopPropagation();
				if (event.key === 'Enter') {
					event.preventDefault();
					this.commitUnifiedEditorRename(filter);
				} else if (event.key === 'Escape') {
					event.preventDefault();
					this.unifiedEditorRenameActive = false;
					this.unifiedEditorRenameDraft = this.filterDisplayLabel(filter);
					this.renderUnifiedWorkspacePanel();
				}
			});
			input.addEventListener('blur', () => this.commitUnifiedEditorRename(filter));
			parent.appendChild(input);
			window.setTimeout(() => {
				input.focus();
				input.select();
			}, 0);
			return;
		}
		const label = document.createElement('span');
		label.className = 'rename-display chip__label chip__label--rename';
		label.title = 'Click to rename';
		label.textContent = this.filterDisplayLabel(filter);
		label.addEventListener('click', event => {
			event.stopPropagation();
			this.unifiedEditorRenameActive = true;
			this.unifiedEditorRenameDraft = this.filterDisplayLabel(filter);
			this.renderUnifiedWorkspacePanel();
		});
		label.appendChild(materialIcon('edit', 'rename-pencil material-icons'));
		parent.appendChild(label);
	}

	private async commitUnifiedEditorRename(filter: IFilter, render: boolean = true): Promise<void> {
		const next = this.unifiedEditorRenameDraft.trim();
		this.unifiedEditorRenameActive = false;
		if (next && next !== this.filterDisplayLabel(filter)) {
			await this.filterMgr.renameFilter(filter.id, next);
			this.unifiedEditorRenameDraft = next;
			this.unifiedEditingFilter = {...filter, label: next};
			if (render) {
				this.renderUnifiedWorkspacePanel();
			}
		} else {
			this.unifiedEditorRenameDraft = this.filterDisplayLabel(filter);
			if (render) {
				this.renderUnifiedWorkspacePanel();
			}
		}
	}

	private filterDisplayLabel(filter: IFilter): string {
		return filter.label.trim().length > 0 ?
			filter.label :
			'Unlabeled Filter';
	}

	private filterEditorIcon(filter: IFilter): string {
		switch (filter.icon) {
			case 'owner':
				return 'person';
			case 'shape':
				return 'polyline';
			case 'place':
			default:
				return 'place';
		}
	}

	private renderUnifiedPanelBody(body: HTMLElement): void {
		if (!this.unifiedPanelQuery.trim()) {
			this.renderUnifiedRecentSearches(body);
			return;
		}
		const meta = document.createElement('div');
		meta.className = 'results-meta';
		appendTextSpan(meta, 'Search');
		const status = document.createElement('strong');
		status.textContent = this.unifiedSearchLoading ?
			'Searching...' :
			this.unifiedSearchStatusText();
		meta.appendChild(status);
		body.appendChild(meta);
		if (this.unifiedSearchError) {
			const error = document.createElement('div');
			error.className = 'result result--muted result--error';
			error.appendChild(materialIcon('error', 'result__icon'));
			const copy = document.createElement('div');
			copy.className = 'result__body';
			appendTextDiv(copy, 'Search failed', 'result__title');
			appendTextDiv(copy, this.unifiedSearchError, 'result__sub');
			error.appendChild(copy);
			body.appendChild(error);
			return;
		}
		this.renderUnifiedCategorizedResults(body);
	}

	private renderUnifiedRecentSearches(body: HTMLElement): void {
		const meta = document.createElement('div');
		meta.className = 'results-meta';
		appendTextSpan(meta, 'Recent searches');
		const clear = document.createElement('button');
		clear.type = 'button';
		clear.className = 'btn-link';
		clear.textContent = 'Clear';
		clear.disabled = true;
		meta.appendChild(clear);
		body.appendChild(meta);
		for (const item of this.unifiedRecentSearches()) {
			const row = document.createElement('button');
			row.type = 'button';
			row.className = 'result result--button';
			row.appendChild(materialIcon(item.icon, 'result__icon'));
			const copy = document.createElement('div');
			copy.className = 'result__body';
			appendTextDiv(copy, item.title, 'result__title');
			appendTextDiv(copy, item.meta, 'result__sub');
			row.appendChild(copy);
			row.addEventListener('click', () => {
				this.setUnifiedQuery(item.title.replace(/^(Owner:|Place:|Address:)\s*/i, ''));
			});
			body.appendChild(row);
		}
	}

	private renderUnifiedCategorizedResults(body: HTMLElement): void {
		const addresses = this.unifiedResults.addresses;
		const owners = this.unifiedResults.owners;
		const places = this.unifiedResults.places;
		const isAddressShape = this.unifiedResults.queryShape === 'address';
		const haveAny = addresses.length + owners.length + places.length > 0;
		if (!haveAny && !this.unifiedSearchLoading) {
			body.appendChild(this.unifiedEmptyResult(
				isAddressShape ? 'home_pin' : 'search_off',
				'No matches',
				isAddressShape
					? 'No physical addresses, owner groups, or places matched within this project scope.'
					: 'No owner groups or places matched. For an address, start the query with a number ("123 Main").',
			));
			return;
		}
		if (addresses.length) {
			// Phase A + scope-bypass: address rows can be `match_scope`
			// `'global'` (geocoder fallback, runs against ALL active
			// parcels regardless of current filter state) or
			// `'project'` (direct/trigram tier hit within the project's
			// filter scope). Adjust the header hint so users see when
			// a match was found outside their current filter scope —
			// useful both for fresh no-filter projects AND for projects
			// where the typed address sits outside the current filter
			// set (e.g. "1405 Avenel" while only "30 Station Rd" is
			// filtered).
			const anyGlobal = addresses.some(row => row.matchScope === 'global');
			const addressHint = anyGlobal
				? 'Match found across all counties. Select to add as filter.'
				: 'Select one or more to create an address-based filter.';
			body.appendChild(this.unifiedCategoryHeader(
				'Physical addresses',
				addresses.length,
				addressHint,
			));
			for (const row of addresses) {
				body.appendChild(this.renderUnifiedAddressRow(row));
			}
		}
		if (owners.length) {
			body.appendChild(this.unifiedCategoryHeader(
				'Owners',
				owners.length,
				'Select one or more to create one grouped owner filter.',
			));
			for (const row of owners) {
				body.appendChild(this.renderUnifiedOwnerButton(row));
			}
		}
		if (places.length) {
			body.appendChild(this.unifiedCategoryHeader(
				'Places',
				places.length,
				'Select one to create a place/boundary filter.',
			));
			for (const place of places) {
				body.appendChild(this.renderUnifiedPlaceButton(place));
			}
		}
	}

	private unifiedCategoryHeader(title: string, count: number, hint: string): HTMLElement {
		const header = document.createElement('div');
		header.className = 'results-meta results-meta--category';
		appendTextSpan(header, title);
		const badge = document.createElement('strong');
		badge.textContent = `${numberFormat(count)} ${count === 1 ? 'result' : 'results'}`;
		header.appendChild(badge);
		const hintEl = document.createElement('span');
		hintEl.className = 'results-meta__hint';
		hintEl.textContent = hint;
		header.appendChild(hintEl);
		return header;
	}

	private renderUnifiedOwnerButton(row: IOwnerSearchRow): HTMLElement {
		const key = this.ownerSearchRowKey(row);
		const selected = this.unifiedSelectedOwnerKeys.has(key);
		const button = document.createElement('button');
		button.type = 'button';
		button.className = `result result--button${selected ? ' is-selected' : ''}`;
		const check = document.createElement('span');
		check.className = 'result__checkbox';
		check.textContent = selected ? 'check' : '';
		check.setAttribute('aria-hidden', 'true');
		button.appendChild(check);
		const copy = document.createElement('div');
		copy.className = 'result__body';
		appendTextDiv(copy, safeString(row.owner) || 'Unknown owner', 'result__title');
		appendTextDiv(copy, this.ownerSearchAddressLine(row), 'result__sub');
		appendTextDiv(copy, this.ownerSearchMetaLine(row), 'result__meta');
		button.appendChild(copy);
		// D2b: drag this row onto the Pinned tab to pin the owner. Grip is
		// a <span> (not <button>) — this row is itself a <button>, so a
		// nested button would be invalid HTML.
		button.appendChild(this.makeDragGrip('Drag to pin',
			ev => this.beginUnifiedRowDragToPin(ev, button, () => {
				this.pinSingleOwnerRow(row).catch(
					exc => logger.error('renderUnifiedOwnerButton: pin failed', exc));
			}),
			{tag: 'span'}));
		button.addEventListener('click', () => {
			if (this.unifiedSelectedOwnerKeys.has(key)) {
				this.unifiedSelectedOwnerKeys.delete(key);
			} else {
				this.unifiedSelectedOwnerKeys.add(key);
			}
			this.renderUnifiedWorkspacePanel({preserveResultsScroll: true});
			this.focusUnifiedPanelInput();
		});
		return button;
	}

	private addressRowKey(row: IUnifiedAddressRow): string {
		const samples = Array.isArray(row.samples) ? row.samples : [];
		const firstUid = samples.length ? safeString(samples[0].parcelUid) : '';
		// Stable key per (parcels-in-row + matchKind) so direct,
		// direct_fuzzy, and geocoded rows pointing at overlapping
		// parcels don't collide in the selection set.
		let kind: string;
		switch (row.matchKind) {
			case 'geocoded':
				kind = 'g';
				break;
			case 'direct_fuzzy':
				kind = 'f';
				break;
			default:
				kind = 'd';
		}
		return firstUid ? `${kind}|${firstUid}` : `${kind}|${safeString(row.street)}|${safeString(row.city)}`;
	}

	private addressRowSelectedParcelUids(row: IUnifiedAddressRow): Array<string> {
		const samples = Array.isArray(row.samples) ? row.samples : [];
		const uids: Array<string> = [];
		for (const sample of samples) {
			const uid = safeString(sample.parcelUid);
			if (uid && uids.indexOf(uid) < 0) {
				uids.push(uid);
			}
		}
		return uids;
	}

	private addressRowTitle(row: IUnifiedAddressRow): string {
		// Title priority:
		//   1. The parcel's own street (when populated — authoritative
		//      for direct/trigram matches AND for geocoded matches
		//      against counties whose importer fills the columns).
		//   2. Mapbox's resolved street for geocoded rows (the
		//      Walton-style empty-column case — shows the matched
		//      address from the geocoder rather than a possibly
		//      partial query echo).
		//   3. The user's typed query (last-resort fallback for
		//      geocoded rows when Mapbox didn't return structured
		//      address context — e.g. Census-fallback matches).
		//   4. "Address unavailable" as the final fallback.
		const street = safeString(row.street);
		if (street) {
			return street;
		}
		if (row.matchKind === 'geocoded') {
			const geocodedStreet = safeString(row.geocodedStreet);
			if (geocodedStreet) {
				return geocodedStreet;
			}
			const echo = safeString(row.queryEcho);
			if (echo) {
				return echo;
			}
		}
		return 'Address unavailable';
	}

	private addressRowLocale(row: IUnifiedAddressRow): string {
		// Locale line priority:
		//   1. The parcel's own city/state/zip (when ANY are populated).
		//   2. Mapbox's resolved city/state/zip for geocoded rows when
		//      the parcel's are all blank (Walton case).
		const parcelLocale = [safeString(row.city), safeString(row.state), safeString(row.zip)]
			.filter(Boolean)
			.join(' | ');
		if (parcelLocale) {
			return parcelLocale;
		}
		if (row.matchKind === 'geocoded') {
			return [safeString(row.geocodedCity), safeString(row.geocodedState), safeString(row.geocodedZip)]
				.filter(Boolean)
				.join(' | ');
		}
		return '';
	}

	private renderUnifiedAddressRow(row: IUnifiedAddressRow): HTMLElement {
		const key = this.addressRowKey(row);
		const selected = this.unifiedSelectedAddressKeys.has(key);
		// Phase 3c: row is `<div role="button">` (was `<button>`) so we
		// can nest the parcel link-in + fly-to controls without
		// invalid-HTML interactive-inside-button nesting. Click + Enter
		// + Space all toggle selection; the inline parcel actions
		// stopPropagation so they don't trigger the toggle.
		const rowEl = document.createElement('div');
		rowEl.className = `result result--button result--address${selected ? ' is-selected' : ''}`;
		rowEl.setAttribute('role', 'button');
		rowEl.setAttribute('tabindex', '0');
		rowEl.setAttribute('aria-pressed', selected ? 'true' : 'false');
		const check = document.createElement('span');
		check.className = 'result__checkbox';
		check.textContent = selected ? 'check' : '';
		check.setAttribute('aria-hidden', 'true');
		rowEl.appendChild(check);
		const copy = document.createElement('div');
		copy.className = 'result__body';
		const titleEl = document.createElement('div');
		titleEl.className = 'result__title';
		titleEl.textContent = this.addressRowTitle(row);
		if (row.matchKind === 'geocoded') {
			const badge = document.createElement('span');
			badge.className = 'result__badge result__badge--geocoded';
			const provider = safeString(row.geocoderProvider);
			badge.textContent = provider ? `Matched via geocoder (${provider})` : 'Matched via geocoder';
			titleEl.appendChild(document.createTextNode(' '));
			titleEl.appendChild(badge);
		} else if (row.matchKind === 'direct_fuzzy') {
			const badge = document.createElement('span');
			badge.className = 'result__badge result__badge--fuzzy';
			const echo = safeString(row.queryEcho);
			badge.textContent = echo
				? `Fuzzy match for "${echo}"`
				: 'Fuzzy match';
			titleEl.appendChild(document.createTextNode(' '));
			titleEl.appendChild(badge);
		}
		copy.appendChild(titleEl);
		const locale = this.addressRowLocale(row);
		if (locale) {
			appendTextDiv(copy, locale, 'result__sub');
		}
		appendTextDiv(copy, this.ownerSearchMetaLine(row as IOwnerSearchRow), 'result__meta');
		rowEl.appendChild(copy);
		// Phase 3c parcel link-in + fly-to. Only surface when the row
		// resolves to exactly one parcel — multi-parcel address rows
		// would be ambiguous to link to a single parcel detail page,
		// and the existing per-result selection behavior is the right
		// flow for those.
		const singleParcelUid = this.uniqueAddressRowParcelUid(row);
		if (singleParcelUid) {
			const actions = document.createElement('div');
			actions.className = 'result__inline-actions';
			const flyTo = document.createElement('span');
			flyTo.className = 'result__inline-icon material-icons';
			flyTo.setAttribute('role', 'button');
			flyTo.setAttribute('tabindex', '0');
			flyTo.setAttribute('aria-label', 'Show parcel on map');
			flyTo.setAttribute('title', 'Show on map');
			flyTo.textContent = 'my_location';
			const flyHandler = (evt: Event) => {
				evt.preventDefault();
				evt.stopPropagation();
				this.flyToParcel({uid: singleParcelUid});
			};
			flyTo.addEventListener('click', flyHandler);
			flyTo.addEventListener('keydown', evt => {
				const ke = evt as KeyboardEvent;
				if (ke.key === 'Enter' || ke.key === ' ') {
					flyHandler(evt);
				}
			});
			actions.appendChild(flyTo);
			const openLink = document.createElement('a');
			openLink.className = 'result__inline-link';
			openLink.href = `/parcels/${encodeURIComponent(singleParcelUid)}/`;
			openLink.setAttribute('aria-label', 'Open parcel detail');
			openLink.setAttribute('title', 'Open parcel detail');
			openLink.textContent = 'Open ↗';
			openLink.addEventListener('click', evt => {
				evt.stopPropagation();
			});
			actions.appendChild(openLink);
			rowEl.appendChild(actions);
		}
		// D2b: drag this row onto the Pinned tab to pin its parcel(s).
		rowEl.appendChild(this.makeDragGrip('Drag to pin',
			ev => this.beginUnifiedRowDragToPin(ev, rowEl, () => {
				this.pinSingleAddressRow(row).catch(
					exc => logger.error('renderUnifiedAddressRow: pin failed', exc));
			}),
			{tag: 'span'}));
		const toggle = () => {
			if (this.unifiedSelectedAddressKeys.has(key)) {
				this.unifiedSelectedAddressKeys.delete(key);
			} else {
				this.unifiedSelectedAddressKeys.add(key);
			}
			this.renderUnifiedWorkspacePanel({preserveResultsScroll: true});
			this.focusUnifiedPanelInput();
		};
		rowEl.addEventListener('click', toggle);
		rowEl.addEventListener('keydown', evt => {
			if (evt.key === 'Enter' || evt.key === ' ') {
				evt.preventDefault();
				toggle();
			}
		});
		return rowEl;
	}

	// Returns the single parcel uid this address row resolves to, or
	// null when the row carries zero or multiple parcels. Used to gate
	// the per-row "Open parcel" / fly-to inline actions — those only
	// make sense when there's an unambiguous target.
	private uniqueAddressRowParcelUid(row: IUnifiedAddressRow): string | null {
		const samples = row.samples;
		if (!samples || samples.length !== 1) {
			return null;
		}
		const uid = samples[0] && safeString(samples[0].parcelUid);
		return uid ? uid : null;
	}

	private renderUnifiedPlaceButton(place: IPlace): HTMLElement {
		const selected = this.unifiedSelectedPlaceKeys.has(place.uid);
		const button = document.createElement('button');
		button.type = 'button';
		button.className = `result result--button${selected ? ' is-selected' : ''}`;
		button.appendChild(materialIcon('place', 'result__icon'));
		const copy = document.createElement('div');
		copy.className = 'result__body';
		appendTextDiv(copy, place.name, 'result__title');
		appendTextDiv(copy, this.placeSearchMetaLine(place), 'result__sub');
		appendTextDiv(copy, 'Creates the same production place/boundary filter as autocomplete.', 'result__meta');
		button.appendChild(copy);
		button.addEventListener('click', () => {
			this.unifiedSelectedPlaceKeys.clear();
			this.unifiedSelectedAddressKeys.clear();
			this.unifiedSelectedPlaceKeys.add(place.uid);
			this.renderUnifiedWorkspacePanel({preserveResultsScroll: true});
			this.focusUnifiedPanelInput();
		});
		return button;
	}

	private unifiedEmptyResult(icon: string, title: string, subtitle: string): HTMLElement {
		const row = document.createElement('div');
		row.className = 'result result--muted';
		row.appendChild(materialIcon(icon, 'result__icon'));
		const copy = document.createElement('div');
		copy.className = 'result__body';
		appendTextDiv(copy, title, 'result__title');
		appendTextDiv(copy, subtitle, 'result__sub');
		row.appendChild(copy);
		return row;
	}

	private unifiedPanelFooter(message: string, actionsNode: HTMLElement): HTMLElement {
		const footer = document.createElement('div');
		footer.className = 'results-foot';
		const label = document.createElement('span');
		label.className = 'results-foot__sel';
		label.textContent = message;
		const actions = document.createElement('span');
		actions.className = 'results-foot__actions';
		actions.appendChild(actionsNode);
		footer.appendChild(label);
		footer.appendChild(actions);
		return footer;
	}

	private unifiedFooterActions(): HTMLElement {
		const group = document.createElement('span');
		group.className = 'results-foot__button-group';
		const ownerPinnable = this.unifiedSelectedOwnerKeys.size > 0;
		const addressPinnable = this.unifiedSelectedAddressKeys.size > 0;
		const pin = this.unifiedButton('Pin to workspace', 'btn-secondary');
		pin.disabled = !(ownerPinnable || addressPinnable);
		// Footer hint reflects what would actually get pinned. Owner
		// + address are both supported; they go to the same Pinned tab
		// (address selections pin one `parcel` workspace item per
		// parcel UID in the row's samples — see
		// pinUnifiedAddressSelectionToWorkspace).
		if (ownerPinnable && addressPinnable) {
			pin.title = 'Pin selected owner groups and address parcels to the workspace.';
		} else if (addressPinnable) {
			pin.title = 'Pin the parcel(s) from the selected address rows to the workspace.';
		} else {
			pin.title = 'Pin selected owner groups to the workspace without changing filters.';
		}
		pin.addEventListener('click', () => {
			const tasks: Array<Promise<void>> = [];
			if (ownerPinnable) {
				tasks.push(this.pinUnifiedOwnerSelectionToWorkspace());
			}
			if (addressPinnable) {
				tasks.push(this.pinUnifiedAddressSelectionToWorkspace());
			}
			Promise.all(tasks).catch(exc =>
				logger.error('unifiedFooterActions: Failed to pin selections', exc),
			);
		});
		const add = this.unifiedButton('Add as filter', 'btn-primary');
		const haveAny = (
			this.unifiedSelectedOwnerKeys.size > 0
			|| this.unifiedSelectedPlaceKeys.size === 1
			|| this.unifiedSelectedAddressKeys.size > 0
		);
		add.disabled = !haveAny;
		add.addEventListener('click', () => this.addUnifiedSelectionsToFilters());
		group.appendChild(pin);
		group.appendChild(add);
		return group;
	}

	private unifiedButton(label: string, className: string): HTMLButtonElement {
		const button = document.createElement('button');
		button.type = 'button';
		button.className = className;
		button.textContent = label;
		return button;
	}

	private async runWithButtonPending<T>(button: HTMLButtonElement, pendingLabel: string, task: () => Promise<T>): Promise<T> {
		const label = button.textContent || '';
		const wasDisabled = button.disabled;
		button.disabled = true;
		button.textContent = pendingLabel;
		button.setAttribute('aria-busy', 'true');
		button.dataset.pending = 'true';
		try {
			return await task();
		} finally {
			button.disabled = wasDisabled;
			button.textContent = label;
			button.removeAttribute('aria-busy');
			delete button.dataset.pending;
		}
	}

	private workspacePanelTitle(): string {
		return 'Search a place, owner, or address...';
	}

	private unifiedFooterMessage(): string {
		const owners = this.unifiedSelectedOwnerKeys.size;
		const places = this.unifiedSelectedPlaceKeys.size;
		const addresses = this.unifiedSelectedAddressKeys.size;
		const parts: Array<string> = [];
		if (owners > 0) {
			const parcels = this.selectedOwnerParcelCount();
			parts.push(`${numberFormat(owners)} owner ${owners === 1 ? 'group' : 'groups'}${parcels ? ` (${numberFormat(parcels)} parcels)` : ''}`);
		}
		if (places > 0) {
			parts.push('1 place');
		}
		if (addresses > 0) {
			const parcelCount = this.selectedAddressParcelCount();
			parts.push(`${numberFormat(addresses)} ${addresses === 1 ? 'address' : 'addresses'}${parcelCount ? ` (${numberFormat(parcelCount)} parcels)` : ''}`);
		}
		if (parts.length) {
			return `${parts.join(' + ')} selected — Add as filter creates them.`;
		}
		return 'Select owner groups, an address, or a place to create a filter.';
	}

	private selectedAddressParcelCount(): number {
		let total = 0;
		for (const key of this.unifiedSelectedAddressKeys) {
			const row = this.unifiedResults.addresses.find(obj => this.addressRowKey(obj) === key);
			if (row) {
				total += Number(row.parcelCount) || 0;
			}
		}
		return total;
	}

	private unifiedSearchStatusText(): string {
		const total = this.unifiedResults.addresses.length +
			this.unifiedResults.owners.length +
			this.unifiedResults.places.length;
		return `${numberFormat(total)} ${total === 1 ? 'result' : 'results'}`;
	}

	private setUnifiedQuery(query: string): void {
		this.unifiedPanelQuery = query;
		this.unifiedSelectedOwnerKeys.clear();
		this.unifiedSelectedPlaceKeys.clear();
		this.unifiedSelectedAddressKeys.clear();
		this.unifiedSearchError = '';
		this.unifiedSearchedQuery = '';
		this.unifiedResults = {addresses: [], owners: [], places: [], queryShape: 'freeform'};
		this.unifiedSearchLoading = false;
		this.clearUnifiedSearchTimer();
		this.renderUnifiedWorkspacePanel();
		this.focusUnifiedPanelInput();
		this.scheduleUnifiedSearch();
	}

	@bind
	private primaryInputFocusEvent(event: Event): void {
		const text = this.primaryInput ?
			this.primaryInput.text().trim() :
			'';
		this.openUnifiedWorkspacePanel({
			focusInput: true,
			...(text ? {query: text} : {}),
		});
		event.preventDefault();
	}

	private unifiedPanelInputKeyDownEvent(event: KeyboardEvent): void {
		switch (event.key) {
			case 'Escape':
				event.preventDefault();
				this.closeUnifiedWorkspacePanel();
				break;
			case 'Enter':
				event.preventDefault();
				if (
					this.unifiedSelectedOwnerKeys.size > 0
					|| this.unifiedSelectedPlaceKeys.size === 1
					|| this.unifiedSelectedAddressKeys.size > 0
				) {
					this.addUnifiedSelectionsToFilters();
				} else if (this.unifiedResults.places.length === 1
						&& this.unifiedResults.owners.length === 0) {
					this.unifiedSelectedPlaceKeys.add(this.unifiedResults.places[0].uid);
					this.addUnifiedSelectionsToFilters();
				} else if (this.unifiedPanelQuery.trim().length >= 2) {
					this.runUnifiedSearch(++this.unifiedSearchSeq);
				}
				break;
		}
	}

	private focusUnifiedPanelInput(): void {
		if (!this.workspacePanelOpen || !this.unifiedPanelInputEl) {
			return;
		}
		// On a fresh ⌘K open the panel is mid-transition: its `visibility`
		// is part of the CSS transition, so it stays `hidden` for the
		// animation's duration and `.focus()` no-ops on a non-focusable
		// element (the cursor never lands in the input). Retry on each
		// animation frame until the focus actually takes (i.e. the panel
		// has become visible), capped so a never-visible panel can't spin.
		// When the panel is already open (result-row re-renders, etc.) the
		// first attempt succeeds and no retry happens.
		const input = this.unifiedPanelInputEl;
		let attempts = 0;
		const tryFocus = (): void => {
			if (!this.workspacePanelOpen || this.unifiedPanelInputEl !== input) {
				return;
			}
			input.focus();
			if (document.activeElement === input) {
				const end = input.value.length;
				input.setSelectionRange(end, end);
				return;
			}
			if (++attempts < 20) {
				window.requestAnimationFrame(tryFocus);
			}
		};
		tryFocus();
	}

	private syncUnifiedLauncherState(): void {
		const root = document.getElementById('id_lb-project-detail-primary-input');
		if (root) {
			root.classList.toggle('is-search-panel-open', this.workspacePanelOpen);
		}
		if (this.primaryInput) {
			this.primaryInput.setPlaceHolder(this.workspacePanelOpen ?
				'Search panel open below' :
				'Search a place, owner, or draw an area...');
		}
	}

	private clearUnifiedSearchTimer(): void {
		if (this.unifiedSearchTimer !== null) {
			window.clearTimeout(this.unifiedSearchTimer);
			this.unifiedSearchTimer = null;
		}
	}

	private scheduleUnifiedSearch(): void {
		this.clearUnifiedSearchTimer();
		const seq = ++this.unifiedSearchSeq;
		const query = this.unifiedPanelQuery.trim();
		if (query.length < 2) {
			this.unifiedSearchLoading = false;
			this.unifiedSearchError = '';
			this.unifiedResults = {addresses: [], owners: [], places: [], queryShape: 'freeform'};
			this.unifiedSearchedQuery = '';
			return;
		}
		this.unifiedSearchTimer = window.setTimeout(() => {
			this.unifiedSearchTimer = null;
			this.runUnifiedSearch(seq);
		}, 180);
	}

	private async runUnifiedSearch(seq: number): Promise<void> {
		const query = this.unifiedPanelQuery.trim();
		if (!query) {
			return;
		}
		this.unifiedSearchLoading = true;
		this.unifiedSearchError = '';
		this.renderUnifiedWorkspacePanel();
		this.focusUnifiedPanelInput();
		try {
			const response = await this.fetchUnifiedResults(query);
			if (seq !== this.unifiedSearchSeq) {
				return;
			}
			this.unifiedResults = {
				addresses: Array.isArray(response.addresses) ? response.addresses : [],
				owners: Array.isArray(response.owners) ? response.owners : [],
				places: Array.isArray(response.places) ? response.places : [],
				queryShape: (response.meta && response.meta.queryShape) || 'freeform',
			};
			this.unifiedSearchedQuery = query;
		} catch (exc) {
			if (seq !== this.unifiedSearchSeq) {
				return;
			}
			this.unifiedSearchError = String(exc);
			this.unifiedResults = {addresses: [], owners: [], places: [], queryShape: 'freeform'};
		} finally {
			if (seq === this.unifiedSearchSeq) {
				this.unifiedSearchLoading = false;
				this.renderUnifiedWorkspacePanel();
				this.focusUnifiedPanelInput();
			}
		}
	}

	private async fetchUnifiedResults(query: string): Promise<IUnifiedSearchResponse> {
		const response = await fetch(
			`/api/projects/${encodeURIComponent(this.slug)}/unified-search/?q=${encodeURIComponent(query)}&limit=40`,
			{
				credentials: 'same-origin',
				headers: {'Accept': 'application/json'},
				method: 'GET',
			});
		if (!response.ok) {
			throw new Error(`HTTP ${response.status}`);
		}
		return await response.json();
	}

	private async addUnifiedSelectionsToFilters(): Promise<void> {
		const ownerSelections: Array<{addressUid: string; ownerUid: string; parcelUid: string;}> = [];
		for (const key of this.unifiedSelectedOwnerKeys) {
			const row = this.unifiedResults.owners.find(obj => this.ownerSearchRowKey(obj) === key);
			if (row) {
				ownerSelections.push({
					addressUid: safeString(row.addressUid),
					ownerUid: safeString(row.ownerUid),
					parcelUid: safeString(row.parcelUid),
				});
			}
		}
		// Phase 3: collect parcel UIDs from selected address rows. Each
		// row contributes its `samples[*].parcelUid` set, deduped across
		// rows.
		const addressParcelUids: Array<string> = [];
		const addressDedupe = new Set<string>();
		for (const key of this.unifiedSelectedAddressKeys) {
			const row = this.unifiedResults.addresses.find(obj => this.addressRowKey(obj) === key);
			if (!row) {
				continue;
			}
			for (const uid of this.addressRowSelectedParcelUids(row)) {
				if (!addressDedupe.has(uid)) {
					addressDedupe.add(uid);
					addressParcelUids.push(uid);
				}
			}
		}
		const placeUid = this.unifiedSelectedPlaceKeys.size === 1 ?
			Array.from(this.unifiedSelectedPlaceKeys)[0] :
			'';
		const place = placeUid ?
			this.unifiedResults.places.find(obj => obj.uid === placeUid) || null :
			null;
		if (!ownerSelections.length && !place && !addressParcelUids.length) {
			return;
		}
		this.beginPendingFilterCreateFeedback();
		this.unifiedSearchLoading = true;
		this.renderUnifiedWorkspacePanel();
		try {
			if (ownerSelections.length) {
				const response = await fetch(`/api/projects/${encodeURIComponent(this.slug)}/owner-search/`, {
					body: JSON.stringify({action: 'add_filter', selections: ownerSelections}),
					credentials: 'same-origin',
					headers: {
						'Accept': 'application/json',
						'Content-Type': 'application/json',
						'X-CSRFToken': cookieValue('csrftoken'),
					},
					method: 'POST',
				});
				if (!response.ok) {
					let errorText = '';
					try {
						const payload = await response.json();
						errorText = safeString(payload.error || payload.detail || JSON.stringify(payload));
					} catch (parseErr) {
						errorText = '';
					}
					throw new Error(`HTTP ${response.status}${errorText ? ` - ${errorText}` : ''}`);
				}
				const data = await response.json();
				window.dispatchEvent(new CustomEvent('lb:project-filter-created', {
					detail: {filters: data.filters || []},
				}));
			}
			if (addressParcelUids.length) {
				const response = await fetch(`/api/projects/${encodeURIComponent(this.slug)}/address-search/`, {
					body: JSON.stringify({
						action: 'add_filter',
						query: this.unifiedPanelQuery,
						selections: addressParcelUids.map(uid => ({parcelUid: uid})),
					}),
					credentials: 'same-origin',
					headers: {
						'Accept': 'application/json',
						'Content-Type': 'application/json',
						'X-CSRFToken': cookieValue('csrftoken'),
					},
					method: 'POST',
				});
				if (!response.ok) {
					let errorText = '';
					try {
						const payload = await response.json();
						errorText = safeString(payload.error || payload.detail || JSON.stringify(payload));
					} catch (parseErr) {
						errorText = '';
					}
					throw new Error(`HTTP ${response.status}${errorText ? ` - ${errorText}` : ''}`);
				}
				const data = await response.json();
				window.dispatchEvent(new CustomEvent('lb:project-filter-created', {
					detail: {filters: data.filters || []},
				}));
			}
			if (place) {
				this.setAutoCompleteEnabled(false);
				this.setPrimaryInputText('');
				this.placeAutoCompleteActivated(place);
			}
			this.unifiedSelectedOwnerKeys.clear();
			this.unifiedSelectedPlaceKeys.clear();
			this.unifiedSelectedAddressKeys.clear();
			this.unifiedPanelQuery = '';
			this.closeUnifiedWorkspacePanel();
		} catch (exc) {
			this.unifiedSearchError = `Add to Filters failed: ${String(exc)}`;
		} finally {
			this.unifiedSearchLoading = false;
			this.renderUnifiedWorkspacePanel();
		}
	}

	private unifiedRecentSearches(): Array<IUnifiedRecentSearch> {
		return [
			{icon: 'place', meta: 'Place | county, boundary, ZIP, neighborhood', title: 'Pender County'},
			{icon: 'person_search', meta: 'Owner | grouped owner/address filter', title: 'LLC'},
			{icon: 'home_pin', meta: 'Address | physical site address', title: '123 Main St'},
		];
	}

	private ownerSearchRowKey(row: IOwnerSearchRow): string {
		if (row.ownerUid || row.addressUid) {
			return `${safeString(row.ownerUid)}|${safeString(row.addressUid)}`;
		}
		return `parcel|${safeString(row.parcelUid)}`;
	}

	private ownerSearchAddressLine(row: IOwnerSearchRow): string {
		const parts = [
			safeString(row.street),
			[safeString(row.city), safeString(row.state), safeString(row.zip)].filter(Boolean).join(' | '),
		].filter(Boolean);
		return parts.length ? parts.join(' - ') : 'Address unavailable';
	}

	private ownerSearchMetaLine(row: IOwnerSearchRow): string {
		const samples = Array.isArray(row.samples) ?
			row.samples :
			[];
		const parcelCount = Number(row.parcelCount || samples.length || (row.parcelUid ? 1 : 0));
		const sampleText = samples
			.map(sample => safeString(sample.pin || sample.parcelId || sample.parcelUid))
			.filter(Boolean)
			.slice(0, 3)
			.join(', ');
		const hidden = Math.max(parcelCount - samples.length, 0);
		const suffix = sampleText ?
			` | samples: ${sampleText}${hidden > 0 ? ` +${numberFormat(hidden)} more` : ''}` :
			'';
		return `${numberFormat(parcelCount)} ${parcelCount === 1 ? 'parcel' : 'parcels'} in this owner/address group${suffix}`;
	}

	private selectedOwnerParcelCount(): number {
		let total = 0;
		for (const key of this.unifiedSelectedOwnerKeys) {
			const row = this.unifiedResults.owners.find(obj => this.ownerSearchRowKey(obj) === key);
			total += Number(row ? (row.parcelCount || (row.parcelUid ? 1 : 0)) : 0);
		}
		return total;
	}

	private placeSearchMetaLine(place: IPlace): string {
		const parts = [
			place.typeDisplay,
			place.city ? capitalize(place.city.toLocaleLowerCase()) : '',
			place.state ? place.state.toLocaleUpperCase() : '',
			place.postcode,
		].filter(Boolean);
		return parts.join(' | ');
	}

	isDisabled(): boolean {
		return this.disabled;
	}

	@SLOT
	private mapFeatureSelectionChanged(): void {
		this.syncMapDrawTrashButtonVisibility();
		if (this.mapSelectedFeatureIds().length < 1) {
			this.setMapInteractiveControlMode(
				InteractiveMapControlMode.NoMode);
			if (this.filterMgr.currentlyEditingShape()) {
				this.filterMgr.stopEditingFilterShape();
			}
		}
	}

	private mapInteractiveControlMode(): InteractiveMapControlMode {
		return this.map.interactiveControlMode();
	}

	@SLOT
	private mapInteractiveControlModeChanged(newMode: InteractiveMapControlMode): void {
		switch (newMode) {
			case InteractiveMapControlMode.DrawShape:
			case InteractiveMapControlMode.InfoMode:
				this.filterMgr.closeFilterBox();
				break;
			case InteractiveMapControlMode.ShapeSelect:
				this.syncMapDrawTrashButtonVisibility();
				if (this.mapSelectedFeatureIds().length < 1) {
					this.setMapInteractiveControlMode(InteractiveMapControlMode.NoMode);
				}
				break;
		}
		if ((newMode !== InteractiveMapControlMode.InfoMode) && this.parcelInfo) {
			this.closePopups();
		}
	}

	@SLOT
	private mapLoaded(): void {
		setTimeout(() => this.syncVisibleParcels(), 0);
		setTimeout(() => this.syncDoNotMailGeoRefs(), 4);
		this.drawer.open();
	}

	private mapSelectedFeatureCount(): number {
		return this.mapSelectedFeatureIds().length;
	}

	private mapSelectedFeatureIds(): Array<string> {
		return this.map.selectedFeatureIds();
	}

	private openParcelPopup(coord: GeoCoordinate, parcels: Array<{parcel: IParcel; ignored: boolean;}>): void {
		this.closePopups();
		this.filterMgr.closeFilterBox();
		this.parcelInfo = new ParcelInfo();
		Obj.connect(
			this.parcelInfo, 'actionActivated',
			this, 'parcelInfoActionActivated');
		for (const obj of parcels) {
			this.parcelInfo.addInfo(obj.parcel);
		}
		this.map.openPopup(
			coord,
			{
				el: this.parcelInfo,
				closeButton: true,
				closeOnClick: false,
			});
	}

	private async openParcelPopupFromCoord(coord: GeoCoordinate): Promise<void> {
		const infos: Array<{parcel: IParcel; ignored: boolean;}> = [];
		for (const parcel of await this.fetchParcels(coord)) {
			infos.push({
				ignored: parcel.doNotMail,
				parcel,
			});
		}
		this.openParcelPopup(coord, infos);
	}

	private async openParcelPanelFromCoord(coord: GeoCoordinate): Promise<void> {
		this.closePopups();
		this.destroyParcelInfo();
		this.filterMgr.closeFilterBox();
		this.parcelWorkspaceParcels = await this.fetchParcels(coord);
		this.openSideWorkspacePanel('parcel');
		this.renderParcelWorkspacePanel();
	}

	private async pinParcelToWorkspace(parcel: IParcel): Promise<void> {
		const item = await svc.workspaceItem.create(this.slug, {
			itemType: 'parcel',
			targetKey: parcel.uid,
			label: this.parcelPrimaryTitle(parcel),
			source: 'map_parcel',
			payloadJson: {parcel},
		});
		const pinned = this.workspaceItemToPinnedItem(item);
		if (pinned) {
			this.upsertPinnedWorkspaceItem(pinned);
		}
		this.openSideWorkspacePanel('owners');
		this.renderPinnedWorkspacePanel();
	}

	// D2: drag a parcel card (Parcel tab) onto the Pinned tab to pin it —
	// the first production consumer of the shared DnD layer (ui/dnd.ts,
	// DROP mode). Drops route to pinParcelToWorkspace, the exact call the
	// "Pin parcel" button makes, so there's no new pin path to maintain.
	//
	// Targets: the Pinned tab button (always visible) and the pinned-list
	// host (hittable once open). Hovering the tab button "spring-loads" it
	// open after a beat so the user can drop into the larger list. The
	// active target gets `.is-drop-active` (dashed zone) from the
	// controller; the source card dims via `.is-drag-source`.
	// Shared drop-target setup for every "drag → pin" flow (D2 parcel
	// cards, D2b unified search rows). Registers the Pinned tab button
	// (spring-loads the tab open after a beat) and the pinned-list host,
	// both routing to `doPin`. Returns the registry + a `cleanup` thunk
	// the caller must invoke on drag end to cancel any pending spring.
	//
	// The tab-button lookup is scoped to the side panel: a second
	// `[data-workspace-tab="owners"]` is mirrored into the project drawer,
	// so an unscoped `document.querySelector` returns THAT one (far left,
	// nowhere near the visible tab) and the drop silently no-ops — the bug
	// fixed in #96. setSideWorkspaceTab already scopes for the same reason.
	private buildPinDropRegistry(doPin: () => void): {registry: DropTargetRegistry; cleanup: () => void} {
		const registry = new DropTargetRegistry();
		let springTimer: number | null = null;
		const cleanup = (): void => {
			if (springTimer !== null) {
				window.clearTimeout(springTimer);
				springTimer = null;
			}
		};
		const panelForTabs = this.sideWorkspacePanelEl
			|| document.getElementById('lb-project-workspace-panel');
		const pinnedTabBtn = panelForTabs
			? panelForTabs.querySelector<HTMLElement>('[data-workspace-tab="owners"]')
			: null;
		if (pinnedTabBtn) {
			registry.register({
				element: pinnedTabBtn,
				onEnter: () => {
					cleanup();
					springTimer = window.setTimeout(() => this.setSideWorkspaceTab('owners'), 500);
				},
				onLeave: cleanup,
				onDrop: doPin,
			});
		}
		const pinnedHost = document.getElementById('lb-project-workspace-owner-host');
		if (pinnedHost) {
			registry.register({element: pinnedHost, onDrop: doPin});
		}
		return {registry, cleanup};
	}

	private beginParcelDragToPin(event: PointerEvent, card: HTMLElement, parcel: IParcel): void {
		const {registry, cleanup} = this.buildPinDropRegistry(() => {
			this.pinParcelToWorkspace(parcel).catch(
				exc => logger.error('beginParcelDragToPin: pin failed', exc));
		});
		beginDrag(event, card, {
			registry,
			createGhost: defaultGhost,
			cursor: 'grabbing',
			onStart: () => card.classList.add('is-drag-source'),
			onEnd: () => {
				cleanup();
				card.classList.remove('is-drag-source');
			},
		});
	}

	// D2b: drag a unified-search result row (address or owner) onto the
	// Pinned tab to pin it — same drop targets as the parcel-card flow.
	// Because the side panel is usually closed while the ⌘K search panel
	// is open, onStart reveals it on the Pinned tab so the pinned list is
	// a visible drop zone; the ghost is a body clone, so it survives the
	// source row's removal when the unified panel closes.
	private beginUnifiedRowDragToPin(event: PointerEvent, rowEl: HTMLElement, doPin: () => void): void {
		const {registry, cleanup} = this.buildPinDropRegistry(doPin);
		beginDrag(event, rowEl, {
			registry,
			createGhost: defaultGhost,
			cursor: 'grabbing',
			onStart: () => {
				rowEl.classList.add('is-drag-source');
				this.openSideWorkspacePanel('owners');
			},
			onEnd: () => {
				cleanup();
				rowEl.classList.remove('is-drag-source');
			},
		});
	}

	private async pinSingleAddressRow(row: IUnifiedAddressRow): Promise<void> {
		// An address row can resolve to several parcels (its samples); pin
		// each as a `parcel` workspace item, matching the bulk
		// pinUnifiedAddressSelectionToWorkspace per-uid logic.
		for (const uid of this.addressRowSelectedParcelUids(row)) {
			const parcel = await this.fetchParcel(uid);
			if (!parcel) {
				continue;
			}
			const item = await svc.workspaceItem.create(this.slug, {
				itemType: 'parcel',
				targetKey: parcel.uid,
				label: this.parcelPrimaryTitle(parcel),
				source: 'address_search',
				payloadJson: {parcel},
			});
			const pinned = this.workspaceItemToPinnedItem(item);
			if (pinned) {
				this.upsertPinnedWorkspaceItem(pinned);
			}
		}
		this.openSideWorkspacePanel('owners');
		this.renderPinnedWorkspacePanel();
	}

	private async pinSingleOwnerRow(row: IOwnerSearchRow): Promise<void> {
		const item = await svc.workspaceItem.create(this.slug, {
			itemType: 'owner',
			targetKey: this.ownerSearchRowKey(row),
			label: this.ownerPinnedTitle(row),
			source: 'owner_search',
			payloadJson: {row},
		});
		const pinned = this.workspaceItemToPinnedItem(item);
		if (pinned) {
			this.upsertPinnedWorkspaceItem(pinned);
		}
		this.openSideWorkspacePanel('owners');
		this.renderPinnedWorkspacePanel();
	}

	// Shared grip factory for the drag affordances (D2/D3/D2b). A small
	// `.up-grip` dots handle; the caller wires the pointerdown to the
	// appropriate begin*Drag method and decides placement. `tag` defaults
	// to 'button' (card grips) but can be 'span' for grips nested inside a
	// clickable row (e.g. the unified owner result is itself a <button>,
	// so a nested <button> would be invalid HTML). A click on the grip is
	// swallowed so it never triggers an ancestor row's select handler.
	private makeDragGrip(
		label: string,
		onPointerDown: (ev: PointerEvent) => void,
		opt: {tag?: 'button' | 'span'} = {},
	): HTMLElement {
		const grip = document.createElement(opt.tag || 'button');
		if (grip instanceof HTMLButtonElement) {
			grip.type = 'button';
		}
		grip.className = 'up-grip workspace-card__drag';
		grip.title = label;
		grip.setAttribute('aria-label', label);
		const dots = document.createElement('span');
		dots.className = 'up-grip__dots';
		for (let i = 0; i < 6; ++i) {
			dots.appendChild(document.createElement('span'));
		}
		grip.appendChild(dots);
		// Cast: createElement with a 'button' | 'span' union widens the
		// element type, so the 'pointerdown' listener loses its
		// PointerEvent typing — the handler is PointerEvent-typed by
		// construction.
		grip.addEventListener('pointerdown', onPointerDown as EventListener);
		grip.addEventListener('click', ev => {
			ev.preventDefault();
			ev.stopPropagation();
		});
		return grip;
	}

	private reorderIndicatorEl: HTMLElement | null = null;

	// D3 + D4: one gesture on a pinned card's grip. Drag it WITHIN the
	// pinned list to reorder (an insertion line shows where it lands);
	// drag it OUT of the panel to unpin (the ghost flips to a red
	// remove-affordance). Drop location decides the action; ESC or a drop
	// that does neither aborts (the source is never removed/moved until a
	// confirmed drop). Reorder persists via svc.workspaceItem.reorder.
	private beginPinnedCardDrag(event: PointerEvent, card: HTMLElement, index: number): void {
		const panel = this.sideWorkspacePanelEl || document.getElementById('lb-project-workspace-panel');
		const host = document.getElementById('lb-project-workspace-owner-host');
		const isOutsidePanel = (x: number, y: number): boolean => {
			if (!panel) {
				return true;
			}
			const r = panel.getBoundingClientRect();
			return x < r.left || x > r.right || y < r.top || y > r.bottom;
		};
		// Insertion index among the OTHER cards (the dragged card excluded).
		// Maps directly to the post-removal array index in
		// reorderPinnedItem, so [A,B,C] dragging B between C and end → 1.
		const otherCards = (): Array<HTMLElement> => host
			? Array.from(host.querySelectorAll<HTMLElement>('.workspace-card')).filter(c => c !== card)
			: [];
		const computeInsertion = (y: number): number => {
			const cards = otherCards();
			let idx = 0;
			for (const c of cards) {
				const r = c.getBoundingClientRect();
				if (y < r.top + r.height / 2) {
					break;
				}
				idx++;
			}
			return idx;
		};
		let insertionIndex: number | null = null;

		beginDrag(event, card, {
			createGhost: defaultGhost,
			cursor: 'grabbing',
			onStart: () => card.classList.add('is-drag-source'),
			onMove: ctx => {
				const outside = isOutsidePanel(ctx.clientX, ctx.clientY);
				if (ctx.ghost) {
					ctx.ghost.classList.toggle('lb-dnd-ghost--remove', outside);
				}
				if (outside) {
					insertionIndex = null;
					this.hideReorderIndicator();
				} else {
					insertionIndex = computeInsertion(ctx.clientY);
					this.positionReorderIndicator(otherCards(), insertionIndex);
				}
			},
			onDrop: ctx => {
				if (isOutsidePanel(ctx.clientX, ctx.clientY)) {
					this.unpinWorkspaceItem(index).catch(
						exc => logger.error('beginPinnedCardDrag: unpin failed', exc));
				} else if (insertionIndex !== null) {
					this.reorderPinnedItem(index, insertionIndex).catch(
						exc => logger.error('beginPinnedCardDrag: reorder failed', exc));
				}
			},
			onEnd: () => {
				card.classList.remove('is-drag-source');
				this.hideReorderIndicator();
			},
		});
	}

	private ensureReorderIndicator(): HTMLElement {
		if (!this.reorderIndicatorEl) {
			const el = document.createElement('div');
			el.className = 'lb-reorder-indicator';
			document.body.appendChild(el);
			this.reorderIndicatorEl = el;
		}
		return this.reorderIndicatorEl;
	}

	// Place the 2px insertion line at the gap for `insertionIndex` among
	// `cards` (the non-dragged cards, in display order).
	private positionReorderIndicator(cards: Array<HTMLElement>, insertionIndex: number): void {
		const el = this.ensureReorderIndicator();
		let top: number;
		let left: number;
		let width: number;
		if (cards.length === 0) {
			const host = document.getElementById('lb-project-workspace-owner-host');
			if (!host) {
				return;
			}
			const r = host.getBoundingClientRect();
			top = r.top + 10;
			left = r.left + 8;
			width = r.width - 16;
		} else if (insertionIndex >= cards.length) {
			const r = cards[cards.length - 1].getBoundingClientRect();
			top = r.bottom;
			left = r.left;
			width = r.width;
		} else {
			const r = cards[insertionIndex].getBoundingClientRect();
			top = r.top;
			left = r.left;
			width = r.width;
		}
		el.style.display = 'block';
		el.style.top = `${top - 1}px`;
		el.style.left = `${left}px`;
		el.style.width = `${width}px`;
	}

	private hideReorderIndicator(): void {
		if (this.reorderIndicatorEl) {
			this.reorderIndicatorEl.style.display = 'none';
		}
	}

	private async reorderPinnedItem(fromIndex: number, toIndexAmongOthers: number): Promise<void> {
		const items = this.pinnedWorkspaceItems;
		if (fromIndex < 0 || fromIndex >= items.length) {
			return;
		}
		const [moved] = items.splice(fromIndex, 1);
		const clamped = Math.max(0, Math.min(toIndexAmongOthers, items.length));
		if (clamped === fromIndex) {
			// No-op move (dropped back in place); restore and bail.
			items.splice(fromIndex, 0, moved);
			return;
		}
		items.splice(clamped, 0, moved);
		this.renderPinnedWorkspacePanel();
		const ids = items
			.map(item => item.workspaceItemId)
			.filter((id): id is WorkspaceItemPk => id !== undefined);
		if (ids.length) {
			await svc.workspaceItem.reorder(this.slug, ids);
		}
	}

	private async pinUnifiedAddressSelectionToWorkspace(): Promise<void> {
		// Phase 5A-style: an address row's samples are parcels. Pin
		// each parcel as a standard `parcel` workspace item so it
		// renders in the existing Pinned tab alongside other parcel
		// pins — no new pin type needed. Fetch the full parcel
		// payload first (the workspace-item create expects an IParcel
		// in payloadJson for `parcel` items; the address row only
		// carries a thin {uid, pin, parcelId, changesetId} sample).
		const selectedRows = Array.from(this.unifiedSelectedAddressKeys)
			.map(key => this.unifiedResults.addresses.find(row => this.addressRowKey(row) === key))
			.filter((row): row is IUnifiedAddressRow => Boolean(row));
		if (!selectedRows.length) {
			return;
		}
		// Dedupe parcel UIDs across rows so the same parcel isn't pinned twice.
		const dedupe = new Set<string>();
		const queue: Array<string> = [];
		for (const row of selectedRows) {
			for (const sample of (row.samples || [])) {
				const uid = safeString(sample.parcelUid);
				if (uid && !dedupe.has(uid)) {
					dedupe.add(uid);
					queue.push(uid);
				}
			}
		}
		for (const uid of queue) {
			let parcel: IParcel | null = null;
			try {
				parcel = await this.fetchParcel(uid);
			} catch (exc) {
				logger.warning('pinUnifiedAddressSelectionToWorkspace: failed to fetch parcel %s: %s', uid, exc);
				continue;
			}
			if (!parcel) {
				continue;
			}
			const item = await svc.workspaceItem.create(this.slug, {
				itemType: 'parcel',
				targetKey: parcel.uid,
				label: this.parcelPrimaryTitle(parcel),
				source: 'address_search',
				payloadJson: {parcel},
			});
			const pinned = this.workspaceItemToPinnedItem(item);
			if (pinned) {
				this.upsertPinnedWorkspaceItem(pinned);
			}
		}
		this.unifiedSelectedAddressKeys.clear();
		this.closeUnifiedWorkspacePanel();
		this.openSideWorkspacePanel('owners');
		this.renderPinnedWorkspacePanel();
	}

	private async pinUnifiedOwnerSelectionToWorkspace(): Promise<void> {
		const selectedRows = Array.from(this.unifiedSelectedOwnerKeys)
			.map(key => this.unifiedResults.owners.find(row => this.ownerSearchRowKey(row) === key))
			.filter((row): row is IOwnerSearchRow => Boolean(row));
		if (!selectedRows.length) {
			return;
		}
		for (const row of selectedRows.reverse()) {
			const key = this.ownerSearchRowKey(row);
			const item = await svc.workspaceItem.create(this.slug, {
				itemType: 'owner',
				targetKey: key,
				label: this.ownerPinnedTitle(row),
				source: 'owner_search',
				payloadJson: {row},
			});
			const pinned = this.workspaceItemToPinnedItem(item);
			if (pinned) {
				this.upsertPinnedWorkspaceItem(pinned);
			}
		}
		this.unifiedSelectedOwnerKeys.clear();
		this.closeUnifiedWorkspacePanel();
		this.openSideWorkspacePanel('owners');
		this.renderPinnedWorkspacePanel();
	}

	private async toggleParcelDoNotMail(parcel: IParcel, sectionIndex: number): Promise<void> {
		const geoRef = await svc.geoRef.setDoNotMail(
			!parcel.doNotMail, {
				geometry: null,
				parcelPk: parcel.uid,
			});
		const updated = {
			...await this.fetchParcel(parcel.uid),
			doNotMail: geoRef ? geoRef.doNotMail : false,
		};
		this.replaceWorkspaceParcel(updated);
		this.replacePinnedParcel(updated);
		await this.parcelExclusionChanged(
			sectionIndex,
			updated,
			geoRef ? geoRef.doNotMail : false);
		this.renderSideWorkspacePanel();
		setTimeout(() => this.syncCounts(), 3);
	}

	private replaceWorkspaceParcel(parcel: IParcel): void {
		this.parcelWorkspaceParcels = this.parcelWorkspaceParcels.map(item => item.uid === parcel.uid ? parcel : item);
	}

	private replacePinnedParcel(parcel: IParcel): void {
		for (const item of this.pinnedWorkspaceItems) {
			if (item.kind === 'parcel' && item.parcel.uid === parcel.uid) {
				item.parcel = parcel;
			}
		}
	}

	@SIGNAL
	private async parcelExclusionChanged(parcelInfoSectionIndex: number, parcel: IParcel, excluded: boolean): Promise<void> {
	}

	@SLOT
	private async parcelInfoActionActivated(sectionIndex: number, action: ParcelAction): Promise<void> {
		if (this.parcelInfo) {
			switch (action) {
				case ParcelAction.IgnoreParcel:
					const parcelInfo = this.parcelInfo.info(sectionIndex);
					if (parcelInfo) {
						const geoRef = await svc.geoRef.setDoNotMail(
							!parcelInfo.doNotMail, {
								geometry: null,
								parcelPk: parcelInfo.uid,
							});
						await this.parcelExclusionChanged(
							sectionIndex,
							await this.fetchParcel(parcelInfo.uid),
							geoRef ? geoRef.doNotMail : false);
						setTimeout(() => this.syncCounts(), 3);
					}
					break;
			}
		}
	}

	@SLOT
	private async payButtonClicked(): Promise<void> {
		await this.beginPayment();
	}

	@SIGNAL
	private paymentProcessEnded(paymentSucceeded: boolean): void {
	}

	@SLOT
	private async paymentEnded(success: boolean): Promise<void> {
		await this.syncDownloadPayButton();
		if (success && (this.downloadPay.currentButton() === DownloadPayButton.DownloadButton)) {
			this.downloadPay.setButtonFlashing(true);
		}
	}

	@SIGNAL
	private placeAutoCompleteActivated(place: IPlace): void {
	}

	@SLOT
	private primaryInputIconActivated(position: TextInputIconPosition): void {
		switch (position) {
			case TextInputIconPosition.Leading:
				this.openUnifiedWorkspacePanel();
				break;
		}
	}

	@SIGNAL
	private projectUpdated(project: IProject): void {
	}

	@SLOT
	private async saveGeoMapConfiguration(): Promise<void> {
		if (this.mapCfgPk === null) {
			logger.warning('saveGeoMapConfiguration: Map configuration PK is null.');
		} else {
			const cam = this.map.camera();
			if (cam) {
				await this.updateGeoMapConfiguration(
					this.mapCfgPk,
					mapOptsToGeoMapConfiguration({
						...cam,
						style: this.map.mapStyleUrl() || '',
					}));

			} else {
				logger.warning('saveGeoMapConfiguration: Geo map returned null camera object');
			}
		}
	}

	private setAutoCompleteEnabled(enable: boolean): void {
		if (enable === Boolean(this.autoComplete)) {
			return;
		}
		if (enable) {
			this.initAutoComplete();
		} else {
			this.destroyAutoComplete();
		}
	}

	setDisabled(disabled: boolean): void {
		if (disabled === this.disabled) {
			return;
		}
		this.disabled = disabled;
		this.downloadPay.setDisabled(this.disabled);
		this.filterList.setDisabled(this.disabled);
		this.filterMgr.setDisabled(this.disabled);
		this.map.setDisabled(this.disabled);
		this.primaryInput && this.primaryInput.setInputDisabled(this.disabled);
		if (this.disabled) {
			this.destroyParcelInfo();
			this.destroyAutoComplete();
			Obj.disconnect(
				this, 'parcelExclusionChanged',
				this, 'syncParcelInfo');
			Obj.disconnect(
				this, 'parcelExclusionChanged',
				this, 'syncDoNotMailGeoRefs');
			Obj.disconnect(
				this, 'parcelExclusionChanged',
				this, 'syncVisibleParcels');
			if (this.primaryInput) {
				Obj.disconnect(
					this.primaryInput, 'textChanged',
					this, 'textInputTextChanged');
			}
			Obj.disconnect(
				this.map, 'mapClicked',
				this, 'geoMapMapClicked');
			Obj.disconnect(
				this.map, 'cameraChanged',
				this, 'saveGeoMapConfiguration');
			Obj.disconnect(
				this.map, 'interactiveControlModeChanged',
				this, 'mapInteractiveControlModeChanged');
			Obj.disconnect(
				this.map, 'popupClosed',
				this, 'geoMapPopupClosed');
			Obj.disconnect(
				this.map, 'styleChanged',
				this, 'geoMapStyleChanged');
			Obj.disconnect(
				this.map, 'featureSelectionChanged',
				this, 'mapFeatureSelectionChanged');
			Obj.disconnect(
				this.filterMgr, 'filterCreated',
				this, 'filterCreated');
			Obj.disconnect(
				this.filterMgr, 'filterCreated',
				this, 'syncCounts');
			Obj.disconnect(
				this.filterMgr, 'filterCreated',
				this, 'syncVisibleParcels');
			Obj.disconnect(
				this.filterMgr, 'filterUpdated',
				this, 'syncCounts');
			Obj.disconnect(
				this.filterMgr, 'filterUpdated',
				this, 'syncVisibleParcels');
			Obj.disconnect(
				this.filterMgr, 'filterDeleted',
				this, 'syncCounts');
			Obj.disconnect(
				this.filterMgr, 'filterDeleted',
				this, 'syncVisibleParcels');
		} else {
			Obj.connect(
				this, 'parcelExclusionChanged',
				this, 'syncParcelInfo');
			Obj.connect(
				this, 'parcelExclusionChanged',
				this, 'syncDoNotMailGeoRefs');
			Obj.connect(
				this, 'parcelExclusionChanged',
				this, 'syncVisibleParcels');
			if (this.primaryInput) {
				Obj.connect(
					this.primaryInput, 'textChanged',
					this, 'textInputTextChanged');
			}
			Obj.connect(
				this.map, 'mapClicked',
				this, 'geoMapMapClicked');
			Obj.connect(
				this.map, 'cameraChanged',
				this, 'saveGeoMapConfiguration');
			Obj.connect(
				this.map, 'interactiveControlModeChanged',
				this, 'mapInteractiveControlModeChanged');
			Obj.connect(
				this.map, 'popupClosed',
				this, 'geoMapPopupClosed');
			Obj.connect(
				this.map, 'styleChanged',
				this, 'geoMapStyleChanged');
			Obj.connect(
				this.map, 'featureSelectionChanged',
				this, 'mapFeatureSelectionChanged');
			Obj.connect(
				this.filterMgr, 'filterCreated',
				this, 'filterCreated');
			Obj.connect(
				this.filterMgr, 'filterCreated',
				this, 'syncCounts');
			Obj.connect(
				this.filterMgr, 'filterCreated',
				this, 'syncVisibleParcels');
			Obj.connect(
				this.filterMgr, 'filterUpdated',
				this, 'syncCounts');
			Obj.connect(
				this.filterMgr, 'filterUpdated',
				this, 'syncVisibleParcels');
			Obj.connect(
				this.filterMgr, 'filterDeleted',
				this, 'syncCounts');
			Obj.connect(
				this.filterMgr, 'filterDeleted',
				this, 'syncVisibleParcels');
		}
	}

	@SLOT
	private setDocumentForProject(project: IProject): void {
		this.setDocumentTitle(project.title);
		if (this.disabled !== project.readOnly) {
			this.setDisabled(project.readOnly);
		}
	}

	private setDocumentTitle(title: string): void {
		if (title.trim().length > 0) {
			document.title = `${title} - Listblox`;
		} else {
			document.title = 'Listblox';
		}
	}

	private setMapInteractiveControlMode(mode: InteractiveMapControlMode): void {
		this.map.setInteractiveControlMode(mode);
	}

	setSelectedMapFeatures(featureId: number | string | Array<number | string>): void {
		this.map.setSelectedFeatures(featureId);
	}

	private setPrimaryInputText(text: string): void {
		if (this.primaryInput) {
			this.primaryInput.setText(text);
		}
	}

	setFilterListDividerVisible(visible: boolean): void {
		if (this.filterListDivider) {
			this.filterListDivider.setVisible(visible);
		}
	}

	@SLOT
	private async syncCounts(): Promise<void> {
		this.clearPendingFilterCreateFeedback();
		const refreshFilterMetrics = this.sender() === this.filterMgr;
		let canceled: boolean = false;
		if (this.cancelSrc.syncCounts) {
			// Currently awaiting response from previous request.
			// `beginSync()` already called.
			this.cancelSrc.syncCounts.cancel();
		} else {
			this.stats.beginSync();
		}
		this.cancelSrc.syncCounts = svc.cancelTokenSource();
		try {
			const invoice = await this.fetchInvoice(
				true,
				{cancelToken: this.cancelSrc.syncCounts.token});
			// If we're here we  our request was not canceled. We set our
			// values and closeup shop.
			this.stats.setAddressCount(invoice.totalQuantity);
			this.stats.setPrice(invoice.total);
			// Cache for the two-zone editor's instant pristine-open count + the opt-in
			// banner's "your current list (N addresses)" line.
			this.lastKnownAddressCount = invoice.totalQuantity;
			this.renderProjectOptInBanner();
		} catch (exc) {
			if (svc.project.isCancellation(exc)) {
				// Our request was canceled by a subsequent call.
				canceled = true;
			} else {
				throw exc;
			}
		} finally {
			if (!canceled) {
				// This request was not canceled. The values were set above;
				// sync is complete.
				this.cancelSrc.syncCounts = null;
				this.stats.endSync();
				if (refreshFilterMetrics) {
					this.filterMgr.syncFilterList().catch(exc => logger.warning('syncCounts: Failed to refresh filter metrics.', exc));
				}
			}
		}
	}

	@SLOT
	private async syncDoNotMailGeoRefs(): Promise<void> {
		const objs = await svc.geoRef.geoRefList();
		const mpFeatColl: GeoJsonFeatureCollection<IGeoRefMultiPolygon> = {
			features: [],
			type: 'FeatureCollection',
		};
		const pointFeatColl: GeoJsonFeatureCollection<IGeoRefPoint> = {
			features: [],
			type: 'FeatureCollection',
		};
		for (const obj of objs) {
			if (obj.doNotMail && obj.point) {
				const feat: GeoJsonFeature<IGeoRefPoint> = {
					geometry: obj.point,
					id: obj.id,
					properties: {},
					type: 'Feature',
				};
				pointFeatColl.features.push(feat);
			}
			if (obj.doNotMail && obj.multipolygon) {
				const feat: GeoJsonFeature<IGeoRefMultiPolygon> = {
					geometry: obj.multipolygon,
					id: obj.id,
					properties: {},
					type: 'Feature',
				};
				mpFeatColl.features.push(feat);
			}
		}
		this.map.setDoNotMailSourceData({
			fill: mpFeatColl,
			point: pointFeatColl,
		});
	}

	private async syncDownloadPayButton(project?: IProject): Promise<void> {
		if (!project) {
			project = await this.fetchProject();
		}
		this.downloadWithOwnerNameEnabled = project.downloadWithOwnerNameEnabled;
		let buttonEnabled: boolean;
		if (typeof project.absoluteListUrl === 'string') {
			this.absoluteListUri = project.absoluteListUrl;
			this.downloadPay.showDownloadButton();
			buttonEnabled = true;
		} else {
			this.absoluteListUri = '';
			this.downloadPay.showPayButton();
			buttonEnabled = !project.readOnly;
		}
		this.downloadPay.setButtonEnabled(buttonEnabled);
	}

	private syncMapDrawTrashButtonVisibility(): void {
		setTimeout(() => {
			this.map.setDrawButtonVisible(ButtonId.Trash, this.mapSelectedFeatureIds().length > 0);
		}, 125);
	}

	@SLOT
	private syncParcelInfo(parcelInfoSectionIndex: number, parcel: IParcel, excluded: boolean): void {
		if (this.parcelInfo) {
			this.parcelInfo.setInfo(parcelInfoSectionIndex, parcel);
		}
		this.replaceWorkspaceParcel(parcel);
		this.replacePinnedParcel(parcel);
	}

	private async fetchTopLevelFilter(filterId: number): Promise<IFilter | null> {
		try {
			const filter = await svc.filter.get(filterId);
			if (filter.parentId === null) {
				return filter;
			}
		} catch (exc) {
			logger.warning('fetchTopLevelFilter: Failed to fetch filter %s.', filterId);
		}
		return null;
	}

	private async syncAllParcelFilterTiles(): Promise<void> {
		this.map.syncParcelFilterTiles(
			await this.fetchFilters(),
			{removeMissing: true});
	}

	private async syncChangedParcelFilterTiles(filterOrId?: IFilter | number, opt?: IParcelFilterSyncOpt): Promise<void> {
		const parcelFilterId = opt ? opt.parcelFilterId : undefined;
		if (!parcelFilterId) {
			await this.syncAllParcelFilterTiles();
			return;
		}
		if (typeof filterOrId === 'number') {
			if (filterOrId === parcelFilterId) {
				await this.syncAllParcelFilterTiles();
				return;
			}
			const filter = await this.fetchTopLevelFilter(parcelFilterId);
			if (filter) {
				this.map.syncParcelFilterTiles([filter], {refreshFilterIds: [parcelFilterId]});
			} else {
				await this.syncAllParcelFilterTiles();
			}
			return;
		}
		if (filterOrId && filterOrId.parentId === null && filterOrId.id === parcelFilterId) {
			this.map.syncParcelFilterTiles([filterOrId], {refreshFilterIds: [parcelFilterId]});
			return;
		}
		const filter = await this.fetchTopLevelFilter(parcelFilterId);
		if (filter) {
			this.map.syncParcelFilterTiles([filter], {refreshFilterIds: [parcelFilterId]});
		} else {
			await this.syncAllParcelFilterTiles();
		}
	}

	@SLOT
	private async syncVisibleParcels(filterOrId?: IFilter | number, opt?: IParcelFilterSyncOpt): Promise<void> {
		if (this.map.usesProjectScopedParcelTiles()) {
			this.map.filterParcels(null);
			if (this.sender() === this.filterMgr) {
				await this.syncChangedParcelFilterTiles(filterOrId, opt);
			} else if (arguments.length === 0) {
				await this.syncAllParcelFilterTiles();
			}
		} else {
			const pks = await svc.parcel.projectParcelPkList(this.slug);
			this.map.filterParcels(pks);
		}
		if (!this.map.isParcelsVisible()) {
			this.map.setParcelsVisible(true);
		}
	}

	@SLOT
	private async textInputTextChanged(text: string): Promise<void> {
		if (text.trim().length > 0) {
			const places = await this.fetchPlaces(text);
			this.places = new list<IPlace>(places);
			this.setAutoCompleteEnabled(true);
			if (this.autoComplete) {
				const items: Array<IAutoCompleteItem> = [];
				for (const obj of this.places) {
					const labelParts: Array<string> = [
						obj.typeDisplay,
					];
					const city = obj.city.toLocaleLowerCase();
					if (city.length > 0) {
						labelParts.push(`in ${capitalize(city)}`);
						const state = obj.state.toLocaleUpperCase();
						if (state === 'NORTH CAROLINA') {
							labelParts.push(`, NC`);
						}
					}
					items.push({
						label: labelParts.join(' '),
						text: obj.name,
					});

				}
				this.autoComplete.setItems(items);
			}
		} else {
			this.setAutoCompleteEnabled(false);
		}
	}

	@SLOT
	private undoViewActionActivated(action: 'undo' | 'redo'): void {
		switch (action) {
			case 'undo':
				this.undoStack.undo();
				break;
			case 'redo':
				this.undoStack.redo();
				break;
		}
	}

	@SLOT
	private updateAutoCompletePosition(): void {
		if (this.autoComplete) {
			if (this.primaryInput) {
				const bodyAbsY = Math.abs(ElObj.body().rect().y);
				const {height, width, x, y} = this.primaryInput.rect();
				this.autoComplete.setStyleProperty('top', pixelString(y + height + bodyAbsY));
				this.autoComplete.setStyleProperty('left', pixelString(x - 1));
				this.autoComplete.setStyleProperty('width', pixelString(width));
			} else {
				logger.warning('updateAutoCompletePosition: Primary input not found.');
			}
		}
	}

	private async updateGeoMapConfiguration(pk: number, data: Partial<Omit<IGeoMapConfiguration, 'id' | 'imageId' | 'imageUrl'>>): Promise<IGeoMapConfiguration> {
		return await svc.map.update({id: pk, ...data});
	}

	private async updateProject(data: IProject): Promise<IProject> {
		return await svc.project.update(data);
	}

	@bind
	private windowEvent(event: Event): void {
		switch (event.type) {
			case 'resize': {
				this.windowResizeEvent();
				break;
			}
		}
	}

	private windowResizeEvent(): void {
		if (this.isAutoCompleteOpen()) {
			this.updateAutoCompletePosition();
		}
		if (this.isSideWorkspacePanelOpen()) {
			this.syncSideWorkspacePanelPosition();
		}
	}
}

function geoMapConfigurationToMapOpts(cfg: Omit<IGeoMapConfiguration, 'id'>): IMapOpts {
	return {
		bearing: cfg.bearing,
		// Remember: center is [longitude, latitude]
		center: [cfg.longitude, cfg.latitude],
		pitch: cfg.pitch,
		style: cfg.styleIdentifier,
		zoom: cfg.zoom,
	};
}

function mapOptsToGeoMapConfiguration(opts: IMapOpts): Omit<IGeoMapConfiguration, 'id' | 'imageId' | 'imageUrl'> {
	return {
		bearing: opts.bearing,
		// Remember: center is [longitude, latitude]
		latitude: opts.center[1],
		longitude: opts.center[0],
		pitch: opts.pitch,
		styleIdentifier: opts.style,
		zoom: opts.zoom,
	};
}

function filterDoNotMailGeoRefs(objs: Iterable<IGeoRef>): Array<IGeoRef> {
	const rv: Array<IGeoRef> = [];
	for (const obj of objs) {
		if (obj.doNotMail) {
			rv.push(obj);
		}
	}
	return rv;
}

function filterMarkOnMapGeoRefs(objs: Iterable<IGeoRef>): Array<IGeoRef> {
	const rv: Array<IGeoRef> = [];
	for (const obj of objs) {
		if (obj.showOnMap) {
			rv.push(obj);
		}
	}
	return rv;
}

function appendTextDiv(parent: HTMLElement, text: string, className: string): HTMLDivElement {
	const el = document.createElement('div');
	el.className = className;
	el.textContent = text;
	parent.appendChild(el);
	return el;
}

function appendTextSpan(parent: HTMLElement, text: string, className?: string): HTMLSpanElement {
	const el = document.createElement('span');
	if (className) {
		el.className = className;
	}
	el.textContent = text;
	parent.appendChild(el);
	return el;
}

function cookieValue(name: string): string {
	const prefix = `${name}=`;
	for (const chunk of document.cookie.split(';')) {
		const part = chunk.trim();
		if (part.indexOf(prefix) === 0) {
			return decodeURIComponent(part.substring(prefix.length));
		}
	}
	return '';
}

function materialIcon(icon: string, className: string = 'material-icons'): HTMLSpanElement {
	const el = document.createElement('span');
	el.className = className.indexOf('material-icons') >= 0 ?
		className :
		`${className} material-icons`;
	el.setAttribute('aria-hidden', 'true');
	el.textContent = icon;
	return el;
}

function safeString(value: unknown): string {
	return (value === undefined || value === null) ?
		'' :
		String(value);
}
